import { Fragment, useEffect, useLayoutEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ESTADOS_OT,
  ESTADOS_OT_ABIERTOS,
  estadoVisualOt,
  fechaInicioMaxima,
  ETIQUETA_ESTADO_OT,
  ETIQUETA_RECURRENCIA,
  ETIQUETA_TIPO_TRABAJO,
  PRIORIDADES,
  ETIQUETA_PRIORIDAD,
  RECURRENCIAS,
  TIPOS_TRABAJO,
  formatearFecha,
  formatearFechaHora,
  formatearDuracion,
  formatearMoneda,
  CODIGOS_ERROR,
  type EstadoOT,
  type Pagina,
  type Prioridad,
  type Recurrencia,
  type TipoTrabajo,
} from '@cmms/shared';
import type { SujetoEvaluable } from '@cmms/shared';
import { api, consulta, ErrorApi } from '../servicios/api.js';
import { useSesion } from '../servicios/sesion.js';
import { useEsMovil } from '../servicios/responsive.js';
import {
  guardarGanttSoloRecurrentes,
  guardarVistaOrdenesPreferida,
  obtenerGanttSoloRecurrentes,
} from '../servicios/preferencias.js';
import {
  Aviso,
  Cargando,
  CasillaVerificacion,
  Dialogo,
  DialogoConfirmar,
  Etiqueta,
  EstadoOtEtiqueta,
  GaleriaImagenes,
  ICONO_ESTADO_VISUAL_OT,
  Indicador,
  Paginacion,
  ListaArchivos,
  MiniaturaImagenTabla,
  PrioridadEtiqueta,
  SeleccionMultiple,
  SubidaArchivos,
  SubidaImagenes,
  TONO_ESTADO_VISUAL_OT,
  Vacio,
  mensajeError,
  type AdjuntoImagen,
} from '../componentes/piezas.js';
import {
  IconoCalendario,
  IconoComentario,
  IconoEditar,
  IconoEnviar,
  IconoEstadoVencida,
  IconoLupa,
  IconoMas,
  IconoPrioridadAlta,
  IconoPrioridadBaja,
  IconoPrioridadMedia,
} from '../componentes/iconos.js';
import { BotonVolver } from '../componentes/boton-volver.js';
import { BotonEscanearQr } from '../componentes/camara.js';
import { BotonConsolidadoMateriales } from '../componentes/consolidado-materiales.js';
import { SeccionMaterialesOt, SeccionMaterialesPrevisualizados } from '../componentes/materiales-ot.js';
import { PlanMaterialesSolicitud } from '../componentes/plan-materiales-solicitud.js';
import { BotonImportarCsv } from '../componentes/importar-csv.js';
import {
  SelectorActivo,
  SelectorParte,
  SelectorPersona,
  SelectorCatalogo,
  SelectorFecha,
  SelectorLista,
  SelectorListaMultiple,
  SelectorSegmentado,
  SelectorUbicacion,
  useGruposOpciones,
  useTiposActivo,
  useTiposFalla,
  useUbicaciones,
  useUsuariosOpciones,
  type NodoUbicacion,
  type ParteBuscable,
} from '../componentes/selectores.js';
import { EnlaceRuta } from '../componentes/enlace-ruta.js';

interface OrdenTrabajo {
  id: string;
  numero: number;
  titulo: string;
  descripcion: string | null;
  estado: EstadoOT;
  prioridad: Prioridad;
  tipoTrabajo: TipoTrabajo;
  ubicacionId: string | null;
  ubicacion: string | null;
  ubicacionRaiz: string | null;
  activoId: string | null;
  activo: string | null;
  sujetoEvaluableId: string | null;
  sujetoEvaluable: string | null;
  tiposFalla: { id: string; nombre: string }[];
  asignados: { id: string; nombre: string; tipo: 'USUARIO' | 'GRUPO' }[];
  encargadoCategoriaId: string | null;
  tiempoEstimadoMinutos: number | null;
  fechaInicio: string | null;
  fechaVencimiento: string | null;
  vencida: boolean;
  recurrencia: Recurrencia;
  recurrenciaConfig: Record<string, unknown> | null;
  esPlantillaRecurrente: boolean;
  recurrenciaActiva: boolean;
  generadaDesdeId: string | null;
  ordenTrabajoPadreId: string | null;
  /** Número y título de la OT padre (solo en las sub-órdenes). */
  ordenTrabajoPadreNumero?: number | null;
  ordenTrabajoPadreTitulo?: string | null;
  otOrigenId: string | null;
  otOrigenNumero: number | null;
  solicitudId: string | null;
  tieneProcedimiento: boolean;
  subOrdenes: { total: number; abiertas: number };
  creadoEl: string;
  creadoPor: string | null;
  completadoEl: string | null;
  fotoUrl: string | null;
}

interface EventoHilo {
  id: string;
  tipoEvento: string;
  usuario: string | null;
  fechaHora: string;
  resumen: string;
  adjuntos: AdjuntoImagen[];
}

interface ObservacionActivo {
  id: string;
  ordenTrabajoNumero: number | null;
  texto: string;
  fechaVencimiento: string | null;
  vencida: boolean;
  resuelta: boolean;
  creadoPor: string | null;
  creadoEl: string;
  adjuntos: AdjuntoImagen[];
}

interface Borrador {
  buscar: string;
  estado: string;
  prioridad: string;
  tipoTrabajo: string;
  encargadoCategoriaId: string;
  ubicacionId: string;
  activoId: string;
  tipoFallaId: string;
  asignadoId: string;
  grupoId: string;
  recurrencia: string;
  fechaInicioDesde: string;
  creadoDesde: string;
  fechaInicioHasta: string;
  creadoHasta: string;
  fechaDesde: string;
  fechaHasta: string;
  tiempoEstimadoDesde: string;
  tiempoEstimadoHasta: string;
}

/**
 * Cabecera común a toda vista de Órdenes de Trabajo (tabla, calendario…):
 * mismo título, mismo selector de vista, misma búsqueda y mismo botón de
 * alta — para que cambiar de vista se sienta como cambiar una pestaña de la
 * misma pantalla, no como entrar a una página distinta.
 */
/**
 * Origen de la OT, a simple vista (§ pedido): manual (sin insignia — es el
 * caso normal, no necesita marcarse), de una solicitud, sub-orden, generada
 * por una plantilla recurrente, o con checklist adjunto. Se pueden combinar.
 * Un color fijo por tipo (mismo `tono` que ya usan Estado/Prioridad), con
 * `LeyendaOrigenOT` como referencia — de ahí sale esta misma lista, para que
 * la insignia y su entrada en la leyenda nunca queden desincronizadas.
 */
const ORIGENES_OT = {
  plantilla: {
    texto: 'Plantilla',
    titulo: 'Plantilla recurrente: nunca se trabaja, genera OTs sola según su recurrencia',
    tono: 'gris' as const,
  },
  solicitud: { texto: 'Solicitud', titulo: 'Viene de una solicitud', tono: 'azul' as const },
  subOrden: { texto: 'Sub-orden', titulo: 'Es sub-orden de otra OT', tono: 'gris' as const },
  recurrente: { texto: 'Recurrente', titulo: 'La generó sola una plantilla recurrente', tono: 'verde' as const },
  checklist: { texto: 'Checklist', titulo: 'Tiene un procedimiento adjunto', tono: 'ambar' as const },
};

function OrigenOT({
  orden,
}: {
  orden: Pick<OrdenTrabajo, 'solicitudId' | 'ordenTrabajoPadreId' | 'generadaDesdeId' | 'tieneProcedimiento'> & {
    /** Ausente en el calendario (§ ese endpoint no la trae): se trata como `false`. */
    esPlantillaRecurrente?: boolean;
  };
}): JSX.Element | null {
  const claves: (keyof typeof ORIGENES_OT)[] = [];
  if (orden.esPlantillaRecurrente) claves.push('plantilla');
  if (orden.solicitudId) claves.push('solicitud');
  if (orden.ordenTrabajoPadreId) claves.push('subOrden');
  if (orden.generadaDesdeId) claves.push('recurrente');
  if (orden.tieneProcedimiento) claves.push('checklist');
  if (claves.length === 0) return null;
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 3 }}>
      {claves.map((clave) => {
        const info = ORIGENES_OT[clave];
        return (
          <span key={clave} className={`etiqueta mini ${info.tono}`} title={info.titulo}>
            {info.texto}
          </span>
        );
      })}
    </div>
  );
}

/** Referencia de colores (§ pedido): una vez visible arriba de la tabla, no
 * hace falta pasar el mouse sobre cada insignia para acordarse de qué es cada
 * color. */
function LeyendaOrigenOT(): JSX.Element {
  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', fontSize: 12, color: 'var(--texto-tenue)' }}>
      <span>Origen:</span>
      {Object.values(ORIGENES_OT).map((info) => (
        <span key={info.texto} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span className={`etiqueta mini ${info.tono}`}>{info.texto}</span>
        </span>
      ))}
    </div>
  );
}

/**
 * Estado de los filtros que comparten el calendario y el Gantt (§ pedido: las
 * dos vistas de planificación llevan el mismo encabezado y los mismos filtros).
 * El rango de fechas lo decide cada vista con su propia navegación, así que
 * acá no hay filtro de vencimiento.
 */
type ClavePlanificacion = 'calendario' | 'gantt';

interface ValoresPlanificacion {
  buscar: string;
  estado: string;
  prioridad: string;
  soloVencidas: boolean;
  soloRecurrentes: boolean;
  soloPadres: boolean;
  ubicacionId: string;
  asignadoId: string;
  grupoId: string;
  tipoTrabajo: string;
  encargadoCategoriaId: string;
  activoId: string;
  tipoFallaId: string;
  recurrencia: string;
  fechaInicioDesde: string;
  creadoDesde: string;
  fechaInicioHasta: string;
  creadoHasta: string;
  tiempoEstimadoDesde: string;
  tiempoEstimadoHasta: string;
}

/** Filtros de cada vista de planificación, recordados mientras dure la sesión: al volver desde una OT no se borran. */
const planificacionGuardada: Partial<Record<ClavePlanificacion, ValoresPlanificacion>> = {};

function useFiltrosPlanificacion(clave: ClavePlanificacion, soloRecurrentesInicial: boolean) {
  const previo = planificacionGuardada[clave];
  const [buscar, setBuscar] = useState(previo?.buscar ?? '');
  const [estado, setEstado] = useState(previo?.estado ?? '');
  const [prioridad, setPrioridad] = useState(previo?.prioridad ?? '');
  const [soloVencidas, setSoloVencidas] = useState(previo?.soloVencidas ?? false);
  const [soloRecurrentes, setSoloRecurrentes] = useState(previo?.soloRecurrentes ?? soloRecurrentesInicial);
  const [soloPadres, setSoloPadres] = useState(previo?.soloPadres ?? false);
  const [ubicacionId, setUbicacionId] = useState(previo?.ubicacionId ?? '');
  const [asignadoId, setAsignadoId] = useState(previo?.asignadoId ?? '');
  const [grupoId, setGrupoId] = useState(previo?.grupoId ?? '');
  const [tipoTrabajo, setTipoTrabajo] = useState(previo?.tipoTrabajo ?? '');
  const [encargadoCategoriaId, setEncargadoCategoriaId] = useState(previo?.encargadoCategoriaId ?? '');
  const [activoId, setActivoId] = useState(previo?.activoId ?? '');
  const [tipoFallaId, setTipoFallaId] = useState(previo?.tipoFallaId ?? '');
  const [recurrencia, setRecurrencia] = useState(previo?.recurrencia ?? '');
  const [fechaInicioDesde, setFechaInicioDesde] = useState(previo?.fechaInicioDesde ?? '');
  const [creadoDesde, setCreadoDesde] = useState(previo?.creadoDesde ?? '');
  const [fechaInicioHasta, setFechaInicioHasta] = useState(previo?.fechaInicioHasta ?? '');
  const [creadoHasta, setCreadoHasta] = useState(previo?.creadoHasta ?? '');
  const [tiempoEstimadoDesde, setTiempoEstimadoDesde] = useState(previo?.tiempoEstimadoDesde ?? '');
  const [tiempoEstimadoHasta, setTiempoEstimadoHasta] = useState(previo?.tiempoEstimadoHasta ?? '');

  // Se guarda en cada render, igual que los filtros de la lista: así lo último tocado queda registrado aunque se salga de la vista.
  useEffect(() => {
    planificacionGuardada[clave] = {
      buscar,
      estado,
      prioridad,
      soloVencidas,
      soloRecurrentes,
      soloPadres,
      ubicacionId,
      asignadoId,
      grupoId,
      tipoTrabajo,
      encargadoCategoriaId,
      activoId,
      tipoFallaId,
      recurrencia,
      fechaInicioDesde,
      creadoDesde,
      fechaInicioHasta,
      creadoHasta,
      tiempoEstimadoDesde,
      tiempoEstimadoHasta,
    };
  });

  const filtrosAvanzados = {
    tipoTrabajo,
    encargadoCategoriaId,
    activoId,
    tipoFallaId,
    recurrencia,
    fechaInicioDesde,
    creadoDesde,
    fechaInicioHasta,
    creadoHasta,
    tiempoEstimadoDesde,
    tiempoEstimadoHasta,
  };
  const cantidadFiltrosAvanzados = Object.values(filtrosAvanzados).filter((v) => v !== '').length;
  // Recurrencia vive en la fila rápida, junto a Estado/Prioridad, así que el
  // número de «Más filtros» no cuenta algo que ya está visible fuera del panel.
  const cantidadFiltrosAvanzadosPanel = Object.entries(filtrosAvanzados).filter(
    ([clave, valor]) => valor !== '' && clave !== 'recurrencia',
  ).length;

  function limpiarFiltrosAvanzados(): void {
    setTipoTrabajo('');
    setEncargadoCategoriaId('');
    setActivoId('');
    setTipoFallaId('');
    setRecurrencia('');
    setFechaInicioDesde('');
    setCreadoDesde('');
    setFechaInicioHasta('');
    setCreadoHasta('');
    setTiempoEstimadoDesde('');
    setTiempoEstimadoHasta('');
  }

  return {
    buscar,
    setBuscar,
    estado,
    setEstado,
    prioridad,
    setPrioridad,
    soloVencidas,
    setSoloVencidas,
    soloRecurrentes,
    setSoloRecurrentes,
    soloPadres,
    setSoloPadres,
    ubicacionId,
    setUbicacionId,
    asignadoId,
    grupoId,
    setAsignadoId,
    setGrupoId,
    tipoTrabajo,
    setTipoTrabajo,
    encargadoCategoriaId,
    setEncargadoCategoriaId,
    activoId,
    setActivoId,
    tipoFallaId,
    setTipoFallaId,
    recurrencia,
    setRecurrencia,
    fechaInicioDesde,
    creadoDesde,
    setFechaInicioDesde,
    setCreadoDesde,
    fechaInicioHasta,
    creadoHasta,
    setFechaInicioHasta,
    setCreadoHasta,
    tiempoEstimadoDesde,
    setTiempoEstimadoDesde,
    tiempoEstimadoHasta,
    setTiempoEstimadoHasta,
    cantidadFiltrosAvanzados,
    cantidadFiltrosAvanzadosPanel,
    limpiarFiltrosAvanzados,
    /** Parámetros listos para `consulta()`. Los booleanos se mandan solo como
     * 'true': Zod convierte a `true` cualquier string no vacío, incluido "false". */
    parametros: {
      estado,
      prioridad,
      vencidas: soloVencidas ? 'true' : '',
      soloRecurrentes: soloRecurrentes ? 'true' : '',
      soloPadres: soloPadres ? 'true' : '',
      ubicacionId,
      asignadoId,
      grupoId,
      buscar,
      ...filtrosAvanzados,
    },
  };
}

type FiltrosPlanificacion = ReturnType<typeof useFiltrosPlanificacion>;

/** Leyenda de origen + filtros rápidos + «Más filtros», idénticos en calendario y Gantt. */
function BloqueFiltrosPlanificacion({ f, prefijo }: { f: FiltrosPlanificacion; prefijo: string }): JSX.Element {
  const { data: ubicaciones } = useUbicaciones();
  const { data: fallas } = useTiposFalla();
  const { data: grupos } = useGruposOpciones();
  const { data: usuarios } = useUsuariosOpciones();
  // En escritorio los filtros rápidos/avanzados se ven siempre (ocultos por
  // CSS en pantallas chicas, clase "filtros-escritorio"); en móvil no había
  // ningún reemplazo — los filtros de Calendario y Gantt simplemente
  // desaparecían sin dejar manera de acceder a ellos (§ bug reportado). Este
  // modal es ese reemplazo, con los mismos campos (sin la clase que los
  // escondería también adentro del modal).
  const [modalAbierto, setModalAbierto] = useState(false);
  const cantidadFiltrosTotal =
    (f.estado ? 1 : 0) +
    (f.prioridad ? 1 : 0) +
    (f.ubicacionId ? 1 : 0) +
    (f.asignadoId ? 1 : 0) +
    (f.grupoId ? 1 : 0) +
    (f.soloVencidas ? 1 : 0) +
    (f.soloRecurrentes ? 1 : 0) +
    (f.soloPadres ? 1 : 0) +
    f.cantidadFiltrosAvanzados;

  function campos(escritorio: boolean): JSX.Element {
    return (
      <>
        <div className={escritorio ? 'filtros filtros-escritorio' : 'filtros'}>
          <SelectorLista
            id={`${prefijo}-filtro-estado`}
          etiqueta="Estado"
          valor={f.estado}
          alCambiar={f.setEstado}
          opciones={ESTADOS_OT.map((e) => ({ id: e, nombre: ETIQUETA_ESTADO_OT[e] }))}
          textoVacio="Todos los estados"
        />
        <label className="campo campo-checkbox" htmlFor={`${prefijo}-filtro-solo-vencidas`}>
          <input
            id={`${prefijo}-filtro-solo-vencidas`}
            type="checkbox"
            checked={f.soloVencidas}
            onChange={(e) => f.setSoloVencidas(e.target.checked)}
          />
          Solo vencidas
        </label>
        <label className="campo campo-checkbox" htmlFor={`${prefijo}-filtro-solo-recurrentes`}>
          <input
            id={`${prefijo}-filtro-solo-recurrentes`}
            type="checkbox"
            checked={f.soloRecurrentes}
            onChange={(e) => f.setSoloRecurrentes(e.target.checked)}
          />
          Solo recurrentes
        </label>
        <label className="campo campo-checkbox" htmlFor={`${prefijo}-filtro-solo-padres`}>
          <input
            id={`${prefijo}-filtro-solo-padres`}
            type="checkbox"
            checked={f.soloPadres}
            onChange={(e) => f.setSoloPadres(e.target.checked)}
          />
          Solo padres
        </label>
        <SelectorLista
          id={`${prefijo}-filtro-prioridad`}
          etiqueta="Prioridad"
          valor={f.prioridad}
          alCambiar={f.setPrioridad}
          opciones={PRIORIDADES.map((p) => ({ id: p, nombre: ETIQUETA_PRIORIDAD[p] }))}
          textoVacio="Todas las prioridades"
        />
        <SelectorUbicacion
          id={`${prefijo}-filtro-ubicacion`}
          valor={f.ubicacionId}
          alCambiar={(v) => {
            f.setUbicacionId(v);
            f.setActivoId('');
          }}
          ubicaciones={ubicaciones}
        />
        <SelectorLista
          id={`${prefijo}-filtro-asignado`}
          etiqueta="Asignado a"
          valor={f.asignadoId}
          alCambiar={f.setAsignadoId}
          grupos={[
            { etiqueta: 'Grupos', opciones: (grupos ?? []).map((g) => ({ id: g.id, nombre: g.nombre })) },
            { etiqueta: 'Usuarios', opciones: (usuarios ?? []).map((u) => ({ id: u.id, nombre: u.nombre })) },
          ]}
          textoVacio="Todos"
        />
        <SelectorLista
          id={`${prefijo}-filtro-grupo`}
          etiqueta="Grupo"
          valor={f.grupoId}
          alCambiar={f.setGrupoId}
          opciones={(grupos ?? []).map((g) => ({ id: g.id, nombre: g.nombre }))}
          textoVacio="Todos"
        />
        <SelectorLista
          id={`${prefijo}-filtro-recurrencia`}
          etiqueta="Recurrencia"
          valor={f.recurrencia}
          alCambiar={f.setRecurrencia}
          opciones={RECURRENCIAS.map((r) => ({ id: r, nombre: ETIQUETA_RECURRENCIA[r] }))}
          textoVacio="Todas"
        />
      </div>

      <details className={`panel filtros-avanzados${escritorio ? ' filtros-escritorio' : ''}`}>
        <summary>
          Más filtros{f.cantidadFiltrosAvanzadosPanel > 0 ? ` (${f.cantidadFiltrosAvanzadosPanel})` : ''}
        </summary>
        <div className="panel-cuerpo">
          <div className="rejilla-campos">
            <SelectorLista
              id={`${prefijo}-filtro-tipo`}
              etiqueta="Tipo de trabajo"
              valor={f.tipoTrabajo}
              alCambiar={f.setTipoTrabajo}
              opciones={TIPOS_TRABAJO.map((t) => ({ id: t, nombre: ETIQUETA_TIPO_TRABAJO[t] }))}
              textoVacio="Todos los tipos"
            />
            <SelectorLista
              id={`${prefijo}-filtro-falla`}
              etiqueta="Tipo de falla"
              valor={f.tipoFallaId}
              alCambiar={f.setTipoFallaId}
              opciones={(fallas ?? []).map((x) => ({ id: x.id, nombre: x.nombre }))}
              textoVacio="Todos"
              vacio="Aún no hay tipos de falla."
            />
            <SelectorCatalogo
              id={`${prefijo}-filtro-encargado`}
              codigo="CATEGORIA_ENCARGADO"
              etiqueta="Categoría de encargado"
              valor={f.encargadoCategoriaId}
              alCambiar={f.setEncargadoCategoriaId}
              textoVacio="Todas"
            />
            <SelectorActivo
              id={`${prefijo}-filtro-activo`}
              valor={f.activoId}
              alCambiar={f.setActivoId}
              ubicacionId={f.ubicacionId || undefined}
            />
          </div>

          <div className="rejilla-campos">
            <SelectorFecha
              id={`${prefijo}-filtro-inicio-desde`}
              etiqueta="Fecha de inicio (desde)"
              valor={f.fechaInicioDesde}
              alCambiar={f.setFechaInicioDesde}
            />
            <SelectorFecha
              id={`${prefijo}-filtro-inicio-hasta`}
              etiqueta="Fecha de inicio (hasta)"
              valor={f.fechaInicioHasta}
              alCambiar={f.setFechaInicioHasta}
            />
            <SelectorFecha
              id={`${prefijo}-filtro-creado-desde`}
              etiqueta="Creada desde"
              valor={f.creadoDesde}
              alCambiar={f.setCreadoDesde}
            />
            <SelectorFecha
              id={`${prefijo}-filtro-creado-hasta`}
              etiqueta="Creada hasta"
              valor={f.creadoHasta}
              alCambiar={f.setCreadoHasta}
            />
            <div className="campo">
              <label htmlFor={`${prefijo}-filtro-tiempo-desde`}>Tiempo estimado desde (min.)</label>
              <input
                id={`${prefijo}-filtro-tiempo-desde`}
                type="number"
                min={0}
                value={f.tiempoEstimadoDesde}
                onChange={(e) => f.setTiempoEstimadoDesde(e.target.value)}
              />
            </div>
            <div className="campo">
              <label htmlFor={`${prefijo}-filtro-tiempo-hasta`}>Tiempo estimado hasta (min.)</label>
              <input
                id={`${prefijo}-filtro-tiempo-hasta`}
                type="number"
                min={0}
                value={f.tiempoEstimadoHasta}
                onChange={(e) => f.setTiempoEstimadoHasta(e.target.value)}
              />
            </div>
          </div>

          {f.cantidadFiltrosAvanzados > 0 ? (
            <button type="button" className="boton secundario pequeno" onClick={f.limpiarFiltrosAvanzados}>
              Limpiar filtros avanzados
            </button>
          ) : null}
        </div>
      </details>
    </>
    );
  }

  return (
    <>
      <div style={{ marginBottom: 12 }}>
        <LeyendaOrigenOT />
      </div>

      <button
        type="button"
        className="boton secundario solo-movil boton-filtros-movil"
        onClick={() => setModalAbierto(true)}
      >
        Filtros{cantidadFiltrosTotal > 0 ? ` (${cantidadFiltrosTotal})` : ''}
      </button>

      {campos(true)}

      {modalAbierto ? (
        <Dialogo titulo="Filtros" alCerrar={() => setModalAbierto(false)}>
          {campos(false)}
        </Dialogo>
      ) : null}
    </>
  );
}

function CabeceraOrdenes({
  vista,
  buscar,
  alCambiarBuscar,
}: {
  vista: 'tabla' | 'calendario' | 'gantt';
  buscar: string;
  alCambiarBuscar: (valor: string) => void;
}): JSX.Element {
  const navegar = useNavigate();
  const { puede } = useSesion();
  const cliente = useQueryClient();
  return (
    <div className="cabecera-pagina cabecera-lista">
      <div className="cabecera-lista-titulo">
        <h1>Órdenes de Trabajo</h1>
        <div className="cabecera-ordenes-vista">
          <IconoCalendario />
          <SelectorLista
            id="ot-vista"
            valor={vista}
            alCambiar={(v) =>
              navegar(v === 'calendario' ? '/ordenes/calendario' : v === 'gantt' ? '/ordenes/gantt' : '/ordenes')
            }
            opciones={[
              { id: 'tabla', nombre: 'Vista de tabla' },
              { id: 'calendario', nombre: 'Vista de calendario' },
              { id: 'gantt', nombre: 'Vista Gantt' },
            ]}
            requerido
          />
        </div>
      </div>
      <div className="campo cabecera-lista-buscar">
        <IconoLupa />
        <input
          aria-label="Buscar órdenes de trabajo"
          placeholder="Buscar Órdenes de Trabajo"
          value={buscar}
          onChange={(e) => alCambiarBuscar(e.target.value)}
        />
      </div>
      {puede('ot:crear') ? (
        <>
          <EnlaceRuta to="/ordenes/nueva" className="boton oculto-en-movil">
            + Nueva Orden de Trabajo
          </EnlaceRuta>
          <BotonImportarCsv
            ruta="ordenes"
            etiqueta="Importar OT"
            alTerminar={() => void cliente.invalidateQueries({ queryKey: ['ordenes'] })}
          />
          {/* En móvil el botón fijo (§ pedido) reemplaza al de la cabecera: uno
              siempre visible abajo a la izquierda en vez de competir por
              espacio con el título y el buscador. */}
          <EnlaceRuta to="/ordenes/nueva" className="boton-flotante-crear" aria-label="Nueva orden de trabajo">
            <IconoMas />
          </EnlaceRuta>
        </>
      ) : null}
    </div>
  );
}

/**
 * Estado normal, o Activa/Pausada si es una plantilla recurrente (§ pedido):
 * misma celda de siempre, pero una plantilla nunca tiene un estado de
 * trabajo real (nunca avanza de ABIERTA) — lo único que tiene sentido
 * mostrar y tocar ahí es el interruptor de la recurrencia.
 */
function EstadoOCeldaOT({ orden }: { orden: OrdenTrabajo }): JSX.Element {
  const cliente = useQueryClient();
  const activar = useMutation({
    mutationFn: (activa: boolean) => api.post(`/ordenes-trabajo/${orden.id}/recurrencia-activa`, { activa }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: ['ordenes'] }),
  });

  // La vencida se marca en toda la fila (`fila-ot-vencida`), no en esta celda:
  // acá siempre se ve el estado real de la OT.
  if (!orden.esPlantillaRecurrente) return <EstadoOtEtiqueta estado={orden.estado} />;

  return (
    <button
      type="button"
      className="boton secundario pequeno"
      disabled={activar.isPending}
      onClick={(e) => {
        e.stopPropagation();
        activar.mutate(!orden.recurrenciaActiva);
      }}
    >
      {orden.recurrenciaActiva ? 'Activa' : 'Pausada'}
    </button>
  );
}

/**
 * Filtros, página y filas expandidas de la lista de OT, recordados mientras
 * dure la sesión (se pierde al recargar, no al navegar) — sin esto, entrar
 * a una OT o a un activo desde la lista y volver la dejaba en blanco: sin
 * filtro, sin la padre desplegada, arriba del todo (§ pedido). Vive fuera
 * del componente para sobrevivir a que `ListaOrdenes` se desmonte al salir
 * de la ruta.
 */
interface EstadoListaOrdenesGuardado {
  pagina: number;
  estado: string;
  prioridad: string;
  buscar: string;
  soloVencidas: boolean;
  verCompletadas?: boolean;
  soloPadres?: boolean;
  tipoTrabajo: string;
  encargadoCategoriaId: string;
  ubicacionId: string;
  activoId: string;
  tipoFallaId: string;
  asignadoId: string;
  grupoId: string;
  recurrencia: string;
  fechaInicioDesde: string;
  creadoDesde: string;
  fechaInicioHasta: string;
  creadoHasta: string;
  fechaDesde: string;
  fechaHasta: string;
  tiempoEstimadoDesde: string;
  tiempoEstimadoHasta: string;
  expandidas: string[];
}
/** Color del círculo de cada evento del hilo: el tono dice qué pasó de un vistazo. */
function tonoEvento(tipo: string): string {
  switch (tipo) {
    case 'COMENTARIO':
      return 'comentario';
    case 'CREACION':
    case 'SOLICITUD_APROBADA':
    case 'SOLICITUD_RESTABLECIDA':
    case 'COMPLETADA':
      return 'tono-exito';
    case 'PUESTA_EN_ESPERA':
    case 'COMPLETADA_CON_ALERTAS':
    case 'CAMBIO_PRIORIDAD':
      return 'tono-alerta';
    case 'ELIMINACION':
    case 'SOLICITUD_DECLINADA':
    case 'ACCION_CORRECTIVA_ELIMINADA':
    case 'PROCEDIMIENTO_REMOVIDO':
      return 'tono-falla';
    case 'PROGRESO_INICIADO':
    case 'CAMBIO_ESTADO':
    case 'REAPERTURA':
      return 'tono-progreso';
    default:
      return 'tono-neutro';
  }
}

let estadoListaOrdenesGuardado: EstadoListaOrdenesGuardado | null = null;

export function ListaOrdenes(): JSX.Element {
  const { puede } = useSesion();
  const navegar = useNavigate();
  const esMovil = useEsMovil();
  const [parametrosUrl] = useSearchParams();
  // El enlace «Órdenes de Trabajo» del menú recuerda si la última vez que el
  // usuario estuvo acá fue en esta lista o en el calendario (§ bug reportado).
  useEffect(() => {
    guardarVistaOrdenesPreferida('tabla');
  }, []);
  // Los parámetros de la URL (p. ej. un link de «Vencidas» del dashboard)
  // expresan una intención explícita y ganan sobre lo recordado de una
  // visita anterior; sin ellos, se recupera el filtro con el que se había
  // quedado la última vez.
  const previo = estadoListaOrdenesGuardado;
  const [pagina, setPagina] = useState(previo?.pagina ?? 1);
  const [estado, setEstado] = useState<string>(parametrosUrl.get('estado') ?? previo?.estado ?? '');
  const [prioridad, setPrioridad] = useState<string>(previo?.prioridad ?? '');
  const [buscar, setBuscar] = useState(previo?.buscar ?? '');
  const [soloVencidas, setSoloVencidas] = useState(
    parametrosUrl.get('vencidas') === 'true' ? true : (previo?.soloVencidas ?? false),
  );
  // Por defecto las completadas no se listan: el técnico ve primero lo que tiene abierto.
  const [verCompletadas, setVerCompletadas] = useState(previo?.verCompletadas ?? false);
  const [soloPadres, setSoloPadres] = useState(previo?.soloPadres ?? false);
  const [tipoTrabajo, setTipoTrabajo] = useState(previo?.tipoTrabajo ?? '');
  const [encargadoCategoriaId, setEncargadoCategoriaId] = useState(previo?.encargadoCategoriaId ?? '');
  const [ubicacionId, setUbicacionId] = useState(previo?.ubicacionId ?? '');
  const [activoId, setActivoId] = useState(previo?.activoId ?? '');
  const [tipoFallaId, setTipoFallaId] = useState(previo?.tipoFallaId ?? '');
  const [tipoActivoId, setTipoActivoId] = useState('');
  const [asignadoId, setAsignadoId] = useState(previo?.asignadoId ?? '');
  const [grupoId, setGrupoId] = useState(previo?.grupoId ?? '');
  const [recurrencia, setRecurrencia] = useState(previo?.recurrencia ?? '');
  const [fechaInicioDesde, setFechaInicioDesde] = useState(previo?.fechaInicioDesde ?? '');
  const [creadoDesde, setCreadoDesde] = useState(previo?.creadoDesde ?? '');
  const [fechaInicioHasta, setFechaInicioHasta] = useState(previo?.fechaInicioHasta ?? '');
  const [creadoHasta, setCreadoHasta] = useState(previo?.creadoHasta ?? '');
  const [fechaDesde, setFechaDesde] = useState(previo?.fechaDesde ?? '');
  const [fechaHasta, setFechaHasta] = useState(previo?.fechaHasta ?? '');
  const [tiempoEstimadoDesde, setTiempoEstimadoDesde] = useState(previo?.tiempoEstimadoDesde ?? '');
  const [tiempoEstimadoHasta, setTiempoEstimadoHasta] = useState(previo?.tiempoEstimadoHasta ?? '');
  // Una OT padre despliega sus hijas ahí mismo, sin salir al detalle — el
  // mismo dato (`subOrdenes.total`) que ya trae cada fila decide si le toca
  // chevron o el espacio vacío que lo alinea con las que no tienen hijas.
  const [expandidas, setExpandidas] = useState<Set<string>>(new Set(previo?.expandidas ?? []));

  // Se guarda en cada render (no solo al desmontar): así, si el usuario
  // cierra la pestaña o recarga justo después de tocar un filtro, lo tocado
  // ya quedó registrado — la sesión no depende de un cleanup que llegue a
  // tiempo.
  useEffect(() => {
    estadoListaOrdenesGuardado = {
      pagina,
      estado,
      prioridad,
      buscar,
      soloVencidas,
      verCompletadas,
      soloPadres,
      tipoTrabajo,
      encargadoCategoriaId,
      ubicacionId,
      activoId,
      tipoFallaId,
      asignadoId,
      grupoId,
      recurrencia,
      fechaInicioDesde,
      creadoDesde,
      fechaInicioHasta,
      creadoHasta,
      fechaDesde,
      fechaHasta,
      tiempoEstimadoDesde,
      tiempoEstimadoHasta,
      expandidas: [...expandidas],
    };
  });

  const { data: ubicaciones } = useUbicaciones();
  const { data: fallas } = useTiposFalla();
  const { data: tiposActivo } = useTiposActivo();
  const { data: grupos } = useGruposOpciones();
  const { data: usuarios } = useUsuariosOpciones();

  const filtrosAvanzados = {
    tipoTrabajo,
    encargadoCategoriaId,
    ubicacionId,
    tipoActivoId,
    activoId,
    tipoFallaId,
    asignadoId,
    grupoId,
    recurrencia,
    fechaInicioDesde,
    creadoDesde,
    fechaInicioHasta,
    creadoHasta,
    fechaDesde,
    fechaHasta,
    tiempoEstimadoDesde,
    tiempoEstimadoHasta,
  };
  const cantidadFiltrosAvanzados = Object.values(filtrosAvanzados).filter((v) => v !== '').length;
  // Ubicación, Asignado a y Recurrencia viven en la fila rápida, junto a
  // Estado/Prioridad (no en el panel «Más filtros»): el contador de ese panel
  // no debe sumarlos, o mostraría un número sin campos visibles adentro que
  // lo expliquen.
  const cantidadFiltrosAvanzadosPanel = Object.entries(filtrosAvanzados).filter(
    ([clave, valor]) =>
      valor !== '' && clave !== 'ubicacionId' && clave !== 'asignadoId' && clave !== 'grupoId' && clave !== 'recurrencia',
  ).length;
  const cantidadFiltrosAplicados =
    cantidadFiltrosAvanzados + (buscar !== '' ? 1 : 0) + (estado !== '' ? 1 : 0) + (prioridad !== '' ? 1 : 0);

  // En móvil todos los filtros se editan juntos en un modal y solo se aplican
  // al confirmar (§«más filtros»), en vez de disparar una consulta por tecla.
  const [modalFiltrosAbierto, setModalFiltrosAbierto] = useState(false);
  const [borrador, setBorrador] = useState<Borrador | null>(null);

  function abrirModalFiltros(): void {
    setBorrador({
      buscar,
      estado,
      prioridad,
      tipoTrabajo,
      encargadoCategoriaId,
      ubicacionId,
      activoId,
      tipoFallaId,
      asignadoId,
      grupoId,
      recurrencia,
      fechaInicioDesde,
      creadoDesde,
      fechaInicioHasta,
      creadoHasta,
      fechaDesde,
      fechaHasta,
      tiempoEstimadoDesde,
      tiempoEstimadoHasta,
    });
    setModalFiltrosAbierto(true);
  }

  function establecerBorrador<K extends keyof Borrador>(campo: K, valor: string): void {
    setBorrador((b) => (b ? { ...b, [campo]: valor } : b));
  }

  function limpiarBorrador(): void {
    setBorrador({
      buscar: '',
      estado: '',
      prioridad: '',
      tipoTrabajo: '',
      encargadoCategoriaId: '',
      ubicacionId: '',
      activoId: '',
      tipoFallaId: '',
      asignadoId: '',
      grupoId: '',
      recurrencia: '',
      fechaInicioDesde: '',
      creadoDesde: '',
      fechaInicioHasta: '',
      creadoHasta: '',
      fechaDesde: '',
      fechaHasta: '',
      tiempoEstimadoDesde: '',
      tiempoEstimadoHasta: '',
    });
  }

  function aplicarBorrador(): void {
    if (!borrador) return;
    setBuscar(borrador.buscar);
    setEstado(borrador.estado);
    setPrioridad(borrador.prioridad);
    setTipoTrabajo(borrador.tipoTrabajo);
    setEncargadoCategoriaId(borrador.encargadoCategoriaId);
    setUbicacionId(borrador.ubicacionId);
    setActivoId(borrador.activoId);
    setTipoFallaId(borrador.tipoFallaId);
    setAsignadoId(borrador.asignadoId);
    setRecurrencia(borrador.recurrencia);
    setFechaInicioDesde(borrador.fechaInicioDesde);
    setCreadoDesde(borrador.creadoDesde);
    setFechaInicioHasta(borrador.fechaInicioHasta);
    setCreadoHasta(borrador.creadoHasta);
    setFechaDesde(borrador.fechaDesde);
    setFechaHasta(borrador.fechaHasta);
    setTiempoEstimadoDesde(borrador.tiempoEstimadoDesde);
    setTiempoEstimadoHasta(borrador.tiempoEstimadoHasta);
    setPagina(1);
    setModalFiltrosAbierto(false);
  }

  function limpiarFiltrosAvanzados(): void {
    setTipoTrabajo('');
    setEncargadoCategoriaId('');
    setTipoActivoId('');
    setUbicacionId('');
    setActivoId('');
    setTipoFallaId('');
    setAsignadoId('');
    setRecurrencia('');
    setFechaInicioDesde('');
    setCreadoDesde('');
    setFechaInicioHasta('');
    setCreadoHasta('');
    setFechaDesde('');
    setFechaHasta('');
    setTiempoEstimadoDesde('');
    setTiempoEstimadoHasta('');
    setPagina(1);
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['ordenes', pagina, estado, verCompletadas, soloPadres, prioridad, buscar, soloVencidas, filtrosAvanzados],
    queryFn: () =>
      api.get<Pagina<OrdenTrabajo>>(
        `/ordenes-trabajo${consulta({
          pagina,
          tamano: 25,
          // Las más recientes primero, por fecha de creación.
          orden: 'creado',
          direccion: 'desc',
          // Sin estado elegido y con «Ver completadas» apagado, solo se piden las abiertas.
          estado: estado || (verCompletadas ? '' : ESTADOS_OT_ABIERTOS.join(',')),
          prioridad,
          buscar,
          vencidas: soloVencidas ? 'true' : '',
          soloPadres: soloPadres ? 'true' : '',
          // Las sub-órdenes se ven expandiendo su OT padre (`TarjetasHijasOT`),
          // no sueltas en esta lista (§ bug reportado: con suficientes
          // sub-órdenes, una página de 25 filas del servidor traía varias
          // hijas que el cliente descartaba al pintar, así que se veían
          // menos OT de las que el total/paginación prometían).
          soloRaiz: 'true',
          ...filtrosAvanzados,
          fechaDesde,
          fechaHasta,
        })}`,
      ),
  });

  // KPIs por estado (§ pedido: mismo color que la etiqueta, y clic filtra la
  // lista por ese estado). Solo los rangos de fecha los mueven: estado, prioridad,
  // búsqueda y demás filtros afectan la lista, no los contadores.
  const filtrosFecha = { creadoDesde, creadoHasta, fechaInicioDesde, fechaInicioHasta, fechaDesde, fechaHasta };
  const { data: resumen } = useQuery({
    // Bajo la clave ['ordenes'] para que cada invalidación de la lista (cambio de
    // estado, alta, edición, borrado) también refresque los KPIs.
    queryKey: ['ordenes', 'resumen', filtrosFecha],
    queryFn: () =>
      api.get<{
        porEstado: { estado: EstadoOT; cantidad: number; subOrdenes: number }[];
        vencidas: number;
        vencidasSubOrdenes: number;
      }>(`/ordenes-trabajo/resumen${consulta(filtrosFecha)}`),
  });
  // El número grande es solo OT raíz (lo mismo que lista la tabla, con
  // `soloRaiz`); las sub-órdenes se muestran aparte, más chico, en vez de
  // sumarse al mismo número — mezcladas, el badge no coincidía con las filas
  // visibles (§ bug reportado: "En curso: 5" con solo 2 filas en pantalla).
  const totalGlobal = (resumen?.porEstado ?? []).reduce((suma, p) => suma + p.cantidad, 0);
  const totalSubOrdenes = (resumen?.porEstado ?? []).reduce((suma, p) => suma + p.subOrdenes, 0);
  const cantidadPorEstado = (e: EstadoOT): number => resumen?.porEstado.find((p) => p.estado === e)?.cantidad ?? 0;
  const subOrdenesPorEstado = (e: EstadoOT): number => resumen?.porEstado.find((p) => p.estado === e)?.subOrdenes ?? 0;
  const notaSubOrdenes = (n: number): string | undefined => (n > 0 ? `+${n} sub-orden${n === 1 ? '' : 'es'}` : undefined);

  // Borrado masivo (§ pedido, solo Administrador vía `ot:eliminar`): al marcar una OT
  // padre, el backend cascadea sola a sus hijas — acá no hace falta resolver el árbol.
  const puedeEliminar = puede('ot:eliminar');
  // La selección también sirve para el consolidado de materiales: la ven quienes pueden consolidar.
  const puedeConsolidar = puede('ot:leer') && puede('solicitud:leer');
  const puedeSeleccionar = puedeEliminar || puedeConsolidar;
  const cliente = useQueryClient();
  const [seleccionadas, setSeleccionadas] = useState<Set<string>>(new Set());
  const [mostrarConfirmarEliminar, setMostrarConfirmarEliminar] = useState(false);
  const eliminarSeleccionadas = useMutation({
    mutationFn: (ids: string[]) => api.post<{ eliminadas: number }>('/ordenes-trabajo/eliminar-masivo', { ids }),
    onSuccess: () => {
      setSeleccionadas(new Set());
      setMostrarConfirmarEliminar(false);
      void cliente.invalidateQueries({ queryKey: ['ordenes'] });
      void cliente.invalidateQueries({ queryKey: ['ordenes', 'resumen'] });
    },
  });
  const alternarSeleccion = (id: string): void =>
    setSeleccionadas((s) => {
      const copia = new Set(s);
      if (copia.has(id)) copia.delete(id);
      else copia.add(id);
      return copia;
    });
  // Solo las raíces de esta página: las hijas viajan implícitas (el backend
  // cascadea), y sumarlas acá infla el contador sin cambiar qué se borra.
  const idsRaizDeLaPagina = (data?.datos ?? []).filter((o) => !o.ordenTrabajoPadreId).map((o) => o.id);
  const todasSeleccionadas = idsRaizDeLaPagina.length > 0 && idsRaizDeLaPagina.every((id) => seleccionadas.has(id));

  return (
    <>
      <CabeceraOrdenes
        vista="tabla"
        buscar={buscar}
        alCambiarBuscar={(v) => {
          setBuscar(v);
          setPagina(1);
        }}
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <LeyendaOrigenOT />
        {puedeSeleccionar && idsRaizDeLaPagina.length > 0 ? (
          <button
            type="button"
            className="boton secundario pequeno"
            onClick={() =>
              setSeleccionadas((s) =>
                todasSeleccionadas
                  ? new Set([...s].filter((id) => !idsRaizDeLaPagina.includes(id)))
                  : new Set([...s, ...idsRaizDeLaPagina]),
              )
            }
          >
            {todasSeleccionadas ? 'Deseleccionar todas' : 'Seleccionar todas'}
          </button>
        ) : null}
      </div>

      <div className="rejilla-indicadores">
        <Indicador
          titulo="TOTAL"
          valor={totalGlobal}
          nota={notaSubOrdenes(totalSubOrdenes)}
          activo={estado === '' && !soloVencidas}
          // Total = todas las OT de cualquier estado, con completadas incluidas
          // y sin filtro de fecha de creación.
          onClick={() => {
            setEstado('');
            setSoloVencidas(false);
            setVerCompletadas(true);
            setPrioridad('');
            setBuscar('');
            limpiarFiltrosAvanzados();
            setCreadoDesde('');
            setPagina(1);
          }}
        />
        {ESTADOS_OT.map((e) => (
          <Indicador
            key={e}
            titulo={ETIQUETA_ESTADO_OT[e].toUpperCase()}
            valor={cantidadPorEstado(e)}
            nota={notaSubOrdenes(subOrdenesPorEstado(e))}
            tono={TONO_ESTADO_VISUAL_OT[e]}
            activo={estado === e}
            className="col-ocultar-movil"
            onClick={() => {
              setEstado((actual) => (actual === e ? '' : e));
              setSoloVencidas(false);
              setPagina(1);
            }}
          />
        ))}
        <Indicador
          titulo="VENCIDAS"
          valor={resumen?.vencidas ?? 0}
          tono="roja"
          nota={
            [
              notaSubOrdenes(resumen?.vencidasSubOrdenes ?? 0),
              (resumen?.vencidas ?? 0) > 0 ? 'Requieren atención' : 'Al día',
            ]
              .filter(Boolean)
              .join(' · ')
          }
          activo={soloVencidas}
          onClick={() => {
            setSoloVencidas((v) => !v);
            setEstado('');
            setPagina(1);
          }}
        />
      </div>

      <button
        type="button"
        className="boton secundario solo-movil boton-filtros-movil"
        onClick={abrirModalFiltros}
      >
        Filtros{cantidadFiltrosAplicados > 0 ? ` (${cantidadFiltrosAplicados})` : ''}
      </button>

      <div className="filtros filtros-escritorio">
        <SelectorLista
          id="ot-filtro-estado-rapido"
          etiqueta="Estado"
          valor={estado}
          alCambiar={(v) => {
            setEstado(v);
            if (v !== '') setSoloVencidas(false);
            setPagina(1);
          }}
          opciones={ESTADOS_OT.map((e) => ({ id: e, nombre: ETIQUETA_ESTADO_OT[e] }))}
          textoVacio="Todos los estados"
        />
        <SelectorLista
          id="ot-filtro-prioridad-rapido"
          etiqueta="Prioridad"
          valor={prioridad}
          alCambiar={(v) => {
            setPrioridad(v);
            setPagina(1);
          }}
          opciones={PRIORIDADES.map((p) => ({ id: p, nombre: ETIQUETA_PRIORIDAD[p] }))}
          textoVacio="Todas las prioridades"
        />
        <SelectorUbicacion
          id="ot-filtro-ubicacion-rapido"
          valor={ubicacionId}
          alCambiar={(v) => {
            setUbicacionId(v);
            setActivoId('');
            setPagina(1);
          }}
          ubicaciones={ubicaciones}
        />
        <SelectorLista
          id="ot-filtro-asignado-rapido"
          etiqueta="Asignado a"
          valor={asignadoId}
          alCambiar={(v) => {
            setAsignadoId(v);
            setPagina(1);
          }}
          grupos={[
            { etiqueta: 'Grupos', opciones: (grupos ?? []).map((g) => ({ id: g.id, nombre: g.nombre })) },
            { etiqueta: 'Usuarios', opciones: (usuarios ?? []).map((u) => ({ id: u.id, nombre: u.nombre })) },
          ]}
          textoVacio="Todos"
        />
        <SelectorLista
          id="ot-filtro-recurrencia-rapido"
          etiqueta="Recurrencia"
          valor={recurrencia}
          alCambiar={(v) => {
            setRecurrencia(v);
            setPagina(1);
          }}
          opciones={RECURRENCIAS.map((r) => ({ id: r, nombre: ETIQUETA_RECURRENCIA[r] }))}
          textoVacio="Todas"
        />
        <label className="campo campo-checkbox" htmlFor="ot-filtro-solo-vencidas">
          <input
            id="ot-filtro-solo-vencidas"
            type="checkbox"
            checked={soloVencidas}
            onChange={(e) => {
              setSoloVencidas(e.target.checked);
              if (e.target.checked) setEstado('');
              setPagina(1);
            }}
          />
          Solo vencidas
        </label>
        <label className="campo campo-checkbox" htmlFor="ot-filtro-ver-completadas">
          <input
            id="ot-filtro-ver-completadas"
            type="checkbox"
            checked={verCompletadas}
            disabled={estado !== ''}
            onChange={(e) => {
              setVerCompletadas(e.target.checked);
              setPagina(1);
            }}
          />
          Ver completadas
        </label>
        <label className="campo campo-checkbox" htmlFor="ot-filtro-solo-padres">
          <input
            id="ot-filtro-solo-padres"
            type="checkbox"
            checked={soloPadres}
            onChange={(e) => {
              setSoloPadres(e.target.checked);
              setPagina(1);
            }}
          />
          Solo padres
        </label>
      </div>

      <details className="panel filtros-avanzados filtros-escritorio">
        <summary>
          Más filtros{cantidadFiltrosAvanzadosPanel > 0 ? ` (${cantidadFiltrosAvanzadosPanel})` : ''}
        </summary>
        <div className="panel-cuerpo">
          {/* Clasificación de la OT (§ pedido: agrupadas, no una sola suelta
              al fondo del panel). */}
          <div className="rejilla-campos">
            <SelectorLista
              id="ot-filtro-tipo"
              etiqueta="Tipo de trabajo"
              valor={tipoTrabajo}
              alCambiar={(v) => {
                setTipoTrabajo(v);
                setPagina(1);
              }}
              opciones={TIPOS_TRABAJO.map((t) => ({ id: t, nombre: ETIQUETA_TIPO_TRABAJO[t] }))}
              textoVacio="Todos los tipos"
            />
            <SelectorLista
              id="ot-filtro-falla"
              etiqueta="Tipo de falla"
              valor={tipoFallaId}
              alCambiar={(v) => {
                setTipoFallaId(v);
                setPagina(1);
              }}
              opciones={(fallas ?? []).map((f) => ({ id: f.id, nombre: f.nombre }))}
              textoVacio="Todos"
              vacio="Aún no hay tipos de falla."
            />
            <SelectorLista
              id="ot-filtro-tipo-activo"
              etiqueta="Tipo de activo"
              valor={tipoActivoId}
              alCambiar={(v) => {
                setTipoActivoId(v);
                setPagina(1);
              }}
              opciones={(tiposActivo ?? []).map((t) => ({ id: t.id, nombre: t.nombre }))}
              textoVacio="Todos"
              vacio="Aún no hay tipos de activo."
            />
            <SelectorCatalogo
              id="ot-filtro-encargado"
              codigo="CATEGORIA_ENCARGADO"
              etiqueta="Categoría de encargado"
              valor={encargadoCategoriaId}
              alCambiar={(v) => {
                setEncargadoCategoriaId(v);
                setPagina(1);
              }}
              textoVacio="Todas"
            />
            <SelectorActivo
              id="ot-filtro-activo"
              valor={activoId}
              alCambiar={(v) => {
                setActivoId(v);
                setPagina(1);
              }}
              ubicacionId={ubicacionId || undefined}
            />
          </div>

          {/* Rangos de fecha, agrupados aparte de la clasificación. */}
          <div className="rejilla-campos">
            <SelectorFecha
              id="ot-filtro-inicio-desde"
              etiqueta="Fecha de inicio (desde)"
              valor={fechaInicioDesde}
              alCambiar={(v) => {
                setFechaInicioDesde(v);
                setPagina(1);
              }}
            />
            <SelectorFecha
              id="ot-filtro-inicio-hasta"
              etiqueta="Fecha de inicio (hasta)"
              valor={fechaInicioHasta}
              alCambiar={(v) => {
                setFechaInicioHasta(v);
                setPagina(1);
              }}
            />
            <SelectorFecha
              id="ot-filtro-creado-desde"
              etiqueta="Creada desde"
              valor={creadoDesde}
              alCambiar={(v) => {
                setCreadoDesde(v);
                setPagina(1);
              }}
            />
            <SelectorFecha
              id="ot-filtro-creado-hasta"
              etiqueta="Creada hasta"
              valor={creadoHasta}
              alCambiar={(v) => {
                setCreadoHasta(v);
                setPagina(1);
              }}
            />
            <SelectorFecha
              id="ot-filtro-vencimiento-desde"
              etiqueta="Fecha de vencimiento (desde)"
              valor={fechaDesde}
              alCambiar={(v) => {
                setFechaDesde(v);
                setPagina(1);
              }}
            />
            <SelectorFecha
              id="ot-filtro-vencimiento-hasta"
              etiqueta="Fecha de vencimiento (hasta)"
              valor={fechaHasta}
              alCambiar={(v) => {
                setFechaHasta(v);
                setPagina(1);
              }}
            />
          </div>

          {/* Rango numérico, al final. */}
          <div className="rejilla-campos">
            <div className="campo">
              <label htmlFor="ot-filtro-tiempo-desde">Tiempo estimado desde (min.)</label>
              <input
                id="ot-filtro-tiempo-desde"
                type="number"
                min={0}
                value={tiempoEstimadoDesde}
                onChange={(e) => {
                  setTiempoEstimadoDesde(e.target.value);
                  setPagina(1);
                }}
              />
            </div>
            <div className="campo">
              <label htmlFor="ot-filtro-tiempo-hasta">Tiempo estimado hasta (min.)</label>
              <input
                id="ot-filtro-tiempo-hasta"
                type="number"
                min={0}
                value={tiempoEstimadoHasta}
                onChange={(e) => {
                  setTiempoEstimadoHasta(e.target.value);
                  setPagina(1);
                }}
              />
            </div>
          </div>

          {cantidadFiltrosAvanzados > 0 ? (
            <button type="button" className="boton secundario pequeno" onClick={limpiarFiltrosAvanzados}>
              Limpiar filtros avanzados
            </button>
          ) : null}
        </div>
      </details>

      {modalFiltrosAbierto && borrador ? (
        <Dialogo titulo="Filtros" ancho={560} alCerrar={() => setModalFiltrosAbierto(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="campo">
              <label htmlFor="mf-buscar">Buscar</label>
              <input
                id="mf-buscar"
                placeholder="Buscar por título o descripción…"
                value={borrador.buscar}
                onChange={(e) => establecerBorrador('buscar', e.target.value)}
              />
            </div>

            <label className="campo campo-checkbox" htmlFor="mf-ver-completadas">
              <input
                id="mf-ver-completadas"
                type="checkbox"
                checked={verCompletadas}
                disabled={borrador.estado !== ''}
                onChange={(e) => {
                  setVerCompletadas(e.target.checked);
                  setPagina(1);
                }}
              />
              Ver completadas
            </label>

            <label className="campo campo-checkbox" htmlFor="mf-solo-padres">
              <input
                id="mf-solo-padres"
                type="checkbox"
                checked={soloPadres}
                onChange={(e) => {
                  setSoloPadres(e.target.checked);
                  setPagina(1);
                }}
              />
              Solo padres
            </label>

            <div className="rejilla-campos">
              <SelectorLista
                id="mf-estado"
                etiqueta="Estado"
                valor={borrador.estado}
                alCambiar={(v) => establecerBorrador('estado', v)}
                opciones={ESTADOS_OT.map((e) => ({ id: e, nombre: ETIQUETA_ESTADO_OT[e] }))}
                textoVacio="Todos los estados"
              />
              <SelectorLista
                id="mf-prioridad"
                etiqueta="Prioridad"
                valor={borrador.prioridad}
                alCambiar={(v) => establecerBorrador('prioridad', v)}
                opciones={PRIORIDADES.map((p) => ({ id: p, nombre: ETIQUETA_PRIORIDAD[p] }))}
                textoVacio="Todas las prioridades"
              />
              <SelectorLista
                id="mf-tipo"
                etiqueta="Tipo de trabajo"
                valor={borrador.tipoTrabajo}
                alCambiar={(v) => establecerBorrador('tipoTrabajo', v)}
                opciones={TIPOS_TRABAJO.map((t) => ({ id: t, nombre: ETIQUETA_TIPO_TRABAJO[t] }))}
                textoVacio="Todos los tipos"
              />

              <SelectorCatalogo
                id="mf-encargado"
                codigo="CATEGORIA_ENCARGADO"
                etiqueta="Categoría de encargado"
                valor={borrador.encargadoCategoriaId}
                alCambiar={(v) => establecerBorrador('encargadoCategoriaId', v)}
                textoVacio="Todas"
              />

              <SelectorUbicacion
                id="mf-ubicacion"
                valor={borrador.ubicacionId}
                alCambiar={(v) => setBorrador((b) => (b ? { ...b, ubicacionId: v, activoId: '' } : b))}
                ubicaciones={ubicaciones}
              />

              <SelectorActivo
                id="mf-activo"
                valor={borrador.activoId}
                alCambiar={(v) => establecerBorrador('activoId', v)}
                ubicacionId={borrador.ubicacionId || undefined}
              />
            </div>

            <div className="rejilla-campos">
              <SelectorFecha
                id="mf-inicio-desde"
                etiqueta="Fecha de inicio (desde)"
                valor={borrador.fechaInicioDesde}
                alCambiar={(v) => establecerBorrador('fechaInicioDesde', v)}
              />
              <SelectorFecha
                id="mf-inicio-hasta"
                etiqueta="Fecha de inicio (hasta)"
                valor={borrador.fechaInicioHasta}
                alCambiar={(v) => establecerBorrador('fechaInicioHasta', v)}
              />
              <SelectorFecha
                id="mf-creado-desde"
                etiqueta="Creada desde"
                valor={borrador.creadoDesde}
                alCambiar={(v) => establecerBorrador('creadoDesde', v)}
              />
              <SelectorFecha
                id="mf-creado-hasta"
                etiqueta="Creada hasta"
                valor={borrador.creadoHasta}
                alCambiar={(v) => establecerBorrador('creadoHasta', v)}
              />
              <SelectorFecha
                id="mf-venc-desde"
                etiqueta="Fecha de vencimiento (desde)"
                valor={borrador.fechaDesde}
                alCambiar={(v) => establecerBorrador('fechaDesde', v)}
              />
              <SelectorFecha
                id="mf-venc-hasta"
                etiqueta="Fecha de vencimiento (hasta)"
                valor={borrador.fechaHasta}
                alCambiar={(v) => establecerBorrador('fechaHasta', v)}
              />
            </div>

            <div className="rejilla-campos">
              <div className="campo">
                <label htmlFor="mf-tiempo-desde">Tiempo estimado desde (min.)</label>
                <input
                  id="mf-tiempo-desde"
                  type="number"
                  min={0}
                  value={borrador.tiempoEstimadoDesde}
                  onChange={(e) => establecerBorrador('tiempoEstimadoDesde', e.target.value)}
                />
              </div>
              <div className="campo">
                <label htmlFor="mf-tiempo-hasta">Tiempo estimado hasta (min.)</label>
                <input
                  id="mf-tiempo-hasta"
                  type="number"
                  min={0}
                  value={borrador.tiempoEstimadoHasta}
                  onChange={(e) => establecerBorrador('tiempoEstimadoHasta', e.target.value)}
                />
              </div>
              <SelectorLista
                id="mf-recurrencia"
                etiqueta="Recurrencia"
                valor={borrador.recurrencia}
                alCambiar={(v) => establecerBorrador('recurrencia', v)}
                opciones={RECURRENCIAS.map((r) => ({ id: r, nombre: ETIQUETA_RECURRENCIA[r] }))}
                textoVacio="Todas"
              />
            </div>

            <div className="rejilla-campos">
              <SelectorLista
                id="mf-falla"
                etiqueta="Tipo de falla"
                valor={borrador.tipoFallaId}
                alCambiar={(v) => establecerBorrador('tipoFallaId', v)}
                opciones={(fallas ?? []).map((f) => ({ id: f.id, nombre: f.nombre }))}
                textoVacio="Todos"
                vacio="Aún no hay tipos de falla."
              />
              <SelectorLista
                id="mf-asignado"
                etiqueta="Asignado a"
                valor={borrador.asignadoId}
                alCambiar={(v) => establecerBorrador('asignadoId', v)}
                grupos={[
                  { etiqueta: 'Grupos', opciones: (grupos ?? []).map((g) => ({ id: g.id, nombre: g.nombre })) },
                  { etiqueta: 'Usuarios', opciones: (usuarios ?? []).map((u) => ({ id: u.id, nombre: u.nombre })) },
                ]}
                textoVacio="Todos"
              />
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 4, flexWrap: 'wrap' }}>
              <button type="button" className="boton secundario" onClick={limpiarBorrador}>
                Limpiar
              </button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="boton secundario" onClick={() => setModalFiltrosAbierto(false)}>
                  Cancelar
                </button>
                <button type="button" className="boton" onClick={aplicarBorrador}>
                  Aplicar filtros
                </button>
              </div>
            </div>
          </div>
        </Dialogo>
      ) : null}

      <div className="panel">
        {error ? <Aviso tono="error">No se pudieron cargar las órdenes de trabajo.</Aviso> : null}
        {isLoading ? <Cargando /> : null}

        {data && data.datos.length === 0 ? (
          <Vacio titulo="Sin órdenes de trabajo" detalle="Ajuste los filtros o registre una nueva." />
        ) : null}

        {data && data.datos.length > 0 ? (
          <>
            {esMovil ? (
              <div className="tarjetas-ot">
                {/* Una sub-orden ya se ve al desplegar a su padre (§ pedido);
                    listarla también suelta acá sería la misma tarjeta dos veces. */}
                {data.datos.filter((orden) => !orden.ordenTrabajoPadreId).map((orden) => {
                  const tieneHijas = orden.subOrdenes.total > 0;
                  const expandida = expandidas.has(orden.id);
                  return (
                    <Fragment key={orden.id}>
                      <div
                        className="tarjeta-ot"
                        role="button"
                        tabIndex={0}
                        onClick={() => navegar(`/ordenes/${orden.id}`)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') navegar(`/ordenes/${orden.id}`);
                        }}
                      >
                        <MiniaturaImagenTabla url={orden.fotoUrl} nombre={orden.titulo} />
                        <div className="tarjeta-ot-cuerpo">
                          <div className="tarjeta-ot-cabecera">
                            <span className="numero-registro">#{orden.numero}</span>
                            <EstadoOCeldaOT orden={orden} />
                          </div>
                          <div className="tarjeta-ot-titulo">{orden.titulo}</div>
                          {orden.otOrigenNumero ? (
                            <div className="tarjeta-ot-nota">Correctiva de #{orden.otOrigenNumero}</div>
                          ) : null}
                          <OrigenOT orden={orden} />
                          <div className="tarjeta-ot-datos">
                            {orden.prioridad !== 'NINGUNA' ? <PrioridadEtiqueta prioridad={orden.prioridad} /> : null}
                            {orden.ubicacion ? <span>{orden.ubicacion}</span> : null}
                            {orden.fechaVencimiento ? (
                              <span style={orden.vencida ? { color: 'var(--falla)', fontWeight: 700 } : undefined}>
                                {formatearFecha(orden.fechaVencimiento)}
                              </span>
                            ) : null}
                          </div>
                          {tieneHijas ? (
                            <button
                              type="button"
                              className="tarjeta-ot-subordenes"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandidas((s) => {
                                  const copia = new Set(s);
                                  if (copia.has(orden.id)) copia.delete(orden.id);
                                  else copia.add(orden.id);
                                  return copia;
                                });
                              }}
                            >
                              {expandida ? '▾' : '▸'} {orden.subOrdenes.total - orden.subOrdenes.abiertas} de{' '}
                              {orden.subOrdenes.total} sub-órdenes completas
                            </button>
                          ) : null}
                        </div>
                      </div>
                      {expandida ? (
                        <TarjetasHijasOT ordenTrabajoPadreId={orden.id} expandidas={expandidas} setExpandidas={setExpandidas} />
                      ) : null}
                    </Fragment>
                  );
                })}
              </div>
            ) : (
              <div className="tabla-contenedor">
                {/* N.º, Título y Estado quedan fijos al desplazar la tabla en horizontal; el resto se recorre.
                    La columna de selección cambia la posición de esas tres: se marca con data-seleccion. */}
                <table className="tabla tabla-ordenes" data-seleccion={puedeSeleccionar ? 'si' : 'no'}>
                  <thead>
                    <tr>
                      {puedeSeleccionar ? (
                        <th>
                          <CasillaVerificacion
                            marcada={todasSeleccionadas}
                            alCambiar={() =>
                              setSeleccionadas((s) => {
                                if (todasSeleccionadas) {
                                  const copia = new Set(s);
                                  for (const id of idsRaizDeLaPagina) copia.delete(id);
                                  return copia;
                                }
                                return new Set([...s, ...idsRaizDeLaPagina]);
                              })
                            }
                            etiqueta="Seleccionar todas las órdenes de esta página"
                          />
                        </th>
                      ) : null}
                      <th>Foto</th>
                      <th>N.º</th>
                      <th>Título</th>
                      <th>Estado</th>
                      <th>Prioridad</th>
                      <th>Tipo</th>
                      <th>Ubicación</th>
                      <th>Activo</th>
                      <th>Asignado a</th>
                      <th>Grupo</th>
                      <th>Vencimiento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Una sub-orden ya se ve al desplegar a su padre (§ pedido);
                        listarla también suelta acá sería la misma fila dos veces. */}
                    {data.datos.filter((orden) => !orden.ordenTrabajoPadreId).map((orden) => {
                      const tieneHijas = orden.subOrdenes.total > 0;
                      const expandida = expandidas.has(orden.id);
                      return (
                        <Fragment key={orden.id}>
                          <tr className={`${tieneHijas ? 'fila-ot-padre ' : ''}${orden.vencida ? 'fila-ot-vencida' : ''}`.trim() || undefined}>
                            {puedeSeleccionar ? (
                              <td>
                                <CasillaVerificacion
                                  marcada={seleccionadas.has(orden.id)}
                                  alCambiar={() => alternarSeleccion(orden.id)}
                                  etiqueta={`Seleccionar orden #${orden.numero}`}
                                />
                              </td>
                            ) : null}
                            <td>
                              <MiniaturaImagenTabla url={orden.fotoUrl} nombre={orden.titulo} />
                            </td>
                            <td className="numero-registro">
                              <div className="celda-numero-ot">
                                {tieneHijas ? (
                                  <button
                                    type="button"
                                    className="chevron-arbol-ot"
                                    aria-label={
                                      expandida ? `Contraer sub-órdenes de #${orden.numero}` : `Ver sub-órdenes de #${orden.numero}`
                                    }
                                    onClick={() =>
                                      setExpandidas((s) => {
                                        const copia = new Set(s);
                                        if (copia.has(orden.id)) copia.delete(orden.id);
                                        else copia.add(orden.id);
                                        return copia;
                                      })
                                    }
                                  >
                                    {expandida ? '▾' : '▸'}
                                  </button>
                                ) : null}
                                #{orden.numero}
                              </div>
                            </td>
                            <td>
                              <EnlaceRuta to={`/ordenes/${orden.id}`} style={{ fontWeight: 600 }}>
                                {orden.titulo}
                              </EnlaceRuta>
                              {orden.otOrigenNumero ? (
                                <div style={{ fontSize: 11.5, color: 'var(--texto-tenue)' }}>
                                  Correctiva de #{orden.otOrigenNumero}
                                </div>
                              ) : null}
                              {orden.esPlantillaRecurrente ? (
                                <div style={{ fontSize: 11.5, color: 'var(--texto-tenue)' }}>
                                  {ETIQUETA_RECURRENCIA[orden.recurrencia]} · próxima:{' '}
                                  {orden.fechaVencimiento ? formatearFecha(orden.fechaVencimiento) : '—'}
                                </div>
                              ) : null}
                              {tieneHijas ? (
                                <div style={{ fontSize: 11.5, color: 'var(--texto-tenue)' }}>
                                  {orden.subOrdenes.total - orden.subOrdenes.abiertas} de {orden.subOrdenes.total} sub-órdenes completas
                                </div>
                              ) : null}
                              <OrigenOT orden={orden} />
                            </td>
                            <td>
                              <EstadoOCeldaOT orden={orden} />
                            </td>
                            <td>
                              <PrioridadEtiqueta prioridad={orden.prioridad} />
                            </td>
                            <td>{ETIQUETA_TIPO_TRABAJO[orden.tipoTrabajo]}</td>
                            <td>
                              {orden.ubicacion ?? '—'}
                              {orden.ubicacionRaiz ? (
                                <div style={{ fontSize: 11.5, color: 'var(--texto-tenue)' }}>
                                  Principal: {orden.ubicacionRaiz}
                                </div>
                              ) : null}
                            </td>
                            <td>
                              {orden.activo ?? (orden.sujetoEvaluable ? `Persona: ${orden.sujetoEvaluable}` : '—')}
                            </td>
                            <td>
                              <PersonasAsignadas asignados={orden.asignados} />
                            </td>
                            <td>
                              <GrupoAsignado asignados={orden.asignados} />
                            </td>
                            <td style={orden.vencida ? { color: 'var(--falla)', fontWeight: 700 } : undefined}>
                              {formatearFecha(orden.fechaVencimiento)}
                            </td>
                          </tr>
                          {expandida ? (
                            <FilasHijasOT
                              ordenTrabajoPadreId={orden.id}
                              expandidas={expandidas}
                              setExpandidas={setExpandidas}
                              puedeSeleccionar={puedeSeleccionar}
                              seleccionadas={seleccionadas}
                              alternarSeleccion={alternarSeleccion}
                            />
                          ) : null}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <Paginacion pagina={data.pagina} tamano={data.tamano} total={data.total} onCambiar={setPagina} />
          </>
        ) : null}
      </div>
      {puedeSeleccionar && seleccionadas.size > 0 ? (
        <div className="barra-acciones-fija">
          <span className="barra-acciones-fija-estado">
            <span className="barra-acciones-fija-punto" />
            {seleccionadas.size} orden(es) seleccionada(s)
          </span>
          <div className="barra-acciones-fija-botones">
            <button type="button" className="boton secundario pequeno" onClick={() => setSeleccionadas(new Set())}>
              Cancelar selección
            </button>
            {puedeConsolidar ? (
              <BotonConsolidadoMateriales
                etiqueta="Ver materiales consolidados"
                elegidas={[...seleccionadas].map((id) => `OT:${id}`)}
              />
            ) : null}
            {puedeEliminar ? (
              <button type="button" className="boton peligro pequeno" onClick={() => setMostrarConfirmarEliminar(true)}>
                Eliminar seleccionadas
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
      {mostrarConfirmarEliminar ? (
        <DialogoConfirmar
          titulo="Eliminar órdenes de trabajo"
          mensaje={`Se eliminarán ${seleccionadas.size} orden(es) de trabajo. Si alguna tiene sub-órdenes, también se eliminarán. Esta acción no se puede deshacer desde aquí.`}
          etiqueta="Eliminar"
          cargando={eliminarSeleccionadas.isPending}
          error={eliminarSeleccionadas.error}
          alCerrar={() => setMostrarConfirmarEliminar(false)}
          alConfirmar={() => eliminarSeleccionadas.mutate(Array.from(seleccionadas))}
        />
      ) : null}
    </>
  );
}

const ICONO_PRIORIDAD: Partial<Record<Prioridad, () => JSX.Element>> = {
  BAJA: IconoPrioridadBaja,
  MEDIA: IconoPrioridadMedia,
  ALTA: IconoPrioridadAlta,
};

function IconoPrioridad({ prioridad }: { prioridad: Prioridad }): JSX.Element | null {
  const Icono = ICONO_PRIORIDAD[prioridad];
  return Icono ? <Icono /> : null;
}

// ─── Calendario de OT (§3) ───────────────────────────────────────────────────

interface OrdenDeCalendario {
  id: string;
  numero: number;
  titulo: string;
  estado: EstadoOT;
  prioridad: Prioridad;
  tipoTrabajo: TipoTrabajo;
  tiempoEstimadoMinutos: number | null;
  ubicacion: string | null;
  activo: string | null;
  asignados: { id: string; nombre: string; tipo: 'USUARIO' | 'GRUPO' }[];
  fechaVencimiento: string | null;
  creadoEl: string;
  vencida: boolean;
  solicitudId: string | null;
  ordenTrabajoPadreId: string | null;
  generadaDesdeId: string | null;
  tieneProcedimiento: boolean;
}

/** Día que rige la posición en el calendario: vencimiento o, si no tiene, creación (§14.1). */
function fechaDeReferencia(o: OrdenDeCalendario): string {
  return (o.fechaVencimiento ?? o.creadoEl).slice(0, 10);
}

const DIAS_SEMANA = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

function claveDia(fecha: Date): string {
  return `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}-${String(fecha.getDate()).padStart(2, '0')}`;
}

/**
 * Cuadrícula que cubre el mes visible completo (4, 5 o 6 semanas según el
 * mes), incluyendo los días de los meses vecinos que caen en la misma semana
 * inicial o final (para no dejar celdas vacías) — se marcan con
 * `.calendario-fuera-mes` para distinguirlos. A diferencia de una cuadrícula
 * fija de 6 filas, no agrega una semana entera del mes siguiente cuando el
 * mes ya cerró en la fila anterior: eso sobraba espacio sin aportar nada.
 */
function diasDeLaCuadricula(mesVisible: Date): Date[] {
  const primerDia = new Date(mesVisible.getFullYear(), mesVisible.getMonth(), 1);
  // getDay(): 0=domingo..6=sábado; se convierte a lunes=0..domingo=6.
  const offset = (primerDia.getDay() + 6) % 7;
  const inicio = new Date(primerDia);
  inicio.setDate(inicio.getDate() - offset);

  const ultimoDia = new Date(mesVisible.getFullYear(), mesVisible.getMonth() + 1, 0);
  const offsetFin = (ultimoDia.getDay() + 6) % 7;
  const diasTotales = offset + ultimoDia.getDate() + (6 - offsetFin);
  const semanas = Math.ceil(diasTotales / 7);

  return Array.from({ length: semanas * 7 }, (_, i) => {
    const dia = new Date(inicio);
    dia.setDate(dia.getDate() + i);
    return dia;
  });
}

/** Los 7 días (lunes a domingo) de la semana que contiene `diaEnLaSemana`. */
function diasDeLaSemana(diaEnLaSemana: Date): Date[] {
  const offset = (diaEnLaSemana.getDay() + 6) % 7;
  const lunes = new Date(diaEnLaSemana);
  lunes.setDate(lunes.getDate() - offset);
  return Array.from({ length: 7 }, (_, i) => {
    const dia = new Date(lunes);
    dia.setDate(dia.getDate() + i);
    return dia;
  });
}

/** Número de semana ISO-8601 (lunes a domingo, semana 1 = la del primer jueves del año). */
function numeroDeSemana(fecha: Date): number {
  // Se mueve al jueves de su propia semana ISO: ese jueves siempre cae en el
  // año al que pertenece la semana (a diferencia del 1 de enero, que puede
  // quedar en la última semana del año anterior).
  const dia = new Date(Date.UTC(fecha.getFullYear(), fecha.getMonth(), fecha.getDate()));
  dia.setUTCDate(dia.getUTCDate() - ((dia.getUTCDay() + 6) % 7) + 3);
  const juevesDeReferencia = dia.getTime();

  // Primer jueves del año de esa fecha: ahí empieza la semana 1.
  const primerDiaDelAnio = new Date(Date.UTC(dia.getUTCFullYear(), 0, 1));
  const primerJueves = new Date(primerDiaDelAnio);
  primerJueves.setUTCDate(1 + ((4 - primerDiaDelAnio.getUTCDay() + 7) % 7));

  return 1 + Math.round((juevesDeReferencia - primerJueves.getTime()) / (7 * 86_400_000));
}

export function CalendarioOrdenes(): JSX.Element {
  const { puede } = useSesion();
  const navegar = useNavigate();
  const cliente = useQueryClient();
  const esMovil = useEsMovil();
  useEffect(() => {
    guardarVistaOrdenesPreferida('calendario');
  }, []);
  const [mesVisible, setMesVisible] = useState(() => {
    const hoy = new Date();
    return new Date(hoy.getFullYear(), hoy.getMonth(), 1);
  });
  const [semanaVisible, setSemanaVisible] = useState(() => new Date());
  // En escritorio la vista mes/semana se elige a mano (§ pedido: alternar
  // como en la referencia); en celular sigue siendo automática por ahora.
  const [vistaEscritorio, setVistaEscritorio] = useState<'mes' | 'semana'>('mes');
  const semanal = esMovil || vistaEscritorio === 'semana';
  const [diaSeleccionado, setDiaSeleccionado] = useState<string | null>(null);
  // Tira de días en móvil (§ pedido): por defecto el día de hoy si cae en la
  // semana visible; si no (se navegó a otra semana), el lunes de esa semana.
  const [diaMovilSeleccionado, setDiaMovilSeleccionado] = useState(() => claveDia(new Date()));
  // Vista previa al pasar el cursor sobre un evento (§ pedido): se posiciona
  // en coordenadas de viewport y se pinta fuera de la cuadrícula para que el
  // `overflow: hidden` de las celdas no la recorte.
  const [vistaPrevia, setVistaPrevia] = useState<{ orden: OrdenDeCalendario; x: number; y: number } | null>(null);
  const [arrastrando, setArrastrando] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const f = useFiltrosPlanificacion('calendario', false);
  const { buscar, setBuscar } = f;

  // En celular la cuadrícula mensual no entra: se ve una semana con el
  // detalle de cada día en vez de eventos comprimidos en una celda pequeña.
  // En escritorio es el toggle Mes/Semana el que decide.
  const dias = semanal ? diasDeLaSemana(semanaVisible) : diasDeLaCuadricula(mesVisible);
  const inicioRango = dias[0]!;
  const finRango = new Date(dias[dias.length - 1]!);
  finRango.setDate(finRango.getDate() + 1);

  const { data, isLoading } = useQuery({
    queryKey: [
      'ordenes',
      'calendario',
      claveDia(inicioRango),
      claveDia(finRango),
      f.parametros,
    ],
    queryFn: () =>
      api.get<Pagina<OrdenDeCalendario>>(
        `/ordenes-trabajo${consulta({
          fechaDesde: inicioRango.toISOString(),
          fechaHasta: finRango.toISOString(),
          tamano: 100,
          orden: 'vencimiento',
          direccion: 'asc',
          ...f.parametros,
        })}`,
      ),
  });

  const mover = useMutation({
    mutationFn: ({ id, fechaVencimiento }: { id: string; fechaVencimiento: string }) =>
      api.post(`/ordenes-trabajo/${id}/reprogramar`, { fechaVencimiento }),
    onSuccess: async () => {
      setError(null);
      await cliente.invalidateQueries({ queryKey: ['ordenes', 'calendario'] });
    },
    onError: () => setError('No se pudo mover la orden a esa fecha.'),
  });

  const porDia = new Map<string, OrdenDeCalendario[]>();
  for (const orden of data?.datos ?? []) {
    const clave = fechaDeReferencia(orden);
    porDia.set(clave, [...(porDia.get(clave) ?? []), orden]);
  }

  const hoyClave = claveDia(new Date());
  const puedeEditar = puede('ot:editar');

  useEffect(() => {
    if (!semanal) return;
    const clavesDeLaSemana = dias.map(claveDia);
    setDiaMovilSeleccionado(clavesDeLaSemana.includes(hoyClave) ? hoyClave : clavesDeLaSemana[0]!);
    // Solo debe reaccionar a un cambio de semana, no a cada refetch de `dias`/`hoyClave`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [semanal, claveDia(dias[0]!)]);

  return (
    <>
      <CabeceraOrdenes vista="calendario" buscar={buscar} alCambiarBuscar={setBuscar} />

      {error ? <Aviso tono="error">{error}</Aviso> : null}

      <BloqueFiltrosPlanificacion f={f} prefijo="ot-calendario" />

      <div className="panel">
        <div className="calendario-cabecera">
          <div className="calendario-cabecera-izquierda">
            {!esMovil ? (
              <div className="calendario-toggle-vista" role="group" aria-label="Vista del calendario">
                <button
                  type="button"
                  className={vistaEscritorio === 'mes' ? 'activo' : ''}
                  onClick={() => setVistaEscritorio('mes')}
                >
                  Mes
                </button>
                <button
                  type="button"
                  className={vistaEscritorio === 'semana' ? 'activo' : ''}
                  onClick={() => setVistaEscritorio('semana')}
                >
                  Semana
                </button>
              </div>
            ) : null}
          </div>

          {semanal ? (
            <div className="calendario-nav">
              <button
                type="button"
                className="calendario-nav-boton"
                onClick={() => setSemanaVisible((d) => { const n = new Date(d); n.setDate(n.getDate() - 7); return n; })}
                aria-label="Semana anterior"
              >
                ‹
              </button>
              <h2 className="calendario-titulo">
                {esMovil ? (
                  <>
                    {dias[0]!.toLocaleDateString('es-PE', { day: 'numeric', month: 'short' })}
                    {' – '}
                    {dias[6]!.toLocaleDateString('es-PE', { day: 'numeric', month: 'short' })}
                  </>
                ) : (
                  <span style={{ textTransform: 'capitalize' }}>
                    {dias[0]!.toLocaleDateString('es-PE', { month: 'long', year: 'numeric' })}
                    {` · Semana ${numeroDeSemana(dias[0]!)}`}
                  </span>
                )}
              </h2>
              <button
                type="button"
                className="calendario-nav-boton"
                onClick={() => setSemanaVisible((d) => { const n = new Date(d); n.setDate(n.getDate() + 7); return n; })}
                aria-label="Semana siguiente"
              >
                ›
              </button>
            </div>
          ) : (
            <div className="calendario-nav">
              <button
                type="button"
                className="calendario-nav-boton"
                onClick={() => setMesVisible((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
                aria-label="Mes anterior"
              >
                ‹
              </button>
              <h2 className="calendario-titulo">
                {mesVisible.toLocaleDateString('es-PE', { month: 'long', year: 'numeric' })}
              </h2>
              <button
                type="button"
                className="calendario-nav-boton"
                onClick={() => setMesVisible((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
                aria-label="Mes siguiente"
              >
                ›
              </button>
            </div>
          )}

          <div className="calendario-cabecera-derecha">
            {isLoading ? <Cargando /> : null}
            <button
              type="button"
              className="boton secundario pequeno"
              onClick={() =>
                semanal ? setSemanaVisible(new Date()) : setMesVisible(() => {
                  const hoy = new Date();
                  return new Date(hoy.getFullYear(), hoy.getMonth(), 1);
                })
              }
            >
              Hoy
            </button>
          </div>
        </div>

        {esMovil ? (
          <>
            {/* Tira de los 7 días de la semana: el punto bajo la fecha distingue
                a simple vista qué días tienen OT de los que no, sin tener que
                abrir cada uno. Por defecto se selecciona hoy (o el lunes de la
                semana, si se navegó a otra). */}
            <div className="calendario-dias-tira" role="tablist" aria-label="Días de la semana">
              {dias.map((dia) => {
                const clave = claveDia(dia);
                const ordenesDelDia = porDia.get(clave) ?? [];
                const vencidasDelDia = ordenesDelDia.filter((o) => o.vencida).length;

                return (
                  <button
                    key={clave}
                    type="button"
                    role="tab"
                    aria-selected={clave === diaMovilSeleccionado}
                    className={`calendario-dia-tab${clave === diaMovilSeleccionado ? ' activo' : ''}${clave === hoyClave ? ' calendario-hoy' : ''}`}
                    onClick={() => setDiaMovilSeleccionado(clave)}
                  >
                    <span className="calendario-dia-tab-nombre">
                      {dia.toLocaleDateString('es-PE', { weekday: 'short' }).replace('.', '')}
                    </span>
                    <span className="calendario-dia-tab-numero">{dia.getDate()}</span>
                    <span
                      className={`calendario-dia-tab-punto${ordenesDelDia.length > 0 ? ' con-ot' : ''}${vencidasDelDia > 0 ? ' con-vencida' : ''}`}
                      aria-hidden="true"
                    />
                  </button>
                );
              })}
            </div>

            {(() => {
              const diaElegido = dias.find((d) => claveDia(d) === diaMovilSeleccionado) ?? dias[0]!;
              const ordenesDelDia = porDia.get(diaMovilSeleccionado) ?? [];
              const vencidasDelDia = ordenesDelDia.filter((o) => o.vencida).length;

              return (
                <div className={`calendario-dia-agenda${diaMovilSeleccionado === hoyClave ? ' calendario-hoy' : ''}`}>
                  <div className="calendario-dia-agenda-cabecera">
                    <span style={{ textTransform: 'capitalize' }}>
                      {diaElegido.toLocaleDateString('es-PE', { weekday: 'long', day: 'numeric', month: 'short' })}
                    </span>
                    {vencidasDelDia > 0 ? (
                      <span className="calendario-dia-agenda-notif" title={`${vencidasDelDia} vencida(s)`}>
                        {vencidasDelDia}
                      </span>
                    ) : null}
                  </div>
                  {ordenesDelDia.length === 0 ? (
                    <p className="campo-nota" style={{ margin: 0 }}>Sin órdenes este día.</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {ordenesDelDia.map((o) => (
                        <EnlaceRuta
                          key={o.id}
                          to={`/ordenes/${o.id}`}
                          state={{ volverA: '/ordenes/calendario' }}
                          className="fila-arbol"
                          style={{ padding: '8px 6px' }}
                        >
                          <span style={{ flex: 1 }}>
                            #{o.numero} {o.titulo}
                          </span>
                          <EstadoOtEtiqueta estado={o.estado} vencida={o.vencida} />
                        </EnlaceRuta>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </>
        ) : (
        <div className="calendario-scroll">
        <div className={`calendario-cuadricula${semanal ? ' calendario-cuadricula-semana' : ''}`}>
          {DIAS_SEMANA.map((d) => (
            <div key={d} className="calendario-encabezado-dia">
              {d}
            </div>
          ))}

          {(() => {
            // La vista semanal tiene una sola fila de 7 celdas altas: entran
            // muchos más eventos antes de tener que recurrir a "+N más" que
            // en la cuadrícula mensual, con hasta 6 filas apretadas.
            const tope = semanal ? 10 : 3;
            return dias.map((dia) => {
              const clave = claveDia(dia);
              const ordenesDelDia = porDia.get(clave) ?? [];
              const fueraDeMes = !semanal && dia.getMonth() !== mesVisible.getMonth();

              return (
                <div
                  key={clave}
                  className={`calendario-celda${fueraDeMes ? ' calendario-fuera-mes' : ''}${clave === hoyClave ? ' calendario-hoy' : ''}${arrastrando && arrastrando !== clave ? ' calendario-celda-objetivo' : ''}`}
                  onClick={() => setDiaSeleccionado(clave)}
                  onDragOver={(e) => {
                    if (puedeEditar) e.preventDefault();
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    setArrastrando(null);
                    const id = e.dataTransfer.getData('text/plain');
                    if (id) mover.mutate({ id, fechaVencimiento: clave });
                  }}
                >
                  <div className="calendario-numero-dia">{dia.getDate()}</div>
                  <div className="calendario-eventos">
                    {ordenesDelDia.slice(0, tope).map((o) => {
                      const visual = estadoVisualOt(o.estado, o.vencida);
                      const IconoEstado = ICONO_ESTADO_VISUAL_OT[visual];
                      return (
                        <div
                          key={o.id}
                          role="button"
                          tabIndex={0}
                          className={`calendario-evento calendario-evento-${visual.toLowerCase()}`}
                          draggable={puedeEditar}
                          onClick={(e) => {
                            e.stopPropagation();
                            navegar(`/ordenes/${o.id}`, { state: { volverA: '/ordenes/calendario' } });
                          }}
                          onDragStart={(e) => {
                            e.stopPropagation();
                            e.dataTransfer.effectAllowed = 'move';
                            e.dataTransfer.setData('text/plain', o.id);
                            setArrastrando(clave);
                          }}
                          onDragEnd={() => setArrastrando(null)}
                          onMouseEnter={(e) => {
                            const r = e.currentTarget.getBoundingClientRect();
                            setVistaPrevia({ orden: o, x: r.left, y: r.bottom + 6 });
                          }}
                          onMouseLeave={() => setVistaPrevia((v) => (v?.orden.id === o.id ? null : v))}
                        >
                          <IconoEstado />
                          <span className="calendario-evento-texto">
                            #{o.numero} {o.titulo}
                          </span>
                          {o.prioridad !== 'NINGUNA' ? (
                            <span className={`calendario-evento-prioridad calendario-evento-prioridad-${o.prioridad.toLowerCase()}`}>
                              <IconoPrioridad prioridad={o.prioridad} />
                            </span>
                          ) : null}
                        </div>
                      );
                    })}
                    {ordenesDelDia.length > tope ? (
                      <button
                        type="button"
                        className="calendario-mas"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDiaSeleccionado(clave);
                        }}
                      >
                        +{ordenesDelDia.length - tope} más
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            });
          })()}
        </div>
        </div>
        )}
      </div>

      {diaSeleccionado ? (
        <Dialogo
          titulo={new Date(`${diaSeleccionado}T00:00:00`).toLocaleDateString('es-PE', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
          alCerrar={() => setDiaSeleccionado(null)}
        >
          {(porDia.get(diaSeleccionado) ?? []).length === 0 ? (
            <Vacio titulo="Sin órdenes con vencimiento este día" />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(porDia.get(diaSeleccionado) ?? []).map((o) => (
                <EnlaceRuta
                  key={o.id}
                  to={`/ordenes/${o.id}`}
                  state={{ volverA: '/ordenes/calendario' }}
                  className="fila-arbol"
                  style={{ padding: '8px 6px' }}
                >
                  <span style={{ flex: 1 }}>
                    #{o.numero} {o.titulo}
                  </span>
                  <EstadoOtEtiqueta estado={o.estado} />
                </EnlaceRuta>
              ))}
            </div>
          )}
        </Dialogo>
      ) : null}

      {vistaPrevia ? <VistaPreviaOrdenCalendario orden={vistaPrevia.orden} x={vistaPrevia.x} y={vistaPrevia.y} /> : null}
    </>
  );
}

/**
 * Ficha corta al pasar el cursor sobre un evento del calendario (§ pedido):
 * lo justo para decidir si vale la pena abrir la OT completa, sin salir de
 * la vista de calendario. Posicionada en coordenadas fijas de viewport
 * (calculadas al entrar el cursor) para no quedar recortada por el
 * `overflow: hidden` de la cuadrícula.
 */
function VistaPreviaOrdenCalendario({ orden, x, y }: { orden: OrdenDeCalendario; x: number; y: number }): JSX.Element {
  const izquierda = Math.min(x, window.innerWidth - 300);
  return (
    <div className="calendario-vista-previa" style={{ left: izquierda, top: y }}>
      <div className="calendario-vista-previa-cabecera">
        <strong>
          #{orden.numero} {orden.titulo}
        </strong>
        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
          <EstadoOtEtiqueta estado={orden.estado} vencida={orden.vencida} />
          {orden.prioridad !== 'NINGUNA' ? <PrioridadEtiqueta prioridad={orden.prioridad} /> : null}
        </div>
        <OrigenOT orden={orden} />
      </div>
      <div className="calendario-vista-previa-cuerpo">
        <DatoVistaPrevia
          titulo="Fecha de vencimiento"
          valor={orden.fechaVencimiento ? formatearFecha(orden.fechaVencimiento) : '—'}
          tono={orden.vencida ? 'roja' : undefined}
        />
        <DatoVistaPrevia titulo="Tiempo estimado" valor={orden.tiempoEstimadoMinutos ? formatearDuracion(orden.tiempoEstimadoMinutos) : '—'} />
        <DatoVistaPrevia titulo="Tipo de trabajo" valor={ETIQUETA_TIPO_TRABAJO[orden.tipoTrabajo]} />
        <DatoVistaPrevia
          titulo="Asignado a"
          valor={orden.asignados.length > 0 ? <AsignadosConNombre asignados={orden.asignados} /> : '—'}
        />
        <DatoVistaPrevia titulo="Ubicación" valor={orden.ubicacion ?? '—'} />
        <DatoVistaPrevia titulo="Activo" valor={orden.activo ?? '—'} />
      </div>
    </div>
  );
}

function DatoVistaPrevia({ titulo, valor, tono }: { titulo: string; valor: ReactNode; tono?: 'roja' }): JSX.Element {
  return (
    <div className="calendario-vista-previa-dato">
      <span>{titulo}</span>
      <span style={tono === 'roja' ? { color: 'var(--falla)', fontWeight: 700 } : undefined}>{valor}</span>
    </div>
  );
}

// ─── Vista Gantt (§ pedido) ──────────────────────────────────────────────────

/**
 * Próxima fecha a partir de una fecha ancla, según la recurrencia. Copia
 * deliberada de `apps/api/.../recurrencia.util.ts` (paquetes distintos, no se
 * puede importar de uno a otro): acá es puramente para PROYECTAR en el Gantt
 * las próximas ocurrencias de una plantilla recurrente, nunca para decidir ni
 * guardar nada — si el cálculo del servidor cambia, esta copia visual puede
 * quedar desactualizada sin que rompa ninguna OT real.
 */
function proyectarSiguienteFecha(fecha: Date, recurrencia: Recurrencia): Date {
  switch (recurrencia) {
    case 'DIARIO': {
      const d = new Date(fecha);
      d.setDate(d.getDate() + 1);
      return d;
    }
    case 'SEMANAL': {
      const d = new Date(fecha);
      d.setDate(d.getDate() + 7);
      return d;
    }
    case 'MENSUAL_POR_FECHA': {
      const dia = fecha.getDate();
      const d = new Date(fecha);
      d.setDate(1);
      d.setMonth(d.getMonth() + 1);
      const ultimoDia = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
      d.setDate(Math.min(dia, ultimoDia));
      return d;
    }
    case 'MENSUAL_POR_DIA_SEMANA': {
      const diaSemana = fecha.getDay();
      const ocurrencia = Math.floor((fecha.getDate() - 1) / 7) + 1;
      const d = new Date(fecha.getFullYear(), fecha.getMonth() + 1, 1);
      let encontrados = 0;
      let ultimaCoincidencia = new Date(d);
      while (true) {
        if (d.getDay() === diaSemana) {
          encontrados += 1;
          ultimaCoincidencia = new Date(d);
          if (encontrados === ocurrencia) break;
        }
        const siguiente = new Date(d);
        siguiente.setDate(d.getDate() + 1);
        if (siguiente.getMonth() !== d.getMonth()) break;
        d.setTime(siguiente.getTime());
      }
      return encontrados === ocurrencia ? d : ultimaCoincidencia;
    }
    case 'ANUAL': {
      const d = new Date(fecha);
      d.setFullYear(d.getFullYear() + 1);
      return d;
    }
    default:
      return fecha;
  }
}

interface OrdenDeGantt {
  id: string;
  numero: number;
  titulo: string;
  estado: EstadoOT;
  vencida: boolean;
  prioridad: Prioridad;
  tipoTrabajo: TipoTrabajo;
  ubicacion: string | null;
  activo: string | null;
  asignados: { id: string; nombre: string; tipo: 'USUARIO' | 'GRUPO' }[];
  tiempoEstimadoMinutos: number | null;
  fechaInicio: string | null;
  fechaVencimiento: string | null;
  creadoEl: string;
  recurrencia: Recurrencia;
  recurrenciaActiva: boolean;
  esPlantillaRecurrente: boolean;
  ordenTrabajoPadreId: string | null;
}

interface BarraGantt {
  key: string;
  orden: OrdenDeGantt;
  diaInicio: number;
  diaFin: number;
  /** Próxima ocurrencia calculada de una plantilla recurrente: no es una OT
   * real todavía, así que no se puede abrir ni participa en los cruces. */
  proyectada: boolean;
  /** Solo en filas proyectadas: todas las ocurrencias de ESA plantilla dentro
   * del mes visible, agrupadas en una sola fila (§ bug reportado: una
   * recurrencia diaria generaba una fila completa por cada día del mes). */
  diasProyectados?: number[];
}

/** Fila de Gantt ya resuelta en representante de familia + hijas expandidas. */
interface FilaGantt extends BarraGantt {
  /** Sub-orden mostrada porque su familia está expandida (indentada, sin su propio botón de expandir). */
  esHija?: boolean;
  /** Solo en la fila representante de una familia con más de un miembro: su id de grupo. */
  grupoId?: string;
  /** Solo en la fila representante: cuántas hijas tiene (para el contador del botón expandir). */
  cantidadHijas?: number;
}

type PeriodoGantt = 'dia' | 'semana' | 'mes' | 'trimestre';

const PERIODOS_GANTT: { id: PeriodoGantt; nombre: string }[] = [
  { id: 'dia', nombre: 'Día' },
  { id: 'semana', nombre: 'Semana' },
  { id: 'mes', nombre: 'Mes' },
  { id: 'trimestre', nombre: 'Trimestre' },
];

const MS_POR_DIA = 86_400_000;

/** Rango visible [inicio, fin) del Gantt según el período y la fecha de referencia. */
function rangoGantt(periodo: PeriodoGantt, ancla: Date): { inicio: Date; fin: Date; nDias: number } {
  const y = ancla.getFullYear();
  const m = ancla.getMonth();
  const d = ancla.getDate();
  let inicio: Date;
  let fin: Date;
  if (periodo === 'dia') {
    inicio = new Date(y, m, d);
    fin = new Date(y, m, d + 1);
  } else if (periodo === 'semana') {
    const lunes = d - ((ancla.getDay() + 6) % 7);
    inicio = new Date(y, m, lunes);
    fin = new Date(y, m, lunes + 7);
  } else if (periodo === 'mes') {
    inicio = new Date(y, m, 1);
    fin = new Date(y, m + 1, 1);
  } else {
    const primerMes = Math.floor(m / 3) * 3;
    inicio = new Date(y, primerMes, 1);
    fin = new Date(y, primerMes + 3, 1);
  }
  return { inicio, fin, nDias: Math.round((fin.getTime() - inicio.getTime()) / MS_POR_DIA) };
}

function desplazarAncla(periodo: PeriodoGantt, ancla: Date, sentido: 1 | -1): Date {
  const y = ancla.getFullYear();
  const m = ancla.getMonth();
  const d = ancla.getDate();
  if (periodo === 'dia') return new Date(y, m, d + sentido);
  if (periodo === 'semana') return new Date(y, m, d + 7 * sentido);
  if (periodo === 'mes') return new Date(y, m + sentido, 1);
  return new Date(y, m + 3 * sentido, 1);
}

function tituloPeriodoGantt(periodo: PeriodoGantt, inicio: Date, fin: Date): string {
  const ultimo = new Date(fin.getFullYear(), fin.getMonth(), fin.getDate() - 1);
  const corto = (f: Date): string => f.toLocaleDateString('es-PE', { day: 'numeric', month: 'short' }).replace('.', '');
  if (periodo === 'dia') {
    return inicio.toLocaleDateString('es-PE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  }
  if (periodo === 'semana') return `${corto(inicio)} – ${corto(ultimo)} ${ultimo.getFullYear()}`;
  if (periodo === 'mes') return inicio.toLocaleDateString('es-PE', { month: 'long', year: 'numeric' });
  const mesCorto = (f: Date): string => f.toLocaleDateString('es-PE', { month: 'short' }).replace('.', '');
  return `T${Math.floor(inicio.getMonth() / 3) + 1} ${inicio.getFullYear()} · ${mesCorto(inicio)} – ${mesCorto(ultimo)}`;
}

export function GanttOrdenes(): JSX.Element {
  const navegar = useNavigate();
  const cliente = useQueryClient();
  const { puede } = useSesion();
  // Arrastrar para reprogramar y las asas de redimensionar dependen de drag
  // HTML5 / `onMouseDown`, que no responden a gestos táctiles (§ bug
  // reportado): en celular el Gantt queda de solo lectura — se abre el
  // detalle con un toque y se reprograma desde ahí, en vez de ofrecer un
  // arrastre que en los hechos no hace nada.
  const esMovil = useEsMovil();
  useEffect(() => {
    guardarVistaOrdenesPreferida('gantt');
  }, []);

  // Período (día/semana/mes/trimestre) + fecha de referencia: el rango visible
  // se deriva de ambos.
  const [periodo, setPeriodo] = useState<PeriodoGantt>('mes');
  const [ancla, setAncla] = useState(() => new Date());
  // «Solo recurrentes» arranca marcado (§ pedido): el Gantt sirve para
  // planificar las OT que se repiten; se desmarca para ver todas. La última
  // elección se recuerda en el navegador.
  const f = useFiltrosPlanificacion('gantt', obtenerGanttSoloRecurrentes());
  useEffect(() => {
    guardarGanttSoloRecurrentes(f.soloRecurrentes);
  }, [f.soloRecurrentes]);
  const { buscar, setBuscar } = f;
  // Ficha al pasar el cursor (§ pedido): mismo patrón que el calendario —
  // coordenadas de viewport capturadas al entrar, para no quedar recortada
  // por el `overflow-x: auto` del contenedor.
  const [vistaPrevia, setVistaPrevia] = useState<{ orden: OrdenDeGantt; x: number; y: number; yArriba: number } | null>(null);
  // Click en el título abre un modal de solo lectura en vez de navegar
  // (§ pedido): se queda en el Gantt para seguir comparando fechas.
  const [detalleAbiertoId, setDetalleAbiertoId] = useState<string | null>(null);
  const [arrastrando, setArrastrando] = useState<{ id: string; fechaInicio: string | null } | null>(null);
  const [errorArrastre, setErrorArrastre] = useState<string | null>(null);
  // Redimensión en curso con las asas: la barra se estira en vivo hasta `dia`.
  const [redimension, setRedimension] = useState<{ id: string; lado: 'inicio' | 'fin'; dia: number } | null>(null);
  const puedeEditar = puede('ot:editar');
  // Panel de conflictos y resaltado momentáneo (§ pedido de rediseño): el
  // aviso de cruces ya no es solo texto, abre un detalle de qué se cruza con
  // qué y permite resaltar el par en el propio Gantt.
  const [panelConflictosAbierto, setPanelConflictosAbierto] = useState(false);
  const [resaltadas, setResaltadas] = useState<Set<string>>(new Set());
  function resaltarUnMomento(ids: string[]): void {
    setResaltadas(new Set(ids));
    window.setTimeout(() => setResaltadas(new Set()), 2200);
  }

  const reprogramar = useMutation({
    mutationFn: ({ id, fechaVencimiento }: { id: string; fechaVencimiento: string }) =>
      api.post(`/ordenes-trabajo/${id}/reprogramar`, { fechaVencimiento }),
    onSuccess: async () => {
      setErrorArrastre(null);
      await cliente.invalidateQueries({ queryKey: ['ordenes', 'gantt'] });
    },
    onError: () => setErrorArrastre('No se pudo mover la orden a esa fecha.'),
  });
  // Asas en los bordes de la barra (§ pedido): izquierda ajusta el inicio,
  // derecha el vencimiento — mismo endpoint de reprogramar para el vencimiento
  // y uno análogo nuevo para el inicio, cada uno moviendo un solo extremo.
  const reprogramarInicio = useMutation({
    mutationFn: ({ id, fechaInicio }: { id: string; fechaInicio: string }) =>
      api.post(`/ordenes-trabajo/${id}/reprogramar-inicio`, { fechaInicio }),
    onSuccess: async () => {
      setErrorArrastre(null);
      await cliente.invalidateQueries({ queryKey: ['ordenes', 'gantt'] });
    },
    onError: () => setErrorArrastre('No se pudo mover el inicio a esa fecha.'),
  });
  const [soloCruces, setSoloCruces] = useState(false);
  // Sub-órdenes colapsadas bajo su OT padre (§ pedido): una familia con varias
  // hijas no ocupa una fila por cada una, solo la del padre — expandirla
  // revela las hijas debajo, indentadas. Son ids de grupo (padre), no de OT.
  const [familiasExpandidas, setFamiliasExpandidas] = useState<Set<string>>(new Set());

  const { inicio: inicioRango, fin: finRango, nDias } = rangoGantt(periodo, ancla);

  /** Posición (1..nDias) de una fecha ISO dentro del rango; puede salirse de él. */
  function indiceDeIso(iso: string): number {
    const [anio, mes, dia] = iso.slice(0, 10).split('-').map(Number);
    return Math.round((new Date(anio!, mes! - 1, dia!).getTime() - inicioRango.getTime()) / MS_POR_DIA) + 1;
  }
  function fechaDeIndice(indice: number): Date {
    return new Date(inicioRango.getFullYear(), inicioRango.getMonth(), inicioRango.getDate() + indice - 1);
  }

  const { data, isLoading, isError, error } = useQuery({
    queryKey: [
      'ordenes',
      'gantt',
      claveDia(inicioRango),
      claveDia(finRango),
      f.parametros,
    ],
    queryFn: () =>
      api.get<Pagina<OrdenDeGantt>>(
        `/ordenes-trabajo${consulta({
          fechaDesde: inicioRango.toISOString(),
          fechaHasta: finRango.toISOString(),
          // `tamano` solo acepta 25/50/75/100 (§ paginacionSchema) — un mes
          // con más de 100 OT necesitaría paginar el Gantt, pero por ahora
          // alcanza de sobra para no exceder el máximo y que la consulta
          // falle en silencio (§ bug reportado).
          tamano: 100,
          orden: 'vencimiento',
          direccion: 'asc',
          ...f.parametros,
        })}`,
      ),
  });

  // Plantillas recurrentes activas: se piden aparte (sin filtro de fecha,
  // nunca caen dentro del rango de vencimiento real) para proyectar sus
  // próximas ocurrencias dentro del mes visible.
  const { data: plantillas } = useQuery({
    queryKey: ['ordenes', 'gantt-plantillas'],
    queryFn: () =>
      api.get<Pagina<OrdenDeGantt>>(
        `/ordenes-trabajo${consulta({ esPlantillaRecurrente: 'true', tamano: 100 })}`,
      ),
  });

  const barras: BarraGantt[] = [];

  for (const orden of data?.datos ?? []) {
    // La plantilla recurrente se pide aparte y se proyecta más abajo; acá se
    // descarta para no mostrarla también como una OT real suelta en su fecha
    // ancla (`esPlantillaRecurrente: 'false'` NO sirve para esto: Zod
    // convierte a `true` cualquier string no vacío, incluido "false").
    if (orden.esPlantillaRecurrente) continue;
    // Sin fecha de vencimiento, se ancla por creación — mismo criterio que el
    // calendario (`fechaDeReferencia`) y que el backend (`COALESCE(fecha_
    // vencimiento, creado_el)`): antes se descartaba directo y esas OT nunca
    // aparecían en el Gantt (§ bug reportado).
    const finIso = orden.fechaVencimiento ?? orden.creadoEl.slice(0, 10);
    const inicioIso = orden.fechaInicio ?? finIso;
    const diaFin = Math.min(nDias, Math.max(1, indiceDeIso(finIso)));
    const inicioEnRango = Math.max(1, indiceDeIso(inicioIso));
    barras.push({ key: orden.id, orden, diaInicio: Math.min(inicioEnRango, diaFin), diaFin, proyectada: false });
  }

  for (const plantilla of plantillas?.datos ?? []) {
    if (!plantilla.recurrenciaActiva || !plantilla.fechaVencimiento) continue;
    // El valor ya llega como ISO completo (con hora) — agregarle otra hora
    // encima (como antes) armaba una fecha inválida y el bucle de abajo
    // nunca avanzaba ni terminaba (§ bug reportado: "#14" repetido sin fin).
    let anclaje = new Date(plantilla.fechaVencimiento);
    const dias: number[] = [];
    // Tope de 120 iteraciones: cubre un trimestre completo de recurrencia
    // diaria sin arriesgar un bucle largo.
    for (let i = 0; i < 120; i++) {
      anclaje = proyectarSiguienteFecha(anclaje, plantilla.recurrencia);
      if (anclaje >= finRango) break;
      if (anclaje < inicioRango) continue;
      dias.push(indiceDeIso(claveDia(anclaje)));
    }
    if (dias.length === 0) continue;
    // Todas las ocurrencias de la plantilla van en UNA sola fila (§ bug
    // reportado): una recurrencia diaria llenaba la lista con una fila por
    // cada día del mes.
    barras.push({
      key: plantilla.id,
      orden: plantilla,
      diaInicio: dias[0]!,
      diaFin: dias[dias.length - 1]!,
      proyectada: true,
      diasProyectados: dias,
    });
  }

  // Orden por creación (el número de OT crece con cada alta), no por fecha: así
  // reprogramar una barra la desliza en su misma fila en vez de saltar a otra.
  barras.sort((a, b) => a.orden.numero - b.orden.numero);

  // Cruces (§ pedido, refinado tras reporte de ruido): dos OT reales con rango
  // de fechas superpuesto Y que comparten al menos un asignado (usuario o
  // grupo) — la misma persona o equipo no puede estar en dos OT a la vez. Un
  // cruce por fechas puro, sin mirar asignados, marcaba como "conflicto" OT de
  // personas/equipos totalmente distintos que simplemente caían en el mismo
  // rango del mes, inundando el panel de falsos positivos. Las proyectadas
  // (plantillas) no participan: todavía no existen.
  const idsEnCruce = new Set<string>();
  // Un par por cada cruce encontrado (§ rediseño: el panel de conflictos
  // necesita saber CON QUIÉN se cruza cada una, no solo marcarla).
  const paresCruzados: { a: BarraGantt; b: BarraGantt }[] = [];
  const reales = barras.filter((b) => !b.proyectada);
  // Grupo padre-hijas (§ bug reportado): una sub-orden hereda las fechas de
  // su padre a propósito, así que toda la familia aparece superpuesta en el
  // Gantt sin ser un cruce real — se excluyen los pares dentro del mismo grupo.
  const grupoDe = (o: OrdenDeGantt) => o.ordenTrabajoPadreId ?? o.id;
  const compartenAsignado = (a: OrdenDeGantt, b: OrdenDeGantt) =>
    a.asignados.some((x) => b.asignados.some((y) => x.tipo === y.tipo && x.id === y.id));
  for (let i = 0; i < reales.length; i++) {
    for (let j = i + 1; j < reales.length; j++) {
      const a = reales[i]!;
      const b = reales[j]!;
      if (grupoDe(a.orden) === grupoDe(b.orden)) continue;
      if (!compartenAsignado(a.orden, b.orden)) continue;
      if (a.diaInicio <= b.diaFin && b.diaInicio <= a.diaFin) {
        idsEnCruce.add(a.key);
        idsEnCruce.add(b.key);
        paresCruzados.push({ a, b });
      }
    }
  }

  // Colapsa cada familia padre/hijas en una sola fila representativa (la del
  // padre), con sus sub-órdenes ocultas hasta que se expanda (§ pedido): antes
  // cada hija ocupaba su propia fila suelta, lo que además hacía parecer que
  // había muchos más "conflictos" de los reales (una familia de 5 hijas
  // multiplicaba por 5 cualquier cruce real de la familia contra otra OT).
  const familias = new Map<string, BarraGantt[]>();
  for (const b of reales) {
    const id = grupoDe(b.orden);
    const lista = familias.get(id) ?? [];
    lista.push(b);
    familias.set(id, lista);
  }
  const grupoTieneCruce = (grupoId: string) => (familias.get(grupoId) ?? []).some((m) => idsEnCruce.has(m.key));

  const filas: FilaGantt[] = [];
  const gruposEmitidos = new Set<string>();
  for (const b of barras) {
    if (b.proyectada) {
      filas.push(b);
      continue;
    }
    const grupoId = grupoDe(b.orden);
    if (gruposEmitidos.has(grupoId)) continue;
    gruposEmitidos.add(grupoId);
    const miembros = familias.get(grupoId)!;
    if (miembros.length === 1) {
      filas.push(b);
      continue;
    }
    // El padre es el miembro cuyo id coincide con el del grupo; si no está
    // en el rango visible (una hija puede tener fecha propia distinta a la
    // del padre), se usa la primera hija encontrada como representante.
    const padre = miembros.find((m) => m.orden.id === grupoId) ?? miembros[0]!;
    const hijas = miembros.filter((m) => m !== padre);
    filas.push({ ...padre, grupoId, cantidadHijas: hijas.length });
    if (familiasExpandidas.has(grupoId)) {
      for (const hija of hijas) filas.push({ ...hija, esHija: true });
    }
  }

  const barrasVisibles = soloCruces
    ? filas.filter(
        (f) => !f.proyectada && (idsEnCruce.has(f.key) || (f.grupoId !== undefined && grupoTieneCruce(f.grupoId))),
      )
    : filas;

  const hoy = new Date();
  const indiceHoy = indiceDeIso(claveDia(hoy));
  const hoyEnRango = indiceHoy >= 1 && indiceHoy <= nDias;
  const diasDelMesArr = Array.from({ length: nDias }, (_, i) => i + 1);
  // Debe coincidir con `.gantt-columna-titulo` (260px en escritorio, 150px
  // angosta en celular por la media query de app.css).
  const anchoColumnaTitulo = esMovil ? 150 : 260;
  const NOMBRE_DIA = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  const segmentosMes: { clave: string; nombre: string; dias: number }[] = [];
  for (let i = 1; i <= nDias; i++) {
    const fecha = fechaDeIndice(i);
    const ultimo = segmentosMes[segmentosMes.length - 1];
    if (!ultimo || fecha.getDate() === 1) {
      segmentosMes.push({
        clave: claveDia(fecha),
        nombre: fecha.toLocaleDateString('es-PE', { month: 'long', year: 'numeric' }),
        dias: 1,
      });
    } else {
      ultimo.dias++;
    }
  }

  /**
   * Asas en los bordes de la barra (§ pedido): arrastrar el borde izquierdo
   * cambia solo la fecha de inicio, el derecho solo el vencimiento, cada uno
   * con la otra fecha fija de referencia para no invertir el rango. Usa
   * eventos de mouse normales (no el drag & drop nativo de mover la barra
   * entera) para no pelearse con ese otro gesto sobre el mismo elemento.
   */
  function iniciarRedimension(
    e: ReactMouseEvent<HTMLSpanElement>,
    orden: OrdenDeGantt,
    lado: 'inicio' | 'fin',
  ): void {
    e.preventDefault();
    e.stopPropagation();
    const pista = e.currentTarget.closest('.gantt-pista');
    if (!pista) return;

    // Mientras dura el gesto el cursor ya no está sobre el asa, así que se fija
    // en el body para que la flecha ↔ se mantenga hasta soltar.
    const cursorPrevio = document.body.style.cursor;
    const seleccionPrevia = document.body.style.userSelect;
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';

    // El asa izquierda es el borde del inicio del día (floor); la derecha, el
    // fin del día (ceil) — así el borde sigue al puntero como un acordeón.
    function diaBajo(clientX: number): number {
      const rect = pista!.getBoundingClientRect();
      const fraccion = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      const crudo = fraccion * nDias;
      const dia = lado === 'inicio' ? Math.floor(crudo) + 1 : Math.ceil(crudo);
      return Math.min(nDias, Math.max(1, dia));
    }

    function mover(ev: MouseEvent): void {
      const dia = diaBajo(ev.clientX);
      setRedimension((r) => (r && r.dia === dia ? r : { id: orden.id, lado, dia }));
    }

    setRedimension({ id: orden.id, lado, dia: diaBajo(e.clientX) });
    window.addEventListener('mousemove', mover);

    function soltar(ev: MouseEvent): void {
      window.removeEventListener('mouseup', soltar);
      window.removeEventListener('mousemove', mover);
      setRedimension(null);
      document.body.style.cursor = cursorPrevio;
      document.body.style.userSelect = seleccionPrevia;
      const dia = diaBajo(ev.clientX);
      const fecha = claveDia(fechaDeIndice(dia));

      if (lado === 'inicio') {
        if (orden.fechaVencimiento && fecha > orden.fechaVencimiento.slice(0, 10)) {
          setErrorArrastre('La fecha de inicio no puede quedar después del vencimiento.');
          return;
        }
        reprogramarInicio.mutate({ id: orden.id, fechaInicio: fecha });
      } else {
        if (orden.fechaInicio && fecha < orden.fechaInicio.slice(0, 10)) {
          setErrorArrastre('El vencimiento no puede quedar antes del inicio.');
          return;
        }
        // Sin `fechaInicio` propia, el borde izquierdo de la barra es solo un
        // reflejo visual del vencimiento (`fechaInicio ?? fechaVencimiento` en
        // el armado de barras) — no un valor guardado. Al estirar solo la
        // derecha, el próximo refresco recalculaba ese reflejo igual al nuevo
        // vencimiento y la barra volvía a colapsar a 1 día, como si también
        // se hubiera movido la izquierda (§ bug reportado). Se fija aparte al
        // valor efectivo que tenía antes de este arrastre — el mismo que ya
        // queda fijo si el usuario estira primero la izquierda.
        if (!orden.fechaInicio) {
          const inicioEfectivo = (orden.fechaVencimiento ?? orden.creadoEl).slice(0, 10);
          reprogramarInicio.mutate({ id: orden.id, fechaInicio: inicioEfectivo });
        }
        reprogramar.mutate({ id: orden.id, fechaVencimiento: fecha });
      }
    }

    window.addEventListener('mouseup', soltar);
  }

  return (
    <>
      <CabeceraOrdenes vista="gantt" buscar={buscar} alCambiarBuscar={setBuscar} />

      <BloqueFiltrosPlanificacion f={f} prefijo="gantt" />

      <div className="panel" style={{ marginTop: 12 }}>
        {/* Mismas clases que la cabecera del calendario (§ pedido): en
            celular la columna izquierda se oculta sola y la navegación
            queda en una sola fila limpia con "Hoy" a la derecha, en vez del
            "← Hoy →" apretado que tenía antes el Gantt. El selector de
            período (Día/Semana/Mes/Trimestre) va en su propia fila debajo,
            porque a diferencia del calendario acá sí hace falta elegirlo
            también en celular. */}
        <div className="panel-cabecera calendario-cabecera">
          <div className="calendario-cabecera-izquierda" />
          <div className="calendario-nav">
            <button
              type="button"
              className="calendario-nav-boton"
              aria-label="Período anterior"
              onClick={() => setAncla((a) => desplazarAncla(periodo, a, -1))}
            >
              ‹
            </button>
            <h2 className="calendario-titulo">{tituloPeriodoGantt(periodo, inicioRango, finRango)}</h2>
            <button
              type="button"
              className="calendario-nav-boton"
              aria-label="Período siguiente"
              onClick={() => setAncla((a) => desplazarAncla(periodo, a, 1))}
            >
              ›
            </button>
          </div>
          <div className="calendario-cabecera-derecha">
            <button type="button" className="boton secundario pequeno" onClick={() => setAncla(new Date())}>
              Hoy
            </button>
          </div>
        </div>

        {/* Conflictos en la misma fila que los períodos: siempre visible, con
            cuántos hay o un «sin conflictos» explícito. */}
        <div
          className="panel-cuerpo"
          style={{ paddingTop: 12, paddingBottom: 0, display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}
        >
          <div className="gantt-periodo-selector" role="group" aria-label="Período del Gantt">
            {PERIODOS_GANTT.map((p) => (
              <button
                key={p.id}
                type="button"
                className={periodo === p.id ? 'activo' : ''}
                onClick={() => setPeriodo(p.id)}
              >
                {p.nombre}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="gantt-alerta-conflictos"
            style={{ marginLeft: 'auto' }}
            onClick={() => setPanelConflictosAbierto(true)}
          >
            <IconoEstadoVencida />
            {idsEnCruce.size > 0
              ? `${idsEnCruce.size} conflicto${idsEnCruce.size === 1 ? '' : 's'} de planificación — ver detalle`
              : 'Sin conflictos de planificación'}
          </button>
          {idsEnCruce.size > 0 ? (
            <button type="button" className="boton-enlace" onClick={() => setSoloCruces((v) => !v)}>
              {soloCruces ? 'Ver todas las órdenes' : 'Mostrar solo conflictos'}
            </button>
          ) : null}
        </div>

        {isError ? (
          <div className="panel-cuerpo" style={{ paddingTop: 0, paddingBottom: 0 }}>
            <Aviso tono="error">{mensajeError(error, 'No se pudieron cargar las órdenes de este período.')}</Aviso>
          </div>
        ) : null}

        {/* `tamano` del fetch está fijo en 100 (ver más abajo): un período con
            más OT que eso se corta en silencio. Mientras no haya paginación
            real en el Gantt, al menos se avisa en vez de faltar OT sin
            explicación (§ bug reportado). */}
        {!isError && data && data.total > data.datos.length ? (
          <div className="panel-cuerpo" style={{ paddingTop: 0, paddingBottom: 0 }}>
            <Aviso tono="atencion">
              Este período tiene {data.total} OT y solo se muestran las primeras {data.datos.length}. Acorte el rango
              (p. ej. a semana o mes) para verlas todas.
            </Aviso>
          </div>
        ) : null}

        <div className="panel-cuerpo">
          {isLoading ? (
            <Cargando />
          ) : barrasVisibles.length === 0 ? (
            <Vacio titulo="Sin órdenes en este período" detalle="Ajusta los filtros o navega a otro período." />
          ) : (
            <>
              <div className="gantt-leyenda">
                <span className="gantt-leyenda-item">
                  <span className="gantt-leyenda-punto gantt-barra-azul" />
                  En curso
                </span>
                <span className="gantt-leyenda-item">
                  <span className="gantt-leyenda-punto gantt-barra-ambar" />
                  En espera
                </span>
                <span className="gantt-leyenda-item">
                  <span className="gantt-leyenda-punto gantt-barra-verde" />
                  Completa
                </span>
                <span className="gantt-leyenda-item">
                  <span className="gantt-leyenda-punto gantt-barra-roja" />
                  Vencida
                </span>
                <span className="gantt-leyenda-item">
                  <span className="gantt-leyenda-punto gantt-barra-proyectada" />
                  Próxima (plantilla)
                </span>
                <span className="gantt-leyenda-item">
                  <IconoEstadoVencida />
                  Conflicto
                </span>
              </div>

              <div className="gantt-contenedor">
                <div className="gantt-tabla">
                  {hoyEnRango ? (
                    <div
                      className="gantt-linea-hoy"
                      style={{
                        // El ancho de la columna de título cambia en celular
                        // (angostada por CSS, ver media query) — sin esto la
                        // línea de "hoy" quedaba calculada para los 260px de
                        // escritorio y aparecía corrida en esa vista.
                        left: `calc(${anchoColumnaTitulo}px + (100% - ${anchoColumnaTitulo}px) * ${(indiceHoy - 0.5) / nDias})`,
                      }}
                      aria-hidden="true"
                    />
                  ) : null}

                  <div className="gantt-fila gantt-fila-cabecera">
                    <div className="gantt-columna-titulo">Orden de trabajo</div>
                    <div className="gantt-pista">
                      {nDias > 45
                        ? // Trimestre: 90 días no caben con número cada uno, así que la
                          // cabecera agrupa por mes.
                          segmentosMes.map((s) => (
                            <div
                              key={s.clave}
                              className="gantt-dia gantt-dia-mes"
                              style={{ width: `${(s.dias / nDias) * 100}%` }}
                            >
                              <span className="gantt-dia-numero">{s.nombre}</span>
                            </div>
                          ))
                        : diasDelMesArr.map((dia) => {
                            const fechaDia = fechaDeIndice(dia);
                            const finde = fechaDia.getDay() === 0 || fechaDia.getDay() === 6;
                            return (
                              <div
                                key={dia}
                                className={`gantt-dia${dia === indiceHoy ? ' gantt-dia-hoy' : ''}${
                                  finde ? ' gantt-dia-finde' : ''
                                }${fechaDia.getDay() === 1 && dia !== 1 ? ' gantt-dia-nueva-semana' : ''}`}
                                style={{ width: `${100 / nDias}%` }}
                              >
                                <span className="gantt-dia-numero">{fechaDia.getDate()}</span>
                                <span className="gantt-dia-nombre">{NOMBRE_DIA[fechaDia.getDay()]}</span>
                              </div>
                            );
                          })}
                    </div>
                  </div>

                  {barrasVisibles.map((b, indice) => {
                    const tono = TONO_ESTADO_VISUAL_OT[estadoVisualOt(b.orden.estado, b.orden.vencida)];
                    const IconoTono = ICONO_ESTADO_VISUAL_OT[estadoVisualOt(b.orden.estado, b.orden.vencida)];
                    let diaIni = b.diaInicio;
                    let diaFinal = b.diaFin;
                    const redimEsta = redimension?.id === b.orden.id && !b.proyectada ? redimension : null;
                    if (redimEsta) {
                      if (redimEsta.lado === 'inicio') diaIni = Math.min(redimEsta.dia, diaFinal);
                      else diaFinal = Math.max(redimEsta.dia, diaIni);
                    }
                    const leftPct = ((diaIni - 1) / nDias) * 100;
                    const widthPct = Math.max(((diaFinal - diaIni + 1) / nDias) * 100, 100 / nDias);
                    // Colapsada, la fila del padre igual muestra el ícono de
                    // conflicto si CUALQUIER hija suya se cruza con otra OT —
                    // expandir no debería ser la única forma de enterarse.
                    const enCruce =
                      idsEnCruce.has(b.key) || (b.grupoId !== undefined && grupoTieneCruce(b.grupoId));
                    const puedeArrastrar = !b.proyectada && puedeEditar && !esMovil;
                    const expandida = b.grupoId !== undefined && familiasExpandidas.has(b.grupoId);
                    return (
                      <div
                        className={`gantt-fila${indice % 2 === 1 ? ' gantt-fila-par' : ''}${
                          resaltadas.has(b.key) ? ' gantt-fila-resaltada' : ''
                        }${b.esHija ? ' gantt-fila-hija' : ''}`}
                        key={b.key}
                      >
                        <div className="gantt-columna-titulo">
                          {b.proyectada ? (
                            <span className="gantt-titulo-proyectado" title={`${b.orden.titulo} — plantilla recurrente`}>
                              <span className="gantt-titulo-texto">
                                #{b.orden.numero} · {b.orden.titulo}
                              </span>
                              <em>
                                {b.diasProyectados!.length === 1
                                  ? 'próxima ocurrencia'
                                  : `próximas ${b.diasProyectados!.length} ocurrencias`}
                              </em>
                            </span>
                          ) : (
                            <div className="gantt-titulo-bloque" title={b.orden.titulo}>
                              <div className="gantt-titulo-fila">
                                {b.grupoId !== undefined ? (
                                  <button
                                    type="button"
                                    className="gantt-expandir-familia"
                                    aria-label={expandida ? 'Ocultar sub-órdenes' : 'Mostrar sub-órdenes'}
                                    aria-expanded={expandida}
                                    onClick={() =>
                                      setFamiliasExpandidas((actual) => {
                                        const copia = new Set(actual);
                                        if (copia.has(b.grupoId!)) copia.delete(b.grupoId!);
                                        else copia.add(b.grupoId!);
                                        return copia;
                                      })
                                    }
                                  >
                                    {expandida ? '▾' : '▸'}
                                  </button>
                                ) : null}
                                <button
                                  type="button"
                                  className="boton-enlace gantt-titulo-texto"
                                  onClick={() => setDetalleAbiertoId(b.orden.id)}
                                >
                                  #{b.orden.numero} · {b.orden.titulo}
                                </button>
                                {b.cantidadHijas ? (
                                  <span className="campo-nota">
                                    {b.cantidadHijas} sub-orden{b.cantidadHijas === 1 ? '' : 'es'}
                                  </span>
                                ) : null}
                              </div>
                              <div className="gantt-titulo-meta">
                                <EstadoOtEtiqueta estado={b.orden.estado} vencida={b.orden.vencida} />
                                {b.orden.prioridad !== 'NINGUNA' ? (
                                  <PrioridadEtiqueta prioridad={b.orden.prioridad} />
                                ) : null}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="gantt-pista">
                          {diasDelMesArr.map((dia) => {
                            const fechaDia = fechaDeIndice(dia);
                            const finde = fechaDia.getDay() === 0 || fechaDia.getDay() === 6;
                            return (
                              <div
                                key={dia}
                                className={`gantt-celda-fondo${finde ? ' gantt-dia-finde' : ''}${
                                  dia === indiceHoy ? ' gantt-dia-hoy' : ''
                                }${fechaDia.getDay() === 1 && dia !== 1 ? ' gantt-dia-nueva-semana' : ''}${
                                  arrastrando ? ' gantt-celda-destino' : ''
                                }`}
                                style={{ width: `${100 / nDias}%` }}
                                onDragOver={(e) => {
                                  if (!arrastrando) return;
                                  e.preventDefault();
                                  e.dataTransfer.dropEffect = 'move';
                                }}
                                onDrop={(e) => {
                                  if (!arrastrando) return;
                                  e.preventDefault();
                                  const id = e.dataTransfer.getData('text/plain') || arrastrando.id;
                                  // No se puede soltar antes del propio inicio de la OT: el
                                  // endpoint solo mueve el vencimiento, así que aceptar esto
                                  // dejaba un rango invertido (inicio > vencimiento) que
                                  // colapsaba la barra a un pedacito irreconocible
                                  // (§ bug reportado: varias OT "aplastadas" en un mismo día).
                                  if (arrastrando.fechaInicio && claveDia(fechaDia) < arrastrando.fechaInicio.slice(0, 10)) {
                                    setErrorArrastre('No se puede mover el vencimiento antes de la fecha de inicio.');
                                    setArrastrando(null);
                                    return;
                                  }
                                  reprogramar.mutate({ id, fechaVencimiento: claveDia(fechaDia) });
                                  setArrastrando(null);
                                }}
                              />
                            );
                          })}
                          {b.proyectada ? (
                            // Varios marcadores finos, uno por ocurrencia, en vez de una
                            // barra que abarque de la primera a la última fecha (eso
                            // sugería un rango continuo que no existe).
                            b.diasProyectados!.map((dia) => (
                              <div
                                key={dia}
                                className="gantt-barra gantt-barra-proyectada"
                                style={{
                                  left: `${((dia - 1) / nDias) * 100}%`,
                                  width: `${100 / nDias}%`,
                                  pointerEvents: arrastrando ? 'none' : undefined,
                                }}
                                onMouseEnter={(e) => {
                                  const r = e.currentTarget.getBoundingClientRect();
                                  setVistaPrevia({
                                    orden: { ...b.orden, fechaVencimiento: claveDia(fechaDeIndice(dia)) },
                                    x: r.left,
                                    y: r.bottom + 6,
                                    yArriba: r.top - 6,
                                  });
                                }}
                                onMouseLeave={() =>
                                  setVistaPrevia((v) => (v?.orden.id === b.orden.id ? null : v))
                                }
                              />
                            ))
                          ) : (
                            <div
                              className={`gantt-barra gantt-barra-${tono}${enCruce ? ' gantt-barra-cruce' : ''}${
                                puedeArrastrar ? ' gantt-barra-arrastrable' : ''
                              }${redimEsta ? ' gantt-barra-redimensionando' : ''}`}
                              style={{
                                left: `${leftPct}%`,
                                width: `${widthPct}%`,
                                // Mientras se arrastra CUALQUIER barra, esta deja pasar el
                                // puntero hacia la celda de día que tiene debajo — si no,
                                // tapaba justo los días que ya cubre, y soltar ahí (para
                                // acortarla) nunca llegaba al `onDrop` de la celda
                                // (§ bug reportado: "solo se puede extender, no acortar").
                                pointerEvents: arrastrando ? 'none' : undefined,
                              }}
                              draggable={puedeArrastrar}
                              onClick={esMovil ? () => setDetalleAbiertoId(b.orden.id) : undefined}
                              onDragStart={(e) => {
                                e.dataTransfer.effectAllowed = 'move';
                                e.dataTransfer.setData('text/plain', b.orden.id);
                                setArrastrando({ id: b.orden.id, fechaInicio: b.orden.fechaInicio });
                              }}
                              onDragEnd={() => setArrastrando(null)}
                              onMouseEnter={(e) => {
                                const r = e.currentTarget.getBoundingClientRect();
                                setVistaPrevia({ orden: b.orden, x: r.left, y: r.bottom + 6, yArriba: r.top - 6 });
                              }}
                              onMouseLeave={() =>
                                setVistaPrevia((v) => (v?.orden.id === b.orden.id ? null : v))
                              }
                            >
                              <IconoTono />
                              {widthPct > 10 ? <span className="gantt-barra-texto">{b.orden.titulo}</span> : null}
                              {widthPct > 10 && b.orden.asignados.length > 0 ? (
                                <PilaAsignados asignados={b.orden.asignados} />
                              ) : null}
                              {enCruce ? (
                                <span className="gantt-barra-cruce-icono" title="Se cruza con otra orden">
                                  <IconoEstadoVencida />
                                </span>
                              ) : null}
                            </div>
                          )}
                          {puedeArrastrar ? (
                            <>
                              <span
                                className="gantt-barra-asa gantt-barra-asa-izq"
                                style={{ left: `${leftPct}%` }}
                                title="Arrastra para cambiar la fecha de inicio"
                                onMouseDown={(e) => iniciarRedimension(e, b.orden, 'inicio')}
                              />
                              <span
                                className="gantt-barra-asa gantt-barra-asa-der"
                                style={{ left: `${leftPct + widthPct}%` }}
                                title="Arrastra para cambiar la fecha de vencimiento"
                                onMouseDown={(e) => iniciarRedimension(e, b.orden, 'fin')}
                              />
                              {redimEsta ? (
                                <span
                                  className="gantt-redim-etiqueta"
                                  style={{ left: `${redimEsta.lado === 'inicio' ? leftPct : leftPct + widthPct}%` }}
                                >
                                  {fechaDeIndice(redimEsta.lado === 'inicio' ? diaIni : diaFinal)
                                    .toLocaleDateString('es', { day: 'numeric', month: 'short' })
                                    .replace('.', '')}
                                </span>
                              ) : null}
                            </>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>

        {errorArrastre ? (
          <div className="panel-cuerpo" style={{ paddingTop: 0 }}>
            <Aviso tono="error">{errorArrastre}</Aviso>
          </div>
        ) : null}
      </div>

      {vistaPrevia ? (
        <VistaPreviaOrdenGantt orden={vistaPrevia.orden} x={vistaPrevia.x} y={vistaPrevia.y} yArriba={vistaPrevia.yArriba} />
      ) : null}

      {detalleAbiertoId ? (
        <DialogoDetalleOtGantt id={detalleAbiertoId} alCerrar={() => setDetalleAbiertoId(null)} />
      ) : null}

      {panelConflictosAbierto ? (
        <PanelConflictosGantt
          pares={paresCruzados}
          alCerrar={() => setPanelConflictosAbierto(false)}
          alVerOrden={(id) => {
            setPanelConflictosAbierto(false);
            setDetalleAbiertoId(id);
          }}
          alResaltar={(ids) => {
            setPanelConflictosAbierto(false);
            resaltarUnMomento(ids);
          }}
        />
      ) : null}
    </>
  );
}

/**
 * Panel de conflictos (§ rediseño): qué OT se cruza con cuál, en vez de solo
 * un contador. "Resaltar" marca el par en el Gantt un par de segundos;
 * "Ver orden" abre el mismo modal de detalle que el resto de la vista.
 */
type VistaConflictos = 'ots' | 'personas' | 'fechas';

const ETIQUETA_VISTA_CONFLICTOS: Record<VistaConflictos, string> = {
  ots: 'Por OT',
  personas: 'Por persona',
  fechas: 'Por fechas',
};

function PanelConflictosGantt({
  pares,
  alCerrar,
  alVerOrden,
  alResaltar,
}: {
  pares: { a: BarraGantt; b: BarraGantt }[];
  alCerrar: () => void;
  alVerOrden: (id: string) => void;
  alResaltar: (ids: string[]) => void;
}): JSX.Element {
  const [vista, setVista] = useState<VistaConflictos>('ots');
  // Al cerrar, el panel sale animado antes de desmontarse (ver `saliendo` en CSS).
  const [saliendo, setSaliendo] = useState(false);
  const cerrar = (): void => {
    if (saliendo) return;
    setSaliendo(true);
    window.setTimeout(alCerrar, 240);
  };

  // Por persona: cada cruce se repite bajo cada persona que comparten las dos OT.
  const porPersona = new Map<string, { nombre: string; pares: { a: BarraGantt; b: BarraGantt }[] }>();
  for (const par of pares) {
    for (const x of par.a.orden.asignados) {
      if (!par.b.orden.asignados.some((y) => y.tipo === x.tipo && y.id === x.id)) continue;
      const clave = `${x.tipo}:${x.id}`;
      const grupo = porPersona.get(clave) ?? { nombre: x.nombre, pares: [] };
      grupo.pares.push(par);
      porPersona.set(clave, grupo);
    }
  }
  const personas = [...porPersona.values()].sort((x, y) => y.pares.length - x.pares.length);

  // Por fechas: el tramo exacto en que se solapan las dos OT, en orden cronológico.
  const inicioDe = (o: OrdenDeGantt) => (o.fechaInicio ?? o.fechaVencimiento ?? '').slice(0, 10);
  const finDe = (o: OrdenDeGantt) => (o.fechaVencimiento ?? o.fechaInicio ?? '').slice(0, 10);
  const porFecha = pares
    .map((par) => {
      const desde = [inicioDe(par.a.orden), inicioDe(par.b.orden)].sort().at(-1) ?? '';
      const hasta = [finDe(par.a.orden), finDe(par.b.orden)].sort().at(0) ?? '';
      return { par, desde, hasta };
    })
    .sort((x, y) => x.desde.localeCompare(y.desde));

  const filaPar = (par: { a: BarraGantt; b: BarraGantt }, clave: string, tramo?: string) => (
    <div className="gantt-conflicto-fila" key={clave}>
      <div className="gantt-conflicto-ot">
        <strong>
          #{par.a.orden.numero} · {par.a.orden.titulo}
        </strong>
        <span>
          {formatearFecha(par.a.orden.fechaInicio ?? par.a.orden.fechaVencimiento!)} –{' '}
          {formatearFecha(par.a.orden.fechaVencimiento!)}
        </span>
      </div>
      <div className="gantt-conflicto-separador">{tramo ? `se cruzan el ${tramo}` : 'se cruza con'}</div>
      <div className="gantt-conflicto-ot">
        <strong>
          #{par.b.orden.numero} · {par.b.orden.titulo}
        </strong>
        <span>
          {formatearFecha(par.b.orden.fechaInicio ?? par.b.orden.fechaVencimiento!)} –{' '}
          {formatearFecha(par.b.orden.fechaVencimiento!)}
        </span>
      </div>
      <div className="gantt-conflicto-acciones">
        <button type="button" className="boton secundario pequeno" onClick={() => alResaltar([par.a.key, par.b.key])}>
          Resaltar
        </button>
        <button type="button" className="boton secundario pequeno" onClick={() => alVerOrden(par.a.orden.id)}>
          Ver #{par.a.orden.numero}
        </button>
        <button type="button" className="boton secundario pequeno" onClick={() => alVerOrden(par.b.orden.id)}>
          Ver #{par.b.orden.numero}
        </button>
      </div>
    </div>
  );

  return (
    <div className={`capa-dialogo${saliendo ? " saliendo" : ""}`} role="presentation" onClick={(e) => e.target === e.currentTarget && cerrar()}>
      <div className={`gantt-panel-conflictos panel${saliendo ? " saliendo" : ""}`} role="dialog" aria-label="Conflictos de planificación">
        <div className="panel-cabecera">
          <h2>Conflictos de planificación</h2>
          <button type="button" className="boton-enlace" onClick={cerrar}>
            Cerrar
          </button>
        </div>
        <div className="panel-cuerpo">
          <div className="tabs-conflicto" role="tablist" aria-label="Agrupar conflictos">
            {(Object.keys(ETIQUETA_VISTA_CONFLICTOS) as VistaConflictos[]).map((v) => (
              <button
                key={v}
                type="button"
                role="tab"
                aria-selected={vista === v}
                className={vista === v ? 'activa' : ''}
                onClick={() => setVista(v)}
              >
                {ETIQUETA_VISTA_CONFLICTOS[v]}
              </button>
            ))}
          </div>

          {/* La key por vista hace que el contenido se monte de nuevo al cambiar de
              pestaña, y así la animación de entrada corre en cada apertura. */}
          <div key={vista} className="conflictos-contenido">
          {pares.length === 0 ? (
            <Vacio titulo="Sin conflictos" detalle="Ningún asignado tiene dos órdenes cruzadas por fecha este período." />
          ) : vista === 'ots' ? (
            pares.map(({ a, b }, i) => filaPar({ a, b }, `ot-${i}`))
          ) : vista === 'personas' ? (
            personas.map((grupo) => (
              <section key={grupo.nombre} className="conflicto-persona">
                <header className="conflicto-persona-cabecera">
                  <span className="conflicto-persona-inicial" aria-hidden="true">
                    {grupo.nombre.charAt(0).toUpperCase()}
                  </span>
                  <span>{grupo.nombre}</span>
                  <span className="conflicto-persona-cuenta">
                    {grupo.pares.length} cruce{grupo.pares.length === 1 ? '' : 's'}
                  </span>
                </header>
                {grupo.pares.map((par, i) => filaPar(par, `per-${grupo.nombre}-${i}`))}
              </section>
            ))
          ) : (
            porFecha.map(({ par, desde, hasta }, i) =>
              filaPar(par, `fec-${i}`, desde === hasta ? formatearFecha(desde) : `${formatearFecha(desde)} – ${formatearFecha(hasta)}`),
            )
          )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Ficha al pasar el cursor sobre una barra (§ pedido): frecuencia y los datos
 * que más varían de una OT a otra (ubicación, activo, asignado, tiempo
 * estimado) — mismo componente visual que ya usa el calendario.
 */
function VistaPreviaOrdenGantt({
  orden,
  x,
  y,
  yArriba,
}: {
  orden: OrdenDeGantt;
  x: number;
  y: number;
  yArriba: number;
}): JSX.Element {
  const izquierda = Math.min(x, window.innerWidth - 300);
  // Si la ficha no cabe debajo de la barra (fila al final de la pantalla), se
  // abre hacia arriba; se mide tras renderizar porque el alto depende de los datos.
  const ref = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState(y);
  useLayoutEffect(() => {
    const alto = ref.current?.offsetHeight ?? 0;
    const margen = 8;
    if (y + alto <= window.innerHeight - margen) setTop(y);
    else setTop(Math.max(margen, yArriba - alto));
  }, [y, yArriba]);
  // Duración en días, calculada sobre los mismos dos campos que ya dibujan la
  // barra — nada nuevo, solo expresado de una forma más directa de leer.
  let duracion = '—';
  if (orden.fechaVencimiento) {
    const fin = new Date(`${orden.fechaVencimiento.slice(0, 10)}T00:00:00`);
    const inicio = orden.fechaInicio ? new Date(`${orden.fechaInicio.slice(0, 10)}T00:00:00`) : fin;
    const dias = Math.max(1, Math.round((fin.getTime() - inicio.getTime()) / 86_400_000) + 1);
    duracion = `${dias} día${dias === 1 ? '' : 's'}`;
  }
  return (
    <div ref={ref} className="calendario-vista-previa gantt-tooltip" style={{ left: izquierda, top }}>
      <div className="calendario-vista-previa-cabecera">
        <strong>
          #{orden.numero} {orden.titulo}
        </strong>
        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
          <EstadoOtEtiqueta estado={orden.estado} vencida={orden.vencida} />
          {orden.prioridad !== 'NINGUNA' ? <PrioridadEtiqueta prioridad={orden.prioridad} /> : null}
        </div>
      </div>
      <div className="calendario-vista-previa-cuerpo">
        <DatoVistaPrevia
          titulo="Inicio"
          valor={orden.fechaInicio ? formatearFecha(orden.fechaInicio) : orden.fechaVencimiento ? formatearFecha(orden.fechaVencimiento) : '—'}
        />
        <DatoVistaPrevia
          titulo="Fin"
          valor={orden.fechaVencimiento ? formatearFecha(orden.fechaVencimiento) : '—'}
          tono={orden.vencida ? 'roja' : undefined}
        />
        <DatoVistaPrevia titulo="Duración" valor={duracion} />
        <DatoVistaPrevia titulo="Frecuencia" valor={ETIQUETA_RECURRENCIA[orden.recurrencia]} />
        <DatoVistaPrevia titulo="Tiempo estimado" valor={orden.tiempoEstimadoMinutos ? formatearDuracion(orden.tiempoEstimadoMinutos) : '—'} />
        <DatoVistaPrevia titulo="Tipo de trabajo" valor={ETIQUETA_TIPO_TRABAJO[orden.tipoTrabajo]} />
        {/* Personas y grupos van en secciones separadas: un grupo no es una
            persona y en la misma lista se confundían. */}
        <DatoVistaPrevia
          titulo="Asignado a"
          valor={(() => {
            const personas = orden.asignados.filter((a) => a.tipo === 'USUARIO');
            return personas.length > 0 ? <AsignadosConNombre asignados={personas} /> : '—';
          })()}
        />
        <DatoVistaPrevia
          titulo="Grupos"
          valor={(() => {
            const grupos = orden.asignados.filter((a) => a.tipo === 'GRUPO');
            return grupos.length > 0 ? <AsignadosConNombre asignados={grupos} /> : '—';
          })()}
        />
        <DatoVistaPrevia titulo="Ubicación" valor={orden.ubicacion ?? '—'} />
        <DatoVistaPrevia titulo="Activo" valor={orden.activo ?? '—'} />
      </div>
    </div>
  );
}

/**
 * Detalle de una OT en un modal (§ pedido): clic en el Gantt se queda ahí
 * mismo, en vez de saltar a la ficha completa — más rápido para comparar
 * varias fechas sin perder el mes que se estaba mirando.
 */
function DialogoDetalleOtGantt({ id, alCerrar }: { id: string; alCerrar: () => void }): JSX.Element {
  const { data: orden, isLoading } = useQuery({
    queryKey: ['orden', id],
    queryFn: () => api.get<OrdenTrabajo>(`/ordenes-trabajo/${id}`),
  });

  return (
    <Dialogo titulo={orden ? `#${orden.numero} · ${orden.titulo}` : 'Orden de trabajo'} ancho={520} alCerrar={alCerrar}>
      {isLoading || !orden ? (
        <Cargando />
      ) : (
        <>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
            <EstadoOtEtiqueta estado={orden.estado} vencida={orden.vencida} />
            {orden.prioridad !== 'NINGUNA' ? <PrioridadEtiqueta prioridad={orden.prioridad} /> : null}
          </div>
          {orden.descripcion ? (
            <p style={{ marginTop: 0, color: 'var(--texto-medio)', fontSize: 13.5 }}>{orden.descripcion}</p>
          ) : null}
          <div className="calendario-vista-previa-cuerpo" style={{ padding: 0 }}>
            <DatoVistaPrevia titulo="Frecuencia" valor={ETIQUETA_RECURRENCIA[orden.recurrencia]} />
            <DatoVistaPrevia
              titulo="Fecha de inicio"
              valor={orden.fechaInicio ? formatearFecha(orden.fechaInicio) : '—'}
            />
            <DatoVistaPrevia
              titulo="Fecha de vencimiento"
              valor={orden.fechaVencimiento ? formatearFecha(orden.fechaVencimiento) : '—'}
              tono={orden.vencida ? 'roja' : undefined}
            />
            <DatoVistaPrevia titulo="Tiempo estimado" valor={orden.tiempoEstimadoMinutos ? formatearDuracion(orden.tiempoEstimadoMinutos) : '—'} />
            <DatoVistaPrevia titulo="Tipo de trabajo" valor={ETIQUETA_TIPO_TRABAJO[orden.tipoTrabajo]} />
            <DatoVistaPrevia titulo="Asignado a" valor={orden.asignados.length > 0 ? orden.asignados.map((a) => a.nombre).join(', ') : '—'} />
            <DatoVistaPrevia titulo="Ubicación" valor={orden.ubicacion ?? '—'} />
            <DatoVistaPrevia titulo="Activo" valor={orden.activo ?? '—'} />
            <DatoVistaPrevia
              titulo="Tipos de falla"
              valor={orden.tiposFalla.length > 0 ? orden.tiposFalla.map((t) => t.nombre).join(', ') : '—'}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
            <button type="button" className="boton secundario" onClick={alCerrar}>
              Cerrar
            </button>
            <EnlaceRuta to={`/ordenes/${orden.id}`} className="boton">
              Ver orden completa
            </EnlaceRuta>
          </div>
        </>
      )}
    </Dialogo>
  );
}

export function DetalleOrden(): JSX.Element {
  const { id = '' } = useParams();
  const navegarAuto = useNavigate();
  const ubicacionActual = useLocation();
  const cliente = useQueryClient();
  const { puede } = useSesion();
  const [comentario, setComentario] = useState('');
  const [completarAbierto, setCompletarAbierto] = useState(false);
  const [observacionAbierta, setObservacionAbierta] = useState(false);
  const areaComentarioRef = useRef<HTMLTextAreaElement>(null);

  // Crece con el texto en vez de scrollear adentro, como un composer de
  // chat — hasta un tope para no comerse la pantalla con un párrafo largo.
  useEffect(() => {
    const area = areaComentarioRef.current;
    if (!area) return;
    area.style.height = 'auto';
    area.style.height = `${Math.min(area.scrollHeight, 160)}px`;
  }, [comentario]);

  const { data: orden, isLoading } = useQuery({
    queryKey: ['orden', id],
    queryFn: () => api.get<OrdenTrabajo>(`/ordenes-trabajo/${id}`),
  });

  // §pedido: si la OT trae un único procedimiento adjunto, lo que se quiere
  // ver es el checklist, no la ficha de la OT — se entra directo a
  // `/ejecuciones/:id` en vez de obligar a un clic más desde el panel de
  // abajo. Con más de un procedimiento no hay uno "el" correcto al que
  // saltar, así que ahí sí se queda en la ficha para elegir. `volverA` deja
  // que el botón «Atrás» de la ejecución vuelva aquí sin rebotar de nuevo:
  // esta misma página detecta `desdeEjecucion` y no vuelve a redirigir.
  const { data: ejecucionesOrden } = useQuery({
    queryKey: ['ot-procedimientos', id],
    queryFn: () => api.get<{ id: string }[]>(`/ordenes-trabajo/${id}/procedimientos`),
    enabled: puede('procedimiento:ejecutar'),
  });
  const desdeEjecucion = (ubicacionActual.state as { desdeEjecucion?: boolean } | null)?.desdeEjecucion === true;
  useEffect(() => {
    if (desdeEjecucion) return;
    if (!puede('procedimiento:ejecutar')) return;
    if (ejecucionesOrden?.length === 1) {
      navegarAuto(`/ejecuciones/${ejecucionesOrden[0]!.id}`, { replace: true, state: { volverA: `/ordenes/${id}` } });
    }
    // Solo debe evaluar el salto una vez que las ejecuciones llegan, no en
    // cada re-render (p. ej. al tipear un comentario).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ejecucionesOrden]);

  const { data: hilo } = useQuery({
    queryKey: ['orden-hilo', id],
    queryFn: () => api.get<EventoHilo[]>(`/ordenes-trabajo/${id}/hilo`),
  });

  const { data: adjuntosOrden } = useQuery({
    queryKey: ['adjuntos', 'ORDEN_TRABAJO', id],
    queryFn: () => api.get<AdjuntoImagen[]>(`/adjuntos${consulta({ entidad: 'ORDEN_TRABAJO', entidadId: id })}`),
  });
  // Fotos y documentos comparten la misma entidad de adjunto; se separan solo
  // para mostrarlos distinto: galería para las imágenes, lista para lo demás.
  const fotos = adjuntosOrden?.filter((a) => a.tipoMime.startsWith('image/'));
  const archivos = adjuntosOrden?.filter((a) => !a.tipoMime.startsWith('image/'));

  // §16.3: la «deuda técnica» del activo, para que se vea de entrada al entrar
  // a cualquier OT sobre él y no solo en la OT donde se dejó.
  const { data: observacionesActivo } = useQuery({
    queryKey: ['observaciones-activo', orden?.activoId],
    queryFn: () => api.get<ObservacionActivo[]>(`/activos/${orden?.activoId}/observaciones`),
    enabled: !!orden?.activoId,
  });

  // Mismo mecanismo, pero para OT que apuntan a una ubicación sin activo
  // puntual — la deuda técnica se registra contra la ubicación entonces.
  const { data: observacionesUbicacion } = useQuery({
    queryKey: ['observaciones-ubicacion', orden?.ubicacionId],
    queryFn: () => api.get<ObservacionActivo[]>(`/ubicaciones/${orden?.ubicacionId}/observaciones`),
    enabled: !!orden?.ubicacionId && !orden?.activoId && !orden?.sujetoEvaluableId,
  });

  // Personal: en una OT de persona sin activo, lo pendiente de la persona se
  // ve en el detalle igual que la deuda técnica de un activo.
  const { data: observacionesSujeto } = useQuery({
    queryKey: ['observaciones-sujeto', orden?.sujetoEvaluableId],
    queryFn: () => api.get<ObservacionActivo[]>(`/sujetos-evaluables/${orden?.sujetoEvaluableId}/observaciones`),
    enabled: !!orden?.sujetoEvaluableId && !orden?.activoId,
  });

  // Una OT nunca deja observación en dos entidades a la vez: activo, si no
  // persona, y solo si no hay ninguna de las dos, la ubicación.
  const observacionesOt = orden?.activoId
    ? observacionesActivo
    : orden?.sujetoEvaluableId
      ? observacionesSujeto
      : observacionesUbicacion;

  // Antes de ofrecer «Completa», hay que saber si el cierre va a rechazarse
  // por correctivas sin resolver — el modal de confirmación se apoya en esto
  // para mostrar el bloqueo de una, en vez de dejar que el usuario lo
  // descubra recién al intentarlo.
  const { data: correctivasDeOrden } = useQuery({
    queryKey: ['ordenes', 'ot-origen', id],
    queryFn: () => api.get<Pagina<OrdenTrabajo>>(`/ordenes-trabajo${consulta({ otOrigenId: id, tamano: 50 })}`),
    enabled: !!id,
  });
  const correctivasPendientes = (correctivasDeOrden?.datos ?? []).filter((c) => c.estado !== 'COMPLETA');

  const cambiarEstado = useMutation({
    // El mensaje de confirmación lo redacta el backend (`@MensajeExito` en
    // `OrdenesController.cambiarEstado`) y llega solo, por el cliente de la
    // API — no se repite acá.
    mutationFn: (estado: EstadoOT) => api.post(`/ordenes-trabajo/${id}/estado`, { estado }),
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['orden', id] });
      await cliente.invalidateQueries({ queryKey: ['orden-hilo', id] });
      await cliente.invalidateQueries({ queryKey: ['ordenes'] });
      await cliente.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  // §16.3: cerrar sin resolver del todo el activo es legítimo — la deuda queda
  // registrada contra el activo, no contra esta OT, para que la próxima OT que
  // lo toque la vea de entrada.
  // Ruta de observaciones de la OT actual: activo si lo tiene, si no la
  // ubicación (una OT nunca deja observación en ambas a la vez).
  const rutaObservaciones = orden?.activoId
    ? `/activos/${orden.activoId}/observaciones`
    : orden?.sujetoEvaluableId
      ? `/sujetos-evaluables/${orden.sujetoEvaluableId}/observaciones`
      : orden?.ubicacionId
        ? `/ubicaciones/${orden.ubicacionId}/observaciones`
        : null;

  // Recarga la lista de observaciones de la OT, sea de activo, persona o ubicación.
  const invalidarObservaciones = async (): Promise<void> => {
    if (orden?.activoId) {
      await cliente.invalidateQueries({ queryKey: ['observaciones-activo', orden.activoId] });
    } else if (orden?.sujetoEvaluableId) {
      await cliente.invalidateQueries({ queryKey: ['observaciones-sujeto', orden.sujetoEvaluableId] });
    } else if (orden?.ubicacionId) {
      await cliente.invalidateQueries({ queryKey: ['observaciones-ubicacion', orden.ubicacionId] });
    }
  };

  const completar = useMutation({
    mutationFn: async (
      observacion: { texto: string; fechaVencimiento: string | null; adjuntos: string[] } | null,
    ) => {
      if (observacion && rutaObservaciones) {
        await api.post(rutaObservaciones, {
          texto: observacion.texto,
          ordenTrabajoId: id,
          fechaVencimiento: observacion.fechaVencimiento,
          adjuntos: observacion.adjuntos,
        });
      }
      await api.post(`/ordenes-trabajo/${id}/estado`, { estado: 'COMPLETA' });
    },
    onSuccess: async () => {
      setCompletarAbierto(false);
      await cliente.invalidateQueries({ queryKey: ['orden', id] });
      await cliente.invalidateQueries({ queryKey: ['orden-hilo', id] });
      await cliente.invalidateQueries({ queryKey: ['ordenes'] });
      await cliente.invalidateQueries({ queryKey: ['dashboard'] });
      await invalidarObservaciones();
      // El header de la última llamada (cambiar a COMPLETA) ya trae el
      // mensaje de confirmación.
    },
  });

  // Dejar observación es independiente de cerrar la OT: desde la cabecera se
  // registra sin tocar el estado, y al cerrar el modal la lista de lo dejado.
  const dejarObservacion = useMutation({
    mutationFn: (observacion: { texto: string; fechaVencimiento: string | null; adjuntos: string[] }) =>
      api.post(rutaObservaciones!, {
        texto: observacion.texto,
        ordenTrabajoId: id,
        fechaVencimiento: observacion.fechaVencimiento,
        adjuntos: observacion.adjuntos,
      }),
    onSuccess: async () => {
      setObservacionAbierta(false);
      await cliente.invalidateQueries({ queryKey: ['orden-hilo', id] });
      await invalidarObservaciones();
    },
  });

  // Con correctivas sin resolver el cierre queda bloqueado, pero dejar la
  // observación del activo o la ubicación es una acción propia que no
  // depende de eso — se guarda igual, sin tocar el estado de la OT.
  const guardarObservacion = useMutation({
    mutationFn: (observacion: { texto: string; fechaVencimiento: string | null; adjuntos: string[] }) =>
      api.post(rutaObservaciones!, {
        texto: observacion.texto,
        ordenTrabajoId: id,
        fechaVencimiento: observacion.fechaVencimiento,
        adjuntos: observacion.adjuntos,
      }),
    onSuccess: async () => {
      setCompletarAbierto(false);
      await invalidarObservaciones();
    },
  });

  const [adjuntosComentario, setAdjuntosComentario] = useState<AdjuntoImagen[]>([]);
  const comentar = useMutation({
    mutationFn: (texto: string) =>
      api.post(`/ordenes-trabajo/${id}/comentarios`, {
        texto,
        adjuntos: adjuntosComentario.map((a) => a.id),
        menciones: [],
      }),
    onSuccess: async () => {
      setComentario('');
      setAdjuntosComentario([]);
      await cliente.invalidateQueries({ queryKey: ['orden-hilo', id] });
    },
  });

  if (isLoading) return <Cargando />;
  if (!orden) return <Vacio titulo="La orden de trabajo no existe" />;

  return (
    <>
      <div className="formulario-angosto">
      <div className="cabecera-fija-ot">
        <div className="cabecera-pagina cabecera-detalle-ot">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <BotonVolver a="/ordenes" etiqueta="Volver a Órdenes de Trabajo" />
              <div className="numero-registro">ORDEN DE TRABAJO #{orden.numero}</div>
            </div>
            <h1>{orden.titulo}</h1>
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--texto-tenue)' }}>
              Creado el {formatearFecha(orden.creadoEl)}
            </p>
            <OrigenOT orden={orden} />
          </div>

          {orden.ordenTrabajoPadreId ? (
            <p className="pertenece-a-ot">
              Pertenece a{' '}
              <EnlaceRuta to={`/ordenes/${orden.ordenTrabajoPadreId}`} style={{ fontWeight: 600 }}>
                OT #{orden.ordenTrabajoPadreNumero ?? '—'}
                {orden.ordenTrabajoPadreTitulo ? ` · ${orden.ordenTrabajoPadreTitulo}` : ''}
              </EnlaceRuta>
            </p>
          ) : null}

          <div className="cabecera-detalle-ot-acciones">
            <a href="#comentarios" className="boton secundario pequeno">
              <IconoComentario /> Comentarios
            </a>
            {rutaObservaciones ? (
              <button type="button" className="boton secundario pequeno" onClick={() => setObservacionAbierta(true)}>
                Dejar observación
              </button>
            ) : null}
            {puede('ot:editar') && orden.estado !== 'COMPLETA' ? (
              <EnlaceRuta to={`/ordenes/${orden.id}/editar`} className="boton secundario pequeno">
                <IconoEditar /> Editar
              </EnlaceRuta>
            ) : null}
          </div>
        </div>
      </div>

      {cambiarEstado.isError ? (
        <Aviso tono="error">{(cambiarEstado.error as Error).message}</Aviso>
      ) : null}
      {completar.isError ? <Aviso tono="error">{mensajeError(completar.error)}</Aviso> : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {orden.subOrdenes.total > 0 ? <TarjetaProgresoSubOrdenes subOrdenes={orden.subOrdenes} /> : null}

        <section className="panel">
          <div className="panel-cabecera">
            <h2>Estado</h2>
          </div>
          <div className="panel-cuerpo">
            {orden.vencida ? (
              <Aviso tono="atencion">
                Esta orden venció el {formatearFecha(orden.fechaVencimiento)} y sigue abierta.
              </Aviso>
            ) : null}
            <div className="rejilla-estados-ot">
              {ESTADOS_OT.map((e) => {
                const activo = e === orden.estado;
                const IconoEstado = ICONO_ESTADO_VISUAL_OT[e];
                return (
                  <button
                    key={e}
                    type="button"
                    className={`boton-estado-ot boton-estado-ot-${TONO_ESTADO_VISUAL_OT[e]}${activo ? ' activo' : ''}`}
                    disabled={!puede('ot:cambiar_estado') || cambiarEstado.isPending || activo}
                    onClick={() => {
                      // Con activo o ubicación asociados, el cierre pasa por
                      // el modal que ofrece dejar una observación; sin
                      // ninguno de los dos no hay nada que ese modal pudiera
                      // registrar, así que se completa directo.
                      if (e === 'COMPLETA' && (orden.activoId || orden.ubicacionId || correctivasPendientes.length > 0)) {
                        setCompletarAbierto(true);
                      } else cambiarEstado.mutate(e);
                    }}
                  >
                    <IconoEstado />
                    {ETIQUETA_ESTADO_OT[e]}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        <section className="panel">
          <div className="panel-cabecera">
            <h2>Descripción</h2>
          </div>
          <div className="panel-cuerpo">
            <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
              {orden.descripcion ?? 'Sin descripción.'}
            </p>

            {fotos && fotos.length > 0 ? (
              <div style={{ marginTop: 14 }}>
                <GaleriaImagenes fotos={fotos} titulo={(f) => `${orden.titulo} · OT #${orden.numero} · ${f.nombreArchivo}`} />
              </div>
            ) : null}

            {archivos && archivos.length > 0 ? (
              <div style={{ marginTop: 14 }}>
                <ListaArchivos archivos={archivos} />
              </div>
            ) : null}
          </div>
        </section>

        <section className="panel">
          <div className="panel-cabecera">
            <h2>Ubicación y asignación</h2>
          </div>
          <div
            className="panel-cuerpo"
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 18 }}
          >
            {/* Una OT de persona solo muestra ubicación si la tiene; si no, no aparece. */}
            {!orden.sujetoEvaluableId || orden.ubicacion ? (
            <Dato
              titulo="Ubicación"
              valor={
                <>
                  {orden.ubicacionId && orden.ubicacion ? (
                    <EnlaceRuta to={`/ubicaciones/${orden.ubicacionId}`}>{orden.ubicacion}</EnlaceRuta>
                  ) : (
                    orden.ubicacion ?? '—'
                  )}
                  {orden.ubicacionRaiz && orden.ubicacionRaiz !== orden.ubicacion ? (
                    <div style={{ fontSize: 12, color: 'var(--texto-tenue)', fontWeight: 500, marginTop: 2 }}>
                      Principal: {orden.ubicacionRaiz}
                    </div>
                  ) : null}
                </>
              }
            />
            ) : null}
            {orden.sujetoEvaluable ? <Dato titulo="Persona" valor={orden.sujetoEvaluable} /> : null}
            {orden.activoId || orden.activo ? (
              <Dato
                titulo="Activo"
                valor={
                  orden.activoId && orden.activo ? (
                    <EnlaceRuta to={`/activos/${orden.activoId}`}>{orden.activo}</EnlaceRuta>
                  ) : (
                    orden.activo ?? '—'
                  )
                }
              />
            ) : null}
            <Dato
              titulo="Asignado a"
              valor={
                orden.asignados.some((a) => a.tipo !== 'GRUPO')
                  ? orden.asignados
                      .filter((a) => a.tipo !== 'GRUPO')
                      .map((a) => a.nombre)
                      .join(', ')
                  : 'Sin asignar'
              }
            />
            {orden.asignados.some((a) => a.tipo === 'GRUPO') ? (
              <Dato
                titulo="Grupo asignado"
                valor={orden.asignados
                  .filter((a) => a.tipo === 'GRUPO')
                  .map((a) => a.nombre)
                  .join(', ')}
              />
            ) : null}
          </div>
        </section>

        <section className="panel">
          <div className="panel-cabecera">
            <h2>Clasificación y programación</h2>
          </div>
          <div className="panel-cuerpo" style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
            <div className="rejilla-campos" style={{ marginBottom: 0 }}>
              <Dato titulo="Prioridad" valor={<PrioridadEtiqueta prioridad={orden.prioridad} />} />
              <Dato titulo="Tipo de trabajo" valor={ETIQUETA_TIPO_TRABAJO[orden.tipoTrabajo]} />
              <Dato titulo="Tiempo estimado" valor={formatearDuracion(orden.tiempoEstimadoMinutos)} />
              <Dato titulo="Vencimiento" valor={formatearFecha(orden.fechaVencimiento)} />
            </div>
            {/* Las fallas son de equipos: en una OT de persona no aplican. */}
            {!orden.sujetoEvaluableId ? (
              <Dato
                titulo="Tipos de falla"
                valor={orden.tiposFalla.map((t) => t.nombre).join(', ') || '—'}
              />
            ) : null}
          </div>
        </section>

        <ProcedimientosResumenDeOrden ordenTrabajoId={orden.id} />
        {/* Junto al procedimiento, no al final de la ficha: la falla que
            genera la correctiva se ve arriba, y su seguimiento debe verse
            al lado, no obligar a bajar toda la página para encontrarlo. */}
        <CorrectivasGeneradas ordenTrabajoId={orden.id} />

        {puede('parte:leer') ? <PartesDeOrden ordenTrabajoId={orden.id} /> : null}

        {orden.solicitudId || orden.otOrigenId || orden.completadoEl ? (
          <section className="panel">
            <div className="panel-cabecera">
              <h2>Origen y seguimiento</h2>
            </div>
            <div className="panel-cuerpo" style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
              {orden.solicitudId ? (
                <Dato
                  titulo="Origen"
                  valor={<Link to={`/solicitudes/${orden.solicitudId}`}>Generada desde una solicitud</Link>}
                />
              ) : null}
              {orden.otOrigenId ? (
                <Dato
                  titulo="Origen"
                  valor={
                    <EnlaceRuta to={`/ordenes/${orden.otOrigenId}`}>Acción correctiva de la OT #{orden.otOrigenNumero}</EnlaceRuta>
                  }
                />
              ) : null}
              {orden.completadoEl ? (
                <Dato titulo="Completada el" valor={formatearFechaHora(orden.completadoEl)} />
              ) : null}
            </div>
          </section>
        ) : null}

          {(() => {
            const observaciones = observacionesOt;
            const titulo = orden.activoId
              ? 'Observaciones del activo'
              : orden.sujetoEvaluableId
                ? 'Observaciones de la persona'
                : 'Observaciones de la ubicación';
            if (!observaciones || observaciones.length === 0) return null;
            return (
              <section className="panel">
                <div className="panel-cabecera">
                  <h2>{titulo}</h2>
                </div>
                <div className="panel-cuerpo">
                  <div className="hilo">
                    {observaciones.map((obs) => (
                      <article key={obs.id} className="hilo-evento">
                        <div className="hilo-marca" aria-hidden="true">
                          !
                        </div>
                        <div className="hilo-cuerpo">
                          <div className="hilo-meta">
                            {obs.creadoPor ?? 'Sistema'} · {formatearFechaHora(obs.creadoEl)}
                            {obs.ordenTrabajoNumero ? ` · OT #${obs.ordenTrabajoNumero}` : ''}
                          </div>
                          <div className="hilo-texto">{obs.texto}</div>
                          {obs.adjuntos.length > 0 ? (
                            <div style={{ marginTop: 8 }}>
                              <GaleriaImagenes fotos={obs.adjuntos} />
                            </div>
                          ) : null}
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              </section>
            );
          })()}

        {/* Adjuntar procedimiento y repartir en sub-órdenes ahora se decide al
            crear o editar la OT (ver FormularioOrden); acá solo se muestran
            las sub-órdenes que ya existen, sin ofrecer crear más desde el
            detalle. */}
        {puede('ot:crear') && orden.subOrdenes.total > 0 ? (
          <SubOrdenesDeOrden ordenTrabajoId={orden.id} ubicacionOrden={orden.ubicacionId} />
        ) : null}

        <section className="panel">
          <div className="panel-cuerpo" style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
            <p style={{ margin: 0 }}>
              Creado por <strong>{orden.creadoPor ?? '—'}</strong> el {formatearFechaHora(orden.creadoEl)}
            </p>
            <p style={{ margin: 0, color: 'var(--texto-medio)' }}>
              Última actualización el{' '}
              {formatearFechaHora(
                (hilo ?? []).length > 0 ? (hilo ?? [])[(hilo ?? []).length - 1]!.fechaHora : orden.creadoEl,
              )}
            </p>
          </div>
        </section>

          {puede('ot:leer') ? <SeccionMaterialesOt otId={id} puedeMarcar={puede('ot:editar')} /> : null}

          {/* §10.2: un único hilo cronológico que mezcla comentarios de personas
              y eventos del sistema. Es el mecanismo real de coordinación. */}
          <section className="panel" id="comentarios">
            <div className="panel-cabecera">
              <h2>Comentarios</h2>
            </div>
            <div className="panel-cuerpo">
              <div className="hilo">
                {(hilo ?? []).map((evento) => (
                  <article
                    key={evento.id}
                    className={`hilo-evento ${tonoEvento(evento.tipoEvento)}`}
                  >
                    <div className="hilo-marca" aria-hidden="true">
                      {(evento.usuario ?? '·').charAt(0).toUpperCase()}
                    </div>
                    <div className="hilo-cuerpo">
                      <div className="hilo-meta">
                        {evento.usuario ?? 'Sistema'} · {formatearFechaHora(evento.fechaHora)}
                      </div>
                      <div className="hilo-texto">{evento.resumen}</div>
                      {evento.adjuntos.length > 0 ? (
                        <div style={{ marginTop: 8 }}>
                          <GaleriaImagenes fotos={evento.adjuntos} />
                        </div>
                      ) : null}
                    </div>
                  </article>
                ))}
                {(hilo ?? []).length === 0 ? <Vacio titulo="Sin actividad registrada" /> : null}
              </div>

              {puede('ot:comentar') ? (
                <form
                  className="caja-comentario"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (comentario.trim()) comentar.mutate(comentario.trim());
                  }}
                >
                  <SubidaImagenes
                    compacto
                    etiqueta="Fotos del comentario"
                    valor={adjuntosComentario}
                    alCambiar={setAdjuntosComentario}
                  />
                  <div className="caja-comentario-fila">
                    <textarea
                      ref={areaComentarioRef}
                      id="comentario"
                      className="caja-comentario-texto"
                      rows={1}
                      value={comentario}
                      onChange={(e) => setComentario(e.target.value)}
                      onKeyDown={(e) => {
                        // Enter envía, como en un chat; Shift+Enter sigue
                        // siendo salto de línea para comentarios largos.
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          if (comentario.trim()) comentar.mutate(comentario.trim());
                        }
                      }}
                      placeholder="Escriba un comentario… (Enter para enviar)"
                    />
                    <button
                      type="submit"
                      className="caja-comentario-enviar"
                      disabled={!comentario.trim() || comentar.isPending}
                      aria-label="Enviar comentario"
                      title="Enviar comentario"
                    >
                      <IconoEnviar />
                    </button>
                  </div>
                </form>
              ) : null}
            </div>
          </section>
      </div>
      </div>

      {completarAbierto ? (
        <ModalCompletarOrden
          entidadNombre={orden.activo ?? orden.ubicacion ?? orden.sujetoEvaluable}
          cargando={completar.isPending || guardarObservacion.isPending}
          correctivas={correctivasDeOrden?.datos ?? []}
          observacionesDejadas={(observacionesOt ?? []).filter((o) => o.ordenTrabajoNumero === orden.numero)}
          alCerrar={() => setCompletarAbierto(false)}
          alConfirmar={(observacion) => completar.mutate(observacion)}
          alGuardarObservacion={(observacion) => guardarObservacion.mutate(observacion)}
        />
      ) : null}
      {observacionAbierta && rutaObservaciones ? (
        <ModalDejarObservacion
          entidadNombre={orden.activo ?? orden.ubicacion ?? orden.sujetoEvaluable}
          cargando={dejarObservacion.isPending}
          alCerrar={() => setObservacionAbierta(false)}
          alGuardar={(observacion) => dejarObservacion.mutate(observacion)}
        />
      ) : null}
    </>
  );
}

/**
 * Confirmación al completar una OT con activo asociado (§16.3).
 *
 * «Dejar observación» es opcional y no bloquea el cierre: el técnico puede
 * completar directo si no hay nada que reportar del activo, o expandir el
 * formulario y dejar foto + texto antes de confirmar.
 */
/**
 * Aviso de «deuda técnica» al elegir un activo en el formulario de OT (§16.3).
 *
 * Se muestra ya en la creación/edición, no solo en el detalle: es antes de ir a
 * campo, no después, cuando al técnico le sirve saber que ese activo arrastra
 * algo pendiente de una OT anterior.
 */
function ObservacionesDelActivoSeleccionado({ activoId }: { activoId: string }): JSX.Element | null {
  const [detalleAbierto, setDetalleAbierto] = useState(false);

  const { data } = useQuery({
    queryKey: ['observaciones-activo', activoId],
    queryFn: () => api.get<ObservacionActivo[]>(`/activos/${activoId}/observaciones`),
    enabled: activoId !== '',
  });

  const pendientes = (data ?? []).filter((o) => !o.resuelta);
  if (activoId === '' || pendientes.length === 0) return null;

  const vencidas = pendientes.filter((o) => o.vencida).length;

  return (
    <>
      <Aviso tono={vencidas > 0 ? 'error' : 'atencion'}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <strong>
            Este activo tiene {pendientes.length} {pendientes.length === 1 ? 'observación' : 'observaciones'} pendiente
            {pendientes.length === 1 ? '' : 's'} (deuda técnica)
            {vencidas > 0 ? ` — ${vencidas} vencida${vencidas === 1 ? '' : 's'}` : ''}
          </strong>
          <button
            type="button"
            className="boton secundario pequeno"
            style={{ flex: 'none' }}
            onClick={() => setDetalleAbierto(true)}
          >
            Ver detalle del activo
          </button>
        </div>
        <details style={{ marginTop: 6 }}>
          <summary style={{ cursor: 'pointer', fontSize: 12.5, fontWeight: 700 }}>Ver observaciones</summary>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
            {pendientes.map((obs) => (
              <div key={obs.id}>
                <div style={{ fontSize: 12, fontWeight: 700, opacity: 0.85 }}>
                  {formatearFechaHora(obs.creadoEl)}
                  {obs.ordenTrabajoNumero ? ` · OT #${obs.ordenTrabajoNumero}` : ''}
                  {obs.fechaVencimiento ? (
                    <span style={obs.vencida ? { color: 'var(--falla)' } : undefined}>
                      {' · '}
                      {obs.vencida ? 'venció el' : 'atender antes del'} {formatearFecha(obs.fechaVencimiento)}
                    </span>
                  ) : null}
                </div>
                <div>{obs.texto}</div>
                <GaleriaImagenes fotos={obs.adjuntos} titulo={(f) => `Observación · ${f.nombreArchivo}`} />
              </div>
            ))}
          </div>
        </details>
      </Aviso>

      {detalleAbierto ? (
        <ModalDetalleActivo activoId={activoId} observaciones={data ?? []} alCerrar={() => setDetalleAbierto(false)} />
      ) : null}
    </>
  );
}

/** Mismo aviso de «deuda técnica» que el del activo, pero para la persona elegida en el formulario de OT. */
function ObservacionesDeSujetoSeleccionado({ sujetoId }: { sujetoId: string }): JSX.Element | null {
  const { data } = useQuery({
    queryKey: ['observaciones-sujeto', sujetoId],
    queryFn: () => api.get<ObservacionActivo[]>(`/sujetos-evaluables/${sujetoId}/observaciones`),
    enabled: sujetoId !== '',
  });

  const pendientes = (data ?? []).filter((o) => !o.resuelta);
  if (sujetoId === '' || pendientes.length === 0) return null;

  const vencidas = pendientes.filter((o) => o.vencida).length;

  return (
    <Aviso tono={vencidas > 0 ? 'error' : 'atencion'}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <strong>
          Esta persona tiene {pendientes.length} {pendientes.length === 1 ? 'observación' : 'observaciones'} pendiente
          {pendientes.length === 1 ? '' : 's'}
          {vencidas > 0 ? ` — ${vencidas} vencida${vencidas === 1 ? '' : 's'}` : ''}
        </strong>
        <EnlaceRuta to={`/personal/${sujetoId}`} className="boton secundario pequeno" style={{ flex: 'none' }}>
          Ver ficha
        </EnlaceRuta>
      </div>
      <details style={{ marginTop: 6 }}>
        <summary style={{ cursor: 'pointer', fontSize: 12.5, fontWeight: 700 }}>Ver observaciones</summary>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
          {pendientes.map((obs) => (
            <div key={obs.id}>
              <div style={{ fontSize: 12, fontWeight: 700, opacity: 0.85 }}>
                {formatearFechaHora(obs.creadoEl)}
                {obs.ordenTrabajoNumero ? ` · OT #${obs.ordenTrabajoNumero}` : ''}
                {obs.fechaVencimiento ? (
                  <span style={obs.vencida ? { color: 'var(--falla)' } : undefined}>
                    {' · '}
                    {obs.vencida ? 'venció el' : 'atender antes del'} {formatearFecha(obs.fechaVencimiento)}
                  </span>
                ) : null}
              </div>
              <div>{obs.texto}</div>
              <GaleriaImagenes fotos={obs.adjuntos} titulo={(f) => `Observación · ${f.nombreArchivo}`} />
            </div>
          ))}
        </div>
      </details>
    </Aviso>
  );
}

interface ActivoDetalle {
  numero: number;
  nombre: string;
  tiposActivo: { id: string; nombre: string }[];
  ubicacionNombre: string | null;
  ubicacionRaiz: string | null;
  activoPadreNombre: string | null;
  criticidad: string | null;
  estado: string;
  descripcion: string | null;
  fabricante: string | null;
  modelo: string | null;
  numeroSerie: string | null;
  codigoQr: string | null;
}

/** Vista de solo lectura del activo, sin pasar por el formulario de edición. */
function ModalDetalleActivo({
  activoId,
  observaciones,
  alCerrar,
}: {
  activoId: string;
  observaciones: ObservacionActivo[];
  alCerrar: () => void;
}): JSX.Element {
  const { data: activo, isLoading } = useQuery({
    queryKey: ['activo', activoId],
    queryFn: () => api.get<ActivoDetalle>(`/activos/${activoId}`),
  });

  const { data: fotos } = useQuery({
    queryKey: ['adjuntos', 'ACTIVO', activoId],
    queryFn: () => api.get<AdjuntoImagen[]>(`/adjuntos${consulta({ entidad: 'ACTIVO', entidadId: activoId })}`),
  });

  return (
    <Dialogo titulo={activo ? `#${activo.numero} · ${activo.nombre}` : 'Detalle del activo'} ancho={600} alCerrar={alCerrar}>
      {isLoading || !activo ? (
        <Cargando />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {fotos && fotos.length > 0 ? <GaleriaImagenes fotos={fotos} /> : null}

          <div className="rejilla-campos rejilla-datos">
            <Dato titulo="Ubicación" valor={activo.ubicacionNombre ?? '—'} />
            <Dato titulo="Sede" valor={activo.ubicacionRaiz ?? '—'} />
            <Dato titulo="Pertenece a" valor={activo.activoPadreNombre ?? '—'} />
            <Dato titulo="Criticidad" valor={activo.criticidad ?? '—'} />
            <Dato titulo="Estado" valor={activo.estado} />
            <Dato
              titulo="Tipos"
              valor={activo.tiposActivo.length > 0 ? activo.tiposActivo.map((t) => t.nombre).join(', ') : '—'}
            />
            <Dato titulo="Fabricante" valor={activo.fabricante ?? '—'} />
            <Dato titulo="Modelo" valor={activo.modelo ?? '—'} />
            <Dato titulo="N.º de serie" valor={activo.numeroSerie ?? '—'} />
            <Dato titulo="Código QR" valor={activo.codigoQr ?? '—'} />
          </div>

          {activo.descripcion ? (
            <div>
              <div className="indicador-titulo">DESCRIPCIÓN</div>
              <p style={{ margin: '4px 0 0', fontSize: 13.5 }}>{activo.descripcion}</p>
            </div>
          ) : null}

          <div>
            <div className="indicador-titulo" style={{ marginBottom: 6 }}>
              OBSERVACIONES (DEUDA TÉCNICA)
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {observaciones.map((obs) => (
                <div key={obs.id} className="hilo-evento" style={{ paddingLeft: 0, opacity: obs.resuelta ? 0.6 : 1 }}>
                  <div className="hilo-cuerpo">
                    <div className="hilo-meta">
                      {formatearFechaHora(obs.creadoEl)}
                      {obs.ordenTrabajoNumero ? ` · OT #${obs.ordenTrabajoNumero}` : ''}
                      {obs.resuelta ? ' · resuelta' : ''}
                      {!obs.resuelta && obs.fechaVencimiento ? (
                        <span style={obs.vencida ? { color: 'var(--falla)' } : undefined}>
                          {' · '}
                          {obs.vencida ? 'venció el' : 'atender antes del'} {formatearFecha(obs.fechaVencimiento)}
                        </span>
                      ) : null}
                    </div>
                    <div className="hilo-texto">{obs.texto}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" className="boton secundario" onClick={alCerrar}>
              Cerrar
            </button>
          </div>
        </div>
      )}
    </Dialogo>
  );
}

/** Deja una observación sobre el activo o la ubicación sin cerrar la OT. */
export function ModalDejarObservacion({
  entidadNombre,
  cargando,
  alCerrar,
  alGuardar,
}: {
  entidadNombre: string | null;
  cargando: boolean;
  alCerrar: () => void;
  alGuardar: (observacion: { texto: string; fechaVencimiento: string | null; adjuntos: string[] }) => void;
}): JSX.Element {
  const [texto, setTexto] = useState('');
  const [fechaVencimiento, setFechaVencimiento] = useState('');
  const [fotos, setFotos] = useState<AdjuntoImagen[]>([]);

  return (
    <Dialogo titulo="Dejar observación" alCerrar={alCerrar}>
      <p style={{ fontSize: 13.5, color: 'var(--texto-medio)', marginTop: 0 }}>
        {entidadNombre
          ? `Queda registrada en «${entidadNombre}» y visible en cualquier próxima OT sobre esa misma entidad. La OT sigue abierta.`
          : 'Queda registrada y la OT sigue abierta.'}
      </p>
      <div className="campo">
        <label htmlFor="dejar-observacion">Observación</label>
        <textarea
          id="dejar-observacion"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Ej.: Pared del consultorio sin pintar; programado con el pintor para la próxima semana."
        />
      </div>
      <div className="campo">
        <SelectorFecha
          id="dejar-vencimiento"
          etiqueta="Avisar de nuevo el (opcional)"
          valor={fechaVencimiento}
          alCambiar={setFechaVencimiento}
        />
      </div>
      <SubidaImagenes etiqueta="Fotos de la observación" valor={fotos} alCambiar={setFotos} />
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <button type="button" className="boton secundario" onClick={alCerrar} disabled={cargando}>
          Cancelar
        </button>
        <button
          type="button"
          className="boton"
          disabled={cargando || texto.trim() === ''}
          onClick={() =>
            alGuardar({
              texto: texto.trim(),
              fechaVencimiento: fechaVencimiento || null,
              adjuntos: fotos.map((f) => f.id),
            })
          }
        >
          {cargando ? 'Guardando…' : 'Guardar observación'}
        </button>
      </div>
    </Dialogo>
  );
}

function ModalCompletarOrden({
  entidadNombre,
  cargando,
  correctivas,
  observacionesDejadas,
  alCerrar,
  alConfirmar,
  alGuardarObservacion,
}: {
  /** Nombre del activo o, si la OT no tiene activo, de la ubicación. */
  entidadNombre: string | null;
  cargando: boolean;
  /** Toda OT correctiva nacida de una falla de este procedimiento — completa o no. */
  correctivas: OrdenTrabajo[];
  /** Observaciones dejadas en esta OT antes de cerrarla. */
  observacionesDejadas: ObservacionActivo[];
  alCerrar: () => void;
  alConfirmar: (observacion: { texto: string; fechaVencimiento: string | null; adjuntos: string[] } | null) => void;
  /** Guarda la observación sola, sin completar — la única vía cuando el cierre está bloqueado. */
  alGuardarObservacion: (observacion: { texto: string; fechaVencimiento: string | null; adjuntos: string[] }) => void;
}): JSX.Element {
  const [conObservacion, setConObservacion] = useState(false);
  const [texto, setTexto] = useState('');
  const [fechaVencimiento, setFechaVencimiento] = useState('');
  const [fotos, setFotos] = useState<AdjuntoImagen[]>([]);

  const correctivasPendientes = correctivas.filter((c) => c.estado !== 'COMPLETA');
  const bloqueada = correctivasPendientes.length > 0;

  return (
    <Dialogo titulo="Confirmar OT como completada" alCerrar={alCerrar}>
      {bloqueada ? (
        <Aviso tono="error">
          <div style={{ marginBottom: 8 }}>
            No se puede completar: el procedimiento de esta OT encontró{' '}
            {correctivasPendientes.length === 1 ? 'una falla que' : `${correctivasPendientes.length} fallas que`}{' '}
            {correctivasPendientes.length === 1 ? 'generó' : 'generaron'} una OT correctiva, y{' '}
            {correctivasPendientes.length === 1 ? 'esta' : 'esas'} todavía{' '}
            {correctivasPendientes.length === 1 ? 'no está completa' : 'no están completas'}:
          </div>
        </Aviso>
      ) : (
        <p style={{ fontSize: 13.5, color: 'var(--texto-medio)', marginTop: 0 }}>
          {entidadNombre
            ? `Se marcará como completa. Si algo quedó pendiente en «${entidadNombre}» sin bloquear el cierre, déjelo registrado abajo — quedará visible en cualquier próxima OT sobre esa misma entidad.`
            : 'Se marcará como completa.'}
        </p>
      )}

      {/* Se listan todas, completas o no: verlas resueltas es lo que confirma
          que ya no bloquean, no solo la ausencia de un aviso. */}
      {correctivas.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
          {correctivas.map((c) => (
            <EnlaceRuta key={c.id} to={`/ordenes/${c.id}`} className="fila-arbol" style={{ padding: '8px 10px' }}>
              <span style={{ flex: 1 }}>
                #{c.numero} {c.titulo}
              </span>
              <EstadoOtEtiqueta estado={c.estado} vencida={c.vencida} />
            </EnlaceRuta>
          ))}
        </div>
      ) : null}

      {observacionesDejadas.length > 0 ? (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>
            Observaciones dejadas en esta OT ({observacionesDejadas.length})
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {observacionesDejadas.map((o) => (
              <li key={o.id}>
                {o.texto}
                {o.fechaVencimiento ? (
                  <span style={{ color: 'var(--texto-medio)' }}> · avisar el {formatearFecha(o.fechaVencimiento)}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {conObservacion ? (
        <>
          <div className="campo">
            <label htmlFor="completar-observacion">Observación</label>
            <textarea
              id="completar-observacion"
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder="Ej.: Pared del consultorio sin pintar; programado con el pintor para la próxima semana."
            />
          </div>
          <div className="campo">
            <SelectorFecha
              id="completar-vencimiento"
              etiqueta="Avisar de nuevo el (opcional)"
              valor={fechaVencimiento}
              alCambiar={setFechaVencimiento}
            />
            <span className="campo-nota">
              Si esto depende de algo con fecha (llega un repuesto, vuelve el pintor), la observación se
              marca como vencida a partir de ese día — para que no se pierda de vista.
            </span>
          </div>
          <SubidaImagenes etiqueta="Fotos de la observación" valor={fotos} alCambiar={setFotos} />
        </>
      ) : entidadNombre ? (
        <button type="button" className="boton secundario pequeno" onClick={() => setConObservacion(true)}>
          Dejar observación
        </button>
      ) : null}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <button type="button" className="boton secundario" onClick={alCerrar} disabled={cargando}>
          {bloqueada && !conObservacion ? 'Cerrar' : 'Cancelar'}
        </button>
        {bloqueada ? (
          conObservacion ? (
            <button
              type="button"
              className="boton"
              disabled={cargando || texto.trim() === ''}
              onClick={() =>
                alGuardarObservacion({
                  texto: texto.trim(),
                  fechaVencimiento: fechaVencimiento || null,
                  adjuntos: fotos.map((f) => f.id),
                })
              }
            >
              {cargando ? 'Guardando…' : 'Guardar observación'}
            </button>
          ) : null
        ) : (
          <button
            type="button"
            className="boton"
            disabled={cargando || (conObservacion && texto.trim() === '')}
            onClick={() =>
              alConfirmar(
                conObservacion && texto.trim() !== ''
                  ? {
                      texto: texto.trim(),
                      fechaVencimiento: fechaVencimiento || null,
                      adjuntos: fotos.map((f) => f.id),
                    }
                  : null,
              )
            }
          >
            {cargando ? 'Guardando…' : conObservacion ? 'Guardar observación y completar' : 'Confirmar'}
          </button>
        )}
      </div>
    </Dialogo>
  );
}

/** Iniciales de los asignados, apiladas — para no repetir el nombre completo
 * de cada uno en una columna angosta de una tabla de sub-órdenes. */
/** Ciclo de colores por persona (por `id`, no por índice): mismo color en
 * toda la app aunque cambie el orden de la lista, y no es todo el mismo
 * azul — se lee de un vistazo quién es quién en una pila de avatares. */
const CANTIDAD_COLORES_AVATAR = 6;
function colorAvatar(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return hash % CANTIDAD_COLORES_AVATAR;
}

function PilaAsignados({ asignados }: { asignados: { id: string; nombre: string; tipo: 'USUARIO' | 'GRUPO' }[] }): JSX.Element {
  if (asignados.length === 0) return <span style={{ color: 'var(--texto-tenue)' }}>Sin asignar</span>;

  const iniciales = (nombre: string): string =>
    nombre
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase())
      .join('');

  return (
    <div className="pila-asignados">
      {asignados.map((a) => (
        <span
          key={a.id}
          className={`avatar avatar-pequeno avatar-color-${colorAvatar(a.id)} con-info`}
          data-info={`${a.nombre}${a.tipo === 'GRUPO' ? ' (grupo)' : ''}`}
        >
          {iniciales(a.nombre)}
        </span>
      ))}
    </div>
  );
}

/**
 * Igual que `PilaAsignados`, pero con el nombre a la vista en vez de solo en
 * el tooltip (§ pedido, Gantt): ahí la fila ya es angosta y no conviene
 * depender de un hover para saber a quién está asignada cada OT.
 */
function AsignadosConNombre({
  asignados,
}: {
  asignados: { id: string; nombre: string; tipo: 'USUARIO' | 'GRUPO' }[];
}): JSX.Element | null {
  if (asignados.length === 0) return null;

  const iniciales = (nombre: string): string =>
    nombre
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase())
      .join('');

  return (
    <div className="asignados-con-nombre">
      {asignados.map((a) => (
        <span key={a.id} className="asignado-con-nombre">
          <span className={`avatar avatar-mini avatar-color-${colorAvatar(a.id)}`}>{iniciales(a.nombre)}</span>
          <span className="asignado-con-nombre-texto">
            {a.tipo === 'GRUPO' ? <span className="etiqueta-grupo">Grupo</span> : null}
            {a.nombre}
          </span>
        </span>
      ))}
    </div>
  );
}

type AsignadoOrden = { id: string; nombre: string; tipo: 'USUARIO' | 'GRUPO' };

/**
 * Nombres en una celda angosta de la tabla de OT: se muestran los primeros
 * dos y un chip «+N» que abre un popover con la lista completa. Sin esto, una
 * OT con muchos asignados estira la fila a varias líneas de alto.
 */
const NOMBRES_VISIBLES_EN_CELDA = 2;
function NombresResumidos({ nombres }: { nombres: string[] }): JSX.Element {
  // Vista previa al pasar el mouse (como la tarjeta del Gantt y el calendario):
  // `pointer-events: none`, así no se pelea con el clic de la fila.
  const [vista, setVista] = useState<{ left: number; top: number; arriba: boolean } | null>(null);
  const botonRef = useRef<HTMLButtonElement>(null);

  if (nombres.length === 0) return <>—</>;
  const restantes = nombres.length - NOMBRES_VISIBLES_EN_CELDA;
  if (restantes <= 0) return <>{nombres.join(', ')}</>;

  const mostrar = () => {
    const r = botonRef.current?.getBoundingClientRect();
    if (!r) return;
    // Cerca del borde inferior se abre hacia arriba para no salirse de pantalla.
    const arriba = r.bottom > window.innerHeight / 2;
    // Y no más allá del borde derecho (la tarjeta mide 320px, ver `.nombres-vista-previa`).
    const left = Math.max(12, Math.min(r.left, window.innerWidth - 320 - 12));
    setVista({ left, top: arriba ? r.top - 6 : r.bottom + 6, arriba });
  };
  const ocultar = () => setVista(null);

  return (
    <span className="nombres-resumidos">
      {nombres.slice(0, NOMBRES_VISIBLES_EN_CELDA).join(', ')}
      <button
        ref={botonRef}
        type="button"
        className="nombres-resumidos-mas"
        onMouseEnter={mostrar}
        onMouseLeave={ocultar}
        onFocus={mostrar}
        onBlur={ocultar}
      >
        +{restantes}
      </button>
      {vista
        ? createPortal(
            <div
              className="calendario-vista-previa nombres-vista-previa"
              style={{ left: vista.left, top: vista.top, transform: vista.arriba ? 'translateY(-100%)' : undefined }}
            >
              <div className="calendario-vista-previa-cabecera">
                <strong>Asignados ({nombres.length})</strong>
              </div>
              <div className="calendario-vista-previa-cuerpo">
                {nombres.map((n, i) => (
                  <span key={`${n}-${i}`} className="asignado-con-nombre">
                    <span className={`avatar avatar-mini avatar-color-${colorAvatar(n)}`}>
                      {n
                        .trim()
                        .split(/\s+/)
                        .slice(0, 2)
                        .map((p) => p[0]?.toUpperCase())
                        .join('')}
                    </span>
                    <span className="asignado-con-nombre-texto">{n}</span>
                  </span>
                ))}
              </div>
            </div>,
            document.body,
          )
        : null}
    </span>
  );
}

/** Personas asignadas, una celda de la tabla de OT. */
function PersonasAsignadas({ asignados }: { asignados: AsignadoOrden[] }): JSX.Element {
  return <NombresResumidos nombres={asignados.filter((a) => a.tipo !== 'GRUPO').map((a) => a.nombre)} />;
}

/** Grupos asignados, en su propia columna de la tabla de OT. */
function GrupoAsignado({ asignados }: { asignados: AsignadoOrden[] }): JSX.Element {
  return <NombresResumidos nombres={asignados.filter((a) => a.tipo === 'GRUPO').map((g) => g.nombre)} />;
}

/**
 * Filas de las sub-órdenes, desplegadas bajo su OT padre en el mismo
 * listado — ver el reparto de una OT grande no debería obligar a abrir cada
 * una para volver atrás; el chevron de la fila padre ya alcanza.
 */
/**
 * Sub-órdenes de una OT, en su propia fila — se llama a sí misma para las
 * hijas de las hijas (p. ej. una correctiva generada desde el checklist de
 * una sub-orden, § pedido): cada nivel solo necesita saber quién es su
 * padre, no cuán profundo está la rama.
 */
function FilasHijasOT({
  ordenTrabajoPadreId,
  expandidas,
  setExpandidas,
  nivel = 1,
  puedeSeleccionar = false,
  seleccionadas,
  alternarSeleccion,
}: {
  ordenTrabajoPadreId: string;
  expandidas: Set<string>;
  setExpandidas: React.Dispatch<React.SetStateAction<Set<string>>>;
  nivel?: number;
  puedeSeleccionar?: boolean;
  seleccionadas?: Set<string>;
  alternarSeleccion?: (id: string) => void;
}): JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: ['ordenes', 'sub-ordenes', ordenTrabajoPadreId],
    queryFn: () =>
      api.get<Pagina<OrdenTrabajo>>(
        `/ordenes-trabajo${consulta({ ordenTrabajoPadreId, tamano: 100 })}`,
      ),
  });

  if (isLoading) {
    return (
      <tr>
        <td colSpan={puedeSeleccionar ? 11 : 10} style={{ padding: '10px 14px', color: 'var(--texto-tenue)', fontSize: 12.5 }}>
          Cargando sub-órdenes…
        </td>
      </tr>
    );
  }

  return (
    <>
      {(data?.datos ?? []).map((hija) => {
        const tieneHijas = hija.subOrdenes.total > 0;
        const expandida = expandidas.has(hija.id);
        return (
          <Fragment key={hija.id}>
            <tr className={`fila-hija-ot${hija.vencida ? ' fila-ot-vencida' : ''}`} data-nivel={Math.min(nivel, 3)}>
              {puedeSeleccionar ? (
                <td>
                  <CasillaVerificacion
                    marcada={seleccionadas?.has(hija.id) ?? false}
                    alCambiar={() => alternarSeleccion?.(hija.id)}
                    etiqueta={`Seleccionar orden #${hija.numero}`}
                  />
                </td>
              ) : null}
              <td>
                <MiniaturaImagenTabla url={hija.fotoUrl} nombre={hija.titulo} />
              </td>
              <td className="numero-registro">
                <span className="celda-numero-ot celda-numero-ot-hija" style={{ paddingLeft: 22 * nivel }}>
                  {tieneHijas ? (
                    <button
                      type="button"
                      className="chevron-arbol-ot"
                      aria-label={expandida ? `Contraer sub-órdenes de #${hija.numero}` : `Ver sub-órdenes de #${hija.numero}`}
                      onClick={() =>
                        setExpandidas((s) => {
                          const copia = new Set(s);
                          if (copia.has(hija.id)) copia.delete(hija.id);
                          else copia.add(hija.id);
                          return copia;
                        })
                      }
                    >
                      {expandida ? '▾' : '▸'}
                    </button>
                  ) : null}
                  #{hija.numero}
                </span>
              </td>
              <td>
                {/* El conector va en su propio elemento, fuera del texto del
                    link: metido como parte del texto (§ bug reportado),
                    con un título largo que envuelve a dos líneas la flecha
                    quedaba huérfana en su propia línea. Con `flex` se
                    queda fija arriba a la izquierda pase lo que pase con
                    el wrap. Solo a partir del segundo nivel: una hija
                    directa del padre (nivel 1) ya se distingue por el
                    fondo, no hace falta marcarla también. */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 5, paddingLeft: 22 * (nivel - 1) }}>
                  {nivel > 1 ? <span className="conector-arbol-ot" aria-hidden="true">↳</span> : null}
                  <div>
                    <EnlaceRuta to={`/ordenes/${hija.id}`} style={{ fontWeight: 600 }}>
                      {hija.titulo}
                    </EnlaceRuta>
                    <OrigenOT orden={hija} />
                  </div>
                </div>
              </td>
              <td>
                <EstadoOCeldaOT orden={hija} />
              </td>
              <td>
                <PrioridadEtiqueta prioridad={hija.prioridad} />
              </td>
              <td>{ETIQUETA_TIPO_TRABAJO[hija.tipoTrabajo]}</td>
              <td>{hija.ubicacion ?? '—'}</td>
              <td>{hija.activo ?? '—'}</td>
              <td>
                <PersonasAsignadas asignados={hija.asignados} />
              </td>
              <td>
                <GrupoAsignado asignados={hija.asignados} />
              </td>
              <td style={hija.vencida ? { color: 'var(--falla)', fontWeight: 700 } : undefined}>
                {formatearFecha(hija.fechaVencimiento)}
              </td>
            </tr>
            {expandida ? (
              <FilasHijasOT
                ordenTrabajoPadreId={hija.id}
                expandidas={expandidas}
                setExpandidas={setExpandidas}
                nivel={nivel + 1}
                puedeSeleccionar={puedeSeleccionar}
                seleccionadas={seleccionadas}
                alternarSeleccion={alternarSeleccion}
              />
            ) : null}
          </Fragment>
        );
      })}
    </>
  );
}

/** Igual que `FilasHijasOT`, pero como tarjetas — para la lista de OT en móvil. */
function TarjetasHijasOT({
  ordenTrabajoPadreId,
  expandidas,
  setExpandidas,
  nivel = 1,
}: {
  ordenTrabajoPadreId: string;
  expandidas: Set<string>;
  setExpandidas: React.Dispatch<React.SetStateAction<Set<string>>>;
  nivel?: number;
}): JSX.Element {
  const navegar = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['ordenes', 'sub-ordenes', ordenTrabajoPadreId],
    queryFn: () =>
      api.get<Pagina<OrdenTrabajo>>(
        `/ordenes-trabajo${consulta({ ordenTrabajoPadreId, tamano: 100 })}`,
      ),
  });

  if (isLoading) {
    return <p className="campo-nota" style={{ margin: '0 0 10px 20px' }}>Cargando sub-órdenes…</p>;
  }

  return (
    <>
      {(data?.datos ?? []).map((hija) => {
        const tieneHijas = hija.subOrdenes.total > 0;
        const expandida = expandidas.has(hija.id);
        return (
          <Fragment key={hija.id}>
            <div
              className="tarjeta-ot tarjeta-ot-hija"
              style={{ marginLeft: 14 * (nivel - 1) }}
              role="button"
              tabIndex={0}
              onClick={() => navegar(`/ordenes/${hija.id}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') navegar(`/ordenes/${hija.id}`);
              }}
            >
              <MiniaturaImagenTabla url={hija.fotoUrl} nombre={hija.titulo} />
              <div className="tarjeta-ot-cuerpo">
                <div className="tarjeta-ot-cabecera">
                  <span className="numero-registro">#{hija.numero}</span>
                  <EstadoOtEtiqueta estado={hija.estado} vencida={hija.vencida} />
                </div>
                <div className="tarjeta-ot-titulo">{hija.titulo}</div>
                <div className="tarjeta-ot-datos">
                  {hija.prioridad !== 'NINGUNA' ? <PrioridadEtiqueta prioridad={hija.prioridad} /> : null}
                  {hija.ubicacion ? <span>{hija.ubicacion}</span> : null}
                  {hija.fechaVencimiento ? (
                    <span style={hija.vencida ? { color: 'var(--falla)', fontWeight: 700 } : undefined}>
                      {formatearFecha(hija.fechaVencimiento)}
                    </span>
                  ) : null}
                </div>
                {tieneHijas ? (
                  <button
                    type="button"
                    className="tarjeta-ot-subordenes"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandidas((s) => {
                        const copia = new Set(s);
                        if (copia.has(hija.id)) copia.delete(hija.id);
                        else copia.add(hija.id);
                        return copia;
                      });
                    }}
                  >
                    {expandida ? '▾' : '▸'} {hija.subOrdenes.total - hija.subOrdenes.abiertas} de{' '}
                    {hija.subOrdenes.total} sub-órdenes completas
                  </button>
                ) : null}
              </div>
            </div>
            {expandida ? (
              <TarjetasHijasOT
                ordenTrabajoPadreId={hija.id}
                expandidas={expandidas}
                setExpandidas={setExpandidas}
                nivel={nivel + 1}
              />
            ) : null}
          </Fragment>
        );
      })}
    </>
  );
}

function Dato({ titulo, valor }: { titulo: string; valor: React.ReactNode }): JSX.Element {
  return (
    <div>
      <div className="indicador-titulo">{titulo.toUpperCase()}</div>
      <div style={{ fontSize: 13.5, fontWeight: 600 }}>{valor}</div>
    </div>
  );
}

/**
 * Avance de la OT padre en sus sub-órdenes: «completas de total» + una barra
 * de progreso, en vez de una línea de texto más entre el resto de los datos
 * — es el número que de verdad importa al abrir una OT que reparte trabajo
 * entre varios activos, y por eso va arriba, no al final de la ficha.
 */
function TarjetaProgresoSubOrdenes({
  subOrdenes,
}: {
  subOrdenes: { total: number; abiertas: number };
}): JSX.Element {
  const completas = subOrdenes.total - subOrdenes.abiertas;
  const porcentaje = subOrdenes.total > 0 ? Math.round((completas / subOrdenes.total) * 100) : 0;

  return (
    <section className="panel">
      <div className="panel-cuerpo">
        <div className="indicador-titulo">SUB-ÓRDENES DE TRABAJO</div>
        <div style={{ fontSize: 22, fontWeight: 700, margin: '4px 0 10px' }}>
          {completas} de {subOrdenes.total}
        </div>
        <div className="barra-progreso">
          <div className="barra-progreso-relleno" style={{ width: `${porcentaje}%` }} />
        </div>
      </div>
    </section>
  );
}


/**
 * Combo de búsqueda de OT, para elegir la orden destino al reasignar una
 * correctiva (§8.9, punto 30). Mismo patrón que `SelectorActivo`: se escribe
 * para filtrar y las coincidencias aparecen debajo, acotadas por la API.
 */
function SelectorOrdenTrabajo({
  id,
  etiqueta = 'Orden de trabajo destino',
  valor,
  alCambiar,
  excluir,
}: {
  id: string;
  etiqueta?: string;
  valor: string;
  alCambiar: (valor: string, numero?: number) => void;
  /** La propia OT (y sus descendientes) no debería poder elegirse a sí misma. */
  excluir?: string;
}): JSX.Element {
  const [abierto, setAbierto] = useState(false);
  const [buscar, setBuscar] = useState('');
  const [nombreElegido, setNombreElegido] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ['ordenes-buscables', buscar],
    queryFn: () =>
      api.get<Pagina<{ id: string; numero: number; titulo: string }>>(
        `/ordenes-trabajo${consulta({ tamano: 25, orden: 'numero', direccion: 'desc', buscar })}`,
      ),
    enabled: abierto,
  });

  const opciones = (data?.datos ?? []).filter((o) => o.id !== excluir);

  function elegir(orden: { id: string; numero: number; titulo: string }): void {
    alCambiar(orden.id, orden.numero);
    setNombreElegido(`#${orden.numero} ${orden.titulo}`);
    setBuscar('');
    setAbierto(false);
  }

  return (
    <div className="campo combo">
      <label htmlFor={id}>{etiqueta}</label>
      <input
        id={id}
        type="text"
        autoComplete="off"
        value={abierto ? buscar : (nombreElegido ?? '')}
        onFocus={() => setBuscar('')}
        onClick={() => setAbierto(true)}
        onChange={(e) => {
          setAbierto(true);
          setBuscar(e.target.value);
        }}
        placeholder="Buscar por número o título…"
      />
      {abierto ? (
        <div className="combo-panel" role="listbox">
          {opciones.map((o) => (
            <button key={o.id} type="button" className="combo-opcion" onClick={() => elegir(o)}>
              #{o.numero} {o.titulo}
            </button>
          ))}
          {opciones.length === 0 ? <div className="combo-vacio">Sin resultados.</div> : null}
        </div>
      ) : null}
      {valor && !nombreElegido ? <span className="campo-nota">Seleccionada.</span> : null}
    </div>
  );
}

/**
 * Fallas generadas por esta OT (§8.9, punto 30): cada campo en FALLA de un
 * procedimiento adjunto nace como su propia OT correctiva, desacoplada del
 * cierre de esta — se listan acá para poder reasignarlas a otra OT ya
 * agendada en vez de dejarlas sueltas.
 */
function CorrectivasGeneradas({ ordenTrabajoId }: { ordenTrabajoId: string }): JSX.Element | null {
  const cliente = useQueryClient();
  const [reasignando, setReasignando] = useState<{ id: string; numero: number; titulo: string } | null>(null);
  const [destinoId, setDestinoId] = useState('');

  const { data } = useQuery({
    queryKey: ['ordenes', 'ot-origen', ordenTrabajoId],
    queryFn: () =>
      api.get<Pagina<OrdenTrabajo>>(`/ordenes-trabajo${consulta({ otOrigenId: ordenTrabajoId, tamano: 50 })}`),
  });

  const reasignar = useMutation({
    mutationFn: () =>
      api.post(`/ordenes-trabajo/${reasignando!.id}/reasignar`, { ordenTrabajoDestinoId: destinoId }),
    onSuccess: async () => {
      setReasignando(null);
      setDestinoId('');
      await cliente.invalidateQueries({ queryKey: ['ordenes', 'ot-origen', ordenTrabajoId] });
    },
  });

  const correctivas = data?.datos ?? [];
  if (correctivas.length === 0) return null;

  return (
    <section className="panel">
      <div className="panel-cabecera">
        <h2>Fallas generadas por esta orden</h2>
      </div>
      <div className="panel-cuerpo">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {correctivas.map((c) => (
            <div key={c.id} className="fila-arbol" style={{ padding: '8px 10px' }}>
              <EnlaceRuta to={`/ordenes/${c.id}`} style={{ flex: 1, fontWeight: 600 }}>
                #{c.numero} {c.titulo}
              </EnlaceRuta>
              <EstadoOtEtiqueta estado={c.estado} vencida={c.vencida} />
              {/* Nace como sub-orden de esta OT (§ pedido); «Reasignar» sigue
                  disponible por si en vez de acá corresponde que cuelgue de
                  otra — ya no depende de si tiene padre, porque ahora
                  siempre lo tiene. */}
              {c.estado !== 'COMPLETA' ? (
                <button
                  type="button"
                  className="boton secundario pequeno"
                  onClick={() => setReasignando({ id: c.id, numero: c.numero, titulo: c.titulo })}
                >
                  Reasignar
                </button>
              ) : null}
            </div>
          ))}
        </div>
      </div>

      {reasignando ? (
        <Dialogo
          titulo={`Reasignar #${reasignando.numero} a otra OT`}
          alCerrar={() => {
            setReasignando(null);
            setDestinoId('');
          }}
        >
          <p style={{ fontSize: 13.5, color: 'var(--texto-medio)', marginTop: 0 }}>
            «{reasignando.titulo}» pasará a formar parte del alcance de la OT que elija: quedará como su
            sub-orden, y bloqueará el cierre de esa OT hasta que se resuelva.
          </p>
          <SelectorOrdenTrabajo id="reasignar-destino" valor={destinoId} alCambiar={setDestinoId} excluir={reasignando.id} />
          {reasignar.isError ? <Aviso tono="error">{mensajeError(reasignar.error)}</Aviso> : null}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button
              type="button"
              className="boton secundario"
              onClick={() => {
                setReasignando(null);
                setDestinoId('');
              }}
            >
              Cancelar
            </button>
            <button type="button" className="boton" disabled={!destinoId || reasignar.isPending} onClick={() => reasignar.mutate()}>
              {reasignar.isPending ? 'Reasignando…' : 'Reasignar'}
            </button>
          </div>
        </Dialogo>
      ) : null}
    </section>
  );
}

/**
 * Procedimiento(s) adjuntos, en la vista de solo lectura de la OT.
 *
 * Adjuntar uno queda registrado en el hilo («Adjuntó un procedimiento…»),
 * pero antes de esto no había ningún lugar en el detalle para verlo o
 * entrar a completarlo — solo se podía desde el formulario de edición.
 */
function ProcedimientosResumenDeOrden({ ordenTrabajoId }: { ordenTrabajoId: string }): JSX.Element | null {
  const { data: ejecuciones } = useQuery({
    queryKey: ['ot-procedimientos', ordenTrabajoId],
    queryFn: () => api.get<EjecucionResumen[]>(`/ordenes-trabajo/${ordenTrabajoId}/procedimientos`),
  });

  if (!ejecuciones || ejecuciones.length === 0) return null;

  return (
    <section className="panel">
      <div className="panel-cabecera">
        <h2>Procedimiento</h2>
      </div>
      <div className="panel-cuerpo" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {ejecuciones.map((e) => (
          <EnlaceRuta
            key={e.id}
            to={`/ejecuciones/${e.id}`}
            className="arbol-item"
            style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '10px 14px' }}
          >
            <span style={{ flex: 1 }}>
              {e.plantillaNombre} <span style={{ color: 'var(--texto-tenue)', fontSize: 12 }}>v{e.plantillaVersion}</span>
              {e.resumen.falla > 0 ? (
                <span style={{ color: 'var(--falla)', fontSize: 12 }}> · {e.resumen.falla} con falla</span>
              ) : null}
            </span>
            <Etiqueta tono={e.estado === 'COMPLETADA' ? 'verde' : 'ambar'}>
              {e.estado === 'COMPLETADA' ? 'Completada' : 'En progreso'}
            </Etiqueta>
          </EnlaceRuta>
        ))}
      </div>
    </section>
  );
}

interface EjecucionResumen {
  id: string;
  plantillaNombre: string;
  plantillaVersion: number;
  estado: 'EN_PROGRESO' | 'COMPLETADA';
  puntajeTotal: string | null;
  porcentaje: string | null;
  resumen: { aprobado: number; alerta: number; falla: number; sinResponder: number; totalVisibles: number };
}

/**
 * Procedimientos adjuntos a la orden — §10.2.
 *
 * Adjuntar solo ofrece plantillas publicadas: un borrador no puede llegar a una
 * inspección real (§8.9), y filtrarlo aquí evita que el técnico descubra el
 * rechazo recién al intentarlo.
 */
function ProcedimientosDeOrden({ ordenTrabajoId }: { ordenTrabajoId: string }): JSX.Element {
  const cliente = useQueryClient();
  const [adjuntando, setAdjuntando] = useState(false);
  const [plantillaId, setPlantillaId] = useState('');

  const { data: ejecuciones } = useQuery({
    queryKey: ['ot-procedimientos', ordenTrabajoId],
    queryFn: () => api.get<EjecucionResumen[]>(`/ordenes-trabajo/${ordenTrabajoId}/procedimientos`),
  });

  const { data: plantillas } = useQuery({
    queryKey: ['procedimientos', 'PUBLICADA', ''],
    queryFn: () => api.get<{ id: string; nombre: string; cantidadCampos: number }[]>('/procedimientos?estado=PUBLICADA'),
    enabled: adjuntando,
  });

  const adjuntar = useMutation({
    mutationFn: () => api.post<{ id: string }>(`/ordenes-trabajo/${ordenTrabajoId}/procedimientos`, { plantillaId }),
    onSuccess: async () => {
      setAdjuntando(false);
      setPlantillaId('');
      await cliente.invalidateQueries({ queryKey: ['ot-procedimientos', ordenTrabajoId] });
      await cliente.invalidateQueries({ queryKey: ['orden-hilo', ordenTrabajoId] });
    },
  });

  const quitar = useMutation({
    mutationFn: (ejecucionId: string) => api.delete(`/ejecuciones/${ejecucionId}`),
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['ot-procedimientos', ordenTrabajoId] });
      await cliente.invalidateQueries({ queryKey: ['orden-hilo', ordenTrabajoId] });
    },
  });

  return (
    <section className="panel">
      <div className="panel-cabecera">
        <h2>Procedimientos</h2>
        <button type="button" className="boton secundario pequeno" onClick={() => setAdjuntando((v) => !v)}>
          {adjuntando ? 'Cancelar' : 'Adjuntar procedimiento'}
        </button>
      </div>

      <div className="panel-cuerpo">
        {adjuntar.isError ? <Aviso tono="error">{(adjuntar.error as Error).message}</Aviso> : null}
        {quitar.isError ? <Aviso tono="error">{(quitar.error as Error).message}</Aviso> : null}

        {adjuntando ? (
          <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
            <select
              value={plantillaId}
              onChange={(e) => setPlantillaId(e.target.value)}
              style={{ flex: 1, minWidth: 220, padding: '9px 12px', border: '1px solid var(--borde)', borderRadius: 8 }}
              aria-label="Plantilla a adjuntar"
            >
              <option value="">Elija una plantilla publicada…</option>
              {(plantillas ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre} ({p.cantidadCampos} campos)
                </option>
              ))}
            </select>
            <button
              type="button"
              className="boton"
              disabled={!plantillaId || adjuntar.isPending}
              onClick={() => adjuntar.mutate()}
            >
              Adjuntar
            </button>
          </div>
        ) : null}

        {(ejecuciones ?? []).length === 0 ? (
          <Vacio titulo="Sin procedimientos adjuntos" detalle="Adjunte un checklist para ejecutarlo en sitio." />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {(ejecuciones ?? []).map((e) => {
              const respondidos = e.resumen.totalVisibles - e.resumen.sinResponder;
              const avance = e.resumen.totalVisibles > 0 ? (respondidos / e.resumen.totalVisibles) * 100 : 0;

              return (
                <div key={e.id} className="arbol-item" style={{ padding: '12px 14px', marginBottom: 0 }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
                    <EnlaceRuta to={`/ejecuciones/${e.id}`} style={{ flex: 1, fontWeight: 700, color: 'var(--tinta)' }}>
                      {e.plantillaNombre}{' '}
                      <span style={{ fontWeight: 500, color: 'var(--texto-tenue)', fontSize: 12 }}>
                        v{e.plantillaVersion}
                      </span>
                    </EnlaceRuta>
                    <Etiqueta tono={e.estado === 'COMPLETADA' ? 'verde' : 'azul'}>
                      {e.estado === 'COMPLETADA' ? 'Completado' : 'En progreso'}
                    </Etiqueta>
                    {e.estado !== 'COMPLETADA' ? (
                      <button
                        type="button"
                        className="fila-archivo-quitar"
                        aria-label={`Quitar ${e.plantillaNombre}`}
                        title="Quitar procedimiento"
                        disabled={quitar.isPending}
                        onClick={(ev) => {
                          ev.preventDefault();
                          ev.stopPropagation();
                          quitar.mutate(e.id);
                        }}
                      >
                        ×
                      </button>
                    ) : null}
                  </div>

                  <EnlaceRuta to={`/ejecuciones/${e.id}`} style={{ display: 'block' }}>
                    <div className="barra">
                      <div
                        className={`barra-relleno${e.resumen.falla > 0 ? ' falla' : e.resumen.alerta > 0 ? ' alerta' : ''}`}
                        style={{ width: `${avance}%` }}
                      />
                    </div>

                    <div style={{ fontSize: 12, color: 'var(--texto-medio)', marginTop: 6 }}>
                      {respondidos} de {e.resumen.totalVisibles} campos
                      {e.resumen.falla > 0 ? ` · ${e.resumen.falla} falla(s)` : ''}
                      {e.resumen.alerta > 0 ? ` · ${e.resumen.alerta} alerta(s)` : ''}
                      {e.porcentaje ? ` · ${e.porcentaje} %` : ''}
                    </div>
                  </EnlaceRuta>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}


// ─── Sub-órdenes multi-activo ────────────────────────────────────────────────

/** Una persona entra al reparto de sub-órdenes con la misma forma que un activo; `esPersona` la distingue al guardar. */
function personaComoSubOrden(p: SujetoEvaluable): ActivoParaSubOrden {
  return {
    id: p.id,
    numero: 0,
    nombre: p.nombreCompleto,
    ubicacionId: p.ubicacionId,
    ubicacionNombre: p.ubicacion,
    ubicacionRaiz: null,
    activoPadreId: null,
    activoPadreNombre: null,
    esPersona: true,
  };
}

interface ActivoParaSubOrden {
  id: string;
  numero: number;
  nombre: string;
  /** Sub-orden sobre una persona en vez de un activo. */
  esPersona?: boolean;
  ubicacionId: string | null;
  ubicacionNombre: string | null;
  /** Sede raíz de la ubicación (§4.2): al filtrar por una sede completa, cada
   * fila puede caer en una sub-ubicación distinta y sin esto no queda claro
   * que igual pertenece a la sede elegida. */
  ubicacionRaiz: string | null;
  activoPadreId: string | null;
  activoPadreNombre: string | null;
}

/**
 * Datos de una sub-orden que, en la pantalla de creación, todavía no existen
 * como OT (§ pedido: nada se crea hasta pulsar "Crear orden" — si se deja el
 * alta a la mitad, no deben quedar sub-órdenes sueltas en el servidor). Viven
 * solo en el estado de este formulario; "editarla" abre un modal local que
 * lee y escribe este mismo objeto, no una OT real.
 */
interface SubOrdenDetalle {
  descripcion: string;
  ubicacionId: string;
  prioridad: Prioridad;
  tipoTrabajo: TipoTrabajo;
  tiposFalla: string[];
  asignadosUsuarios: string[];
  asignadosGrupos: string[];
  encargadoCategoriaId: string;
  tiempoEstimado: string;
  fechaInicio: string;
  fechaVencimiento: string;
  fotos: AdjuntoImagen[];
}
/** Nombre (y sede raíz) de una ubicación elegida en el modal de sub-orden:
 * ahí solo se guarda el id, así que la tabla necesita resolverlo contra el
 * árbol de ubicaciones para mostrarlo igual que el resto de columnas. */
function resolverUbicacion(
  ubicaciones: NodoUbicacion[] | undefined,
  id: string,
): { nombre: string | null; raiz: string | null } {
  if (!id || !ubicaciones) return { nombre: null, raiz: null };
  const porId = new Map(ubicaciones.map((u) => [u.id, u]));
  const nodo = porId.get(id);
  if (!nodo) return { nombre: null, raiz: null };
  let raiz = nodo;
  while (raiz.padreId) {
    const padre = porId.get(raiz.padreId);
    if (!padre) break;
    raiz = padre;
  }
  return { nombre: nodo.nombre, raiz: raiz.id === nodo.id ? null : raiz.nombre };
}

const subOrdenDetalleVacio: SubOrdenDetalle = {
  descripcion: '',
  ubicacionId: '',
  prioridad: 'NINGUNA',
  tipoTrabajo: 'REACTIVA',
  tiposFalla: [],
  asignadosUsuarios: [],
  asignadosGrupos: [],
  encargadoCategoriaId: '',
  tiempoEstimado: '',
  fechaInicio: '',
  fechaVencimiento: '',
  fotos: [],
};

interface PlantillaOpcion {
  id: string;
  nombre: string;
  cantidadCampos: number;
}

/**
 * Reparte el alcance de una OT en una OT hija por activo (backlog: "puedo
 * seleccionar más de un activo... cada uno con su propio procedimiento... se
 * crean como OT's distintas pero que están dentro de una").
 *
 * La lista de activos se filtra por ubicación (por defecto la de la orden) e
 * incluye tanto activos padre como hijos, igual que ya hace el filtro
 * recursivo por ubicación del listado de activos.
 */
function SubOrdenesDeOrden({
  ordenTrabajoId,
  ubicacionOrden,
}: {
  ordenTrabajoId: string;
  ubicacionOrden: string | null;
}): JSX.Element {
  const cliente = useQueryClient();
  const [creando, setCreando] = useState(false);
  const [ubicacionId, setUbicacionId] = useState(ubicacionOrden ?? '');
  const [seleccion, setSeleccion] = useState<Record<string, string>>({}); // activoId -> plantillaId ('' = ninguno)
  const [plantillaMasiva, setPlantillaMasiva] = useState('');

  const { data: ubicaciones } = useUbicaciones(creando);

  const { data: subOrdenes } = useQuery({
    queryKey: ['ordenes', 'sub-ordenes', ordenTrabajoId],
    queryFn: () =>
      api.get<Pagina<OrdenTrabajo>>(
        `/ordenes-trabajo${consulta({ ordenTrabajoPadreId: ordenTrabajoId, tamano: 100 })}`,
      ),
  });

  const { data: activos } = useQuery({
    queryKey: ['activos', 'para-sub-orden', ubicacionId],
    queryFn: () =>
      api.get<Pagina<ActivoParaSubOrden>>(`/activos${consulta({ ubicacionId, tamano: 100 })}`),
    enabled: creando && Boolean(ubicacionId),
  });

  const { data: plantillas } = useQuery({
    queryKey: ['procedimientos', 'PUBLICADA', ''],
    queryFn: () => api.get<PlantillaOpcion[]>('/procedimientos?estado=PUBLICADA'),
    enabled: creando,
  });

  const crear = useMutation({
    mutationFn: () =>
      api.post<OrdenTrabajo[]>(`/ordenes-trabajo/${ordenTrabajoId}/sub-ordenes`, {
        items: Object.entries(seleccion).map(([activoId, procedimientoPlantillaId]) => ({
          activoId,
          procedimientoPlantillaId: procedimientoPlantillaId || null,
        })),
      }),
    onSuccess: async () => {
      setCreando(false);
      setSeleccion({});
      setPlantillaMasiva('');
      await cliente.invalidateQueries({ queryKey: ['ordenes', 'sub-ordenes', ordenTrabajoId] });
      await cliente.invalidateQueries({ queryKey: ['orden', ordenTrabajoId] });
    },
  });

  function alternar(activoId: string): void {
    setSeleccion((s) => {
      const copia = { ...s };
      if (activoId in copia) delete copia[activoId];
      else copia[activoId] = plantillaMasiva;
      return copia;
    });
  }

  function aplicarPlantillaATodos(): void {
    setSeleccion((s) => Object.fromEntries(Object.keys(s).map((id) => [id, plantillaMasiva])));
  }

  const cantidadSeleccionada = Object.keys(seleccion).length;

  return (
    <section className="panel">
      <div className="panel-cabecera">
        <h2>Sub-órdenes</h2>
        <button type="button" className="boton secundario pequeno" onClick={() => setCreando((v) => !v)}>
          {creando ? 'Cancelar' : 'Crear sub-órdenes'}
        </button>
      </div>

      <div className="panel-cuerpo">
        {crear.isError ? <Aviso tono="error">{(crear.error as Error).message}</Aviso> : null}

        {creando ? (
          <div style={{ marginBottom: 16 }}>
            <SelectorUbicacion
              id="sub-orden-ubicacion"
              valor={ubicacionId}
              alCambiar={(v) => {
                setUbicacionId(v);
                setSeleccion({});
              }}
              ubicaciones={ubicaciones ?? []}
            />

            {ubicacionId ? (
              <>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '12px 0' }}>
                  <div style={{ flex: 1 }}>
                    <SelectorLista
                      id="plantilla-masiva"
                      valor={plantillaMasiva}
                      alCambiar={setPlantillaMasiva}
                      opciones={(plantillas ?? []).map((p) => ({
                        id: p.id,
                        nombre: `${p.nombre} (${p.cantidadCampos} campos)`,
                      }))}
                      textoVacio="Sin procedimiento"
                    />
                  </div>
                  <button
                    type="button"
                    className="boton secundario pequeno"
                    disabled={cantidadSeleccionada === 0}
                    onClick={aplicarPlantillaATodos}
                  >
                    Aplicar a los {cantidadSeleccionada} seleccionados
                  </button>
                </div>

                <div className="lista-checklist" style={{ maxHeight: 320, overflowY: 'auto' }}>
                  {(activos?.datos ?? []).length === 0 ? (
                    <Vacio titulo="Sin activos en esta ubicación" />
                  ) : (
                    activos!.datos.map((a) => (
                      <div
                        key={a.id}
                        style={{
                          display: 'flex',
                          gap: 10,
                          alignItems: 'center',
                          padding: '8px 4px',
                          borderBottom: '1px solid var(--borde)',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={a.id in seleccion}
                          onChange={() => alternar(a.id)}
                          aria-label={`Seleccionar ${a.nombre}`}
                        />
                        <span style={{ flex: 1 }}>
                          {a.nombre}
                          {a.activoPadreNombre ? (
                            <span style={{ color: 'var(--texto-tenue)', fontSize: 12 }}>
                              {' '}
                              · parte de {a.activoPadreNombre}
                            </span>
                          ) : null}
                        </span>
                        {a.id in seleccion ? (
                          <div style={{ minWidth: 200 }}>
                            <SelectorLista
                              id={`plantilla-activo-${a.id}`}
                              valor={seleccion[a.id] ?? ''}
                              alCambiar={(v) => setSeleccion((s) => ({ ...s, [a.id]: v }))}
                              opciones={(plantillas ?? []).map((p) => ({ id: p.id, nombre: p.nombre }))}
                              textoVacio="Sin procedimiento"
                            />
                          </div>
                        ) : null}
                      </div>
                    ))
                  )}
                </div>

                <div style={{ marginTop: 12 }}>
                  <button
                    type="button"
                    className="boton"
                    disabled={cantidadSeleccionada === 0 || crear.isPending}
                    onClick={() => crear.mutate()}
                  >
                    {crear.isPending
                      ? 'Creando…'
                      : `Crear ${cantidadSeleccionada} sub-orden${cantidadSeleccionada === 1 ? '' : 'es'}`}
                  </button>
                </div>
              </>
            ) : null}
          </div>
        ) : null}

        {(subOrdenes?.datos ?? []).length === 0 ? (
          <Vacio titulo="Sin sub-órdenes" detalle="Reparta el alcance en varios activos con «Crear sub-órdenes»." />
        ) : (
          <div className="tabla-contenedor">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Orden de trabajo</th>
                  <th>Ubicación</th>
                  <th>Estado</th>
                  <th>Asignado a</th>
                  <th>Vencimiento</th>
                </tr>
              </thead>
              <tbody>
                {subOrdenes!.datos.map((o) => (
                  <tr key={o.id}>
                    <td>
                      <EnlaceRuta to={`/ordenes/${o.id}`} style={{ fontWeight: 600 }}>
                        #{o.numero} · {o.titulo}
                      </EnlaceRuta>
                      {o.activo ? (
                        <div style={{ color: 'var(--texto-tenue)', fontSize: 12 }}>{o.activo}</div>
                      ) : null}
                    </td>
                    <td>
                      {o.ubicacionRaiz && o.ubicacionRaiz !== o.ubicacion ? (
                        <>
                          <span style={{ color: 'var(--texto-tenue)' }}>{o.ubicacionRaiz} / </span>
                          {o.ubicacion}
                        </>
                      ) : (
                        o.ubicacion ?? '—'
                      )}
                    </td>
                    <td>
                      <EstadoOtEtiqueta estado={o.estado} vencida={o.vencida} />
                    </td>
                    <td>
                      <PilaAsignados asignados={o.asignados} />
                    </td>
                    <td style={o.vencida ? { color: 'var(--falla)', fontWeight: 700 } : undefined}>
                      {o.fechaVencimiento ? formatearFecha(o.fechaVencimiento) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

// ─── Partes consumidas (§6.2, §17.4) ─────────────────────────────────────────

interface ConsumoOrden {
  consumos: {
    id: string;
    parteId: string;
    parteNombre: string;
    ubicacionNombre: string;
    cantidad: number;
    costoUnitarioPen: string;
    costoTotalPen: string;
    fechaHora: string;
  }[];
  ubicacionSugerida: string | null;
}

/**
 * Partes consumidas en la orden.
 *
 * §6.6: la ubicación de origen se propone con la de la OT, pero queda editable —
 * el técnico pudo tomar la pieza del almacén central, y la transacción debe
 * decir de dónde salió de verdad.
 */
function PartesDeOrden({ ordenTrabajoId }: { ordenTrabajoId: string }): JSX.Element {
  const { puede } = useSesion();
  const cliente = useQueryClient();
  const [agregando, setAgregando] = useState(false);
  const [parteId, setParteId] = useState('');
  const [seleccionada, setSeleccionada] = useState<ParteBuscable | null>(null);
  const [ubicacionId, setUbicacionId] = useState('');
  const [cantidad, setCantidad] = useState('1');
  const stockSeleccionado =
    seleccionada?.stock.find((s) => s.ubicacionId === ubicacionId)?.unidadesEnExistencia ?? 0;
  const cantidadExcedeStock = Number(cantidad) > stockSeleccionado;

  const { data } = useQuery({
    queryKey: ['ot-partes', ordenTrabajoId],
    queryFn: () => api.get<ConsumoOrden>(`/ordenes/${ordenTrabajoId}/partes`),
  });

  const consumir = useMutation({
    mutationFn: () =>
      api.post(`/ordenes/${ordenTrabajoId}/partes`, {
        lineas: [{ parteId, ubicacionId, cantidad: Number(cantidad) }],
      }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['ot-partes', ordenTrabajoId] });
      void cliente.invalidateQueries({ queryKey: ['orden-hilo', ordenTrabajoId] });
      setAgregando(false);
      setParteId('');
      setSeleccionada(null);
      setUbicacionId('');
      setCantidad('1');
    },
  });

  const total = (data?.consumos ?? []).reduce((n, c) => n + Number(c.costoTotalPen), 0);

  return (
    <section className="panel">
      <div className="panel-cabecera">
        <h2>Partes consumidas</h2>
        {puede('parte:transaccionar') && !agregando ? (
          <button type="button" className="boton secundario pequeno" onClick={() => setAgregando(true)}>
            Agregar parte
          </button>
        ) : null}
      </div>
      <div className="panel-cuerpo">
        {(data?.consumos ?? []).length === 0 && !agregando ? (
          <Vacio titulo="Sin partes consumidas en esta orden" />
        ) : null}

        {(data?.consumos ?? []).length > 0 ? (
          <div className="tabla-contenedor">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Parte</th>
                  <th>Ubicación</th>
                  <th>Cantidad</th>
                  <th>Costo unitario</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {data!.consumos.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <EnlaceRuta to={`/partes/${c.parteId}`} style={{ fontWeight: 600 }}>
                        {c.parteNombre}
                      </EnlaceRuta>
                    </td>
                    <td>{c.ubicacionNombre}</td>
                    <td>{Math.abs(c.cantidad)}</td>
                    {/* §6.6: el costo quedó congelado al momento del consumo. */}
                    <td>{formatearMoneda(c.costoUnitarioPen)}</td>
                    <td style={{ fontWeight: 700 }}>{formatearMoneda(c.costoTotalPen)}</td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={4} style={{ textAlign: 'right', fontWeight: 700 }}>
                    Total en partes
                  </td>
                  <td style={{ fontWeight: 800 }}>{formatearMoneda(total)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : null}

        {agregando ? (
          <div style={{ marginTop: 14, borderTop: '1px solid var(--borde)', paddingTop: 14 }}>
            <SelectorParte
              id="consumo-parte"
              valor={parteId}
              alElegir={(p) => {
                setParteId(p?.id ?? '');
                setSeleccionada(p);
                // La ubicación de la OT si la parte existe ahí; si no, la
                // primera con existencias, que suele ser el almacén central.
                const sugerida = p?.stock.find((s) => s.ubicacionId === data?.ubicacionSugerida);
                const conStock = p?.stock.find((s) => s.unidadesEnExistencia > 0);
                setUbicacionId(sugerida?.ubicacionId ?? conStock?.ubicacionId ?? '');
              }}
            />

            {seleccionada ? (
              <div className="rejilla-campos">
                <SelectorLista
                  id="consumo-ubicacion"
                  etiqueta="Tomar de"
                  valor={ubicacionId}
                  alCambiar={setUbicacionId}
                  opciones={seleccionada.stock.map((s) => ({
                    id: s.ubicacionId,
                    nombre: `${s.ubicacionNombre} · ${s.unidadesEnExistencia} disponible(s)${
                      s.ubicacionId === data?.ubicacionSugerida ? ' — ubicación de la OT' : ''
                    }`,
                  }))}
                  textoVacio="Elija de dónde sale…"
                  requerido
                />
                <div className="campo">
                  <label htmlFor="consumo-cantidad">Cantidad</label>
                  <input
                    id="consumo-cantidad"
                    type="number"
                    min={1}
                    max={stockSeleccionado}
                    value={cantidad}
                    onChange={(e) => setCantidad(e.target.value.replace(/\D/g, ''))}
                  />
                  {ubicacionId && cantidadExcedeStock ? (
                    <span className="campo-nota">Solo hay {stockSeleccionado} disponible(s) en esa ubicación.</span>
                  ) : null}
                </div>
              </div>
            ) : null}

            {consumir.isError ? <Aviso tono="error">{(consumir.error as Error).message}</Aviso> : null}

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="boton"
                disabled={!parteId || !ubicacionId || Number(cantidad) < 1 || cantidadExcedeStock || consumir.isPending}
                onClick={() => consumir.mutate()}
              >
                {consumir.isPending ? 'Registrando…' : 'Registrar consumo'}
              </button>
              <button type="button" className="boton secundario" onClick={() => setAgregando(false)}>
                Cancelar
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}

// ─── Alta y edición de la orden (§10.3) ──────────────────────────────────────

/** Un `Date` de la API en el formato que espera `<input type="date">`. */
function comoFechaDeCampo(valor: string | null): string {
  return valor ? valor.slice(0, 10) : '';
}

/**
 * Tiempo estimado como un solo campo `<input type="time">` (§ pedido: "09:30"
 * en vez de dos campos sueltos de horas y minutos). Se sigue mandando al
 * servidor como minutos totales; esto solo cambia cómo se edita.
 */
function comoTextoDeDuracion(minutos: number | null): string {
  if (minutos == null) return '';
  const horas = Math.floor(minutos / 60);
  const mins = minutos % 60;
  return `${String(horas).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}

function comoMinutosDeDuracion(texto: string): number | null {
  if (!texto) return null;
  const partes = texto.split(':').map((n) => parseInt(n, 10));
  const horas = partes[0] ?? NaN;
  const mins = partes[1] ?? NaN;
  if (Number.isNaN(horas) && Number.isNaN(mins)) return null;
  return (Number.isNaN(horas) ? 0 : horas) * 60 + (Number.isNaN(mins) ? 0 : mins);
}

/**
 * §16.3: crear una OT a partir de una observación pendiente en el módulo de
 * alertas de activos pasa el activo y un título sugerido por query string,
 * para no obligar a repetir a mano lo que ya se sabía en la alerta.
 *
 * §9.2: aprobar una solicitud abre esta misma alta en vez de generar la OT
 * reactiva a ciegas, con el contexto de la solicitud ya cargado y con
 * `solicitudId` para que "Guardar" apruebe la solicitud en vez de crear una
 * OT suelta.
 */
export function NuevaOrden(): JSX.Element {
  const [parametros] = useSearchParams();
  return (
    <FormularioOrden
      orden={null}
      prellenado={{
        activoId: parametros.get('activoId') ?? '',
        titulo: parametros.get('titulo') ?? '',
        descripcion: parametros.get('descripcion') ?? '',
        ubicacionId: parametros.get('ubicacionId') ?? '',
        prioridad: (parametros.get('prioridad') as Prioridad | null) ?? undefined,
        solicitudId: parametros.get('solicitudId') ?? undefined,
        observacionId: parametros.get('observacionId') ?? undefined,
        sujetoEvaluableId: parametros.get('sujetoEvaluableId') ?? undefined,
      }}
    />
  );
}

export function EditarOrden(): JSX.Element {
  const { id = '' } = useParams();

  const { data: orden, isLoading } = useQuery({
    queryKey: ['orden', id],
    queryFn: () => api.get<OrdenTrabajo>(`/ordenes-trabajo/${id}`),
    enabled: Boolean(id),
  });

  if (isLoading) return <Cargando />;
  if (!orden) return <Vacio titulo="La orden de trabajo no existe" />;
  if (orden.estado === 'COMPLETA') {
    return (
      <>
        <BotonVolver a={`/ordenes/${orden.id}`} etiqueta="Volver a la orden" />
        <Aviso tono="atencion">
          Esta orden está completada, así que no se puede editar. Si hay que corregirla, reábrala desde su ficha.
        </Aviso>
      </>
    );
  }
  return <FormularioOrden orden={orden} />;
}

/**
 * Modal de selección de activos para sub-órdenes de trabajo, dentro del alta
 * de una OT. El modal solo decide QUÉ activos entran; el procedimiento de
 * cada uno se asigna después, ya en la tabla (en masa o fila por fila).
 */
function ModalSeleccionarActivosSubOrden({
  ubicaciones,
  tiposActivo,
  excluirIds,
  alConfirmar,
  alCerrar,
  alContinuarSinActivo,
}: {
  ubicaciones: NodoUbicacion[] | undefined;
  tiposActivo: { id: string; nombre: string }[] | undefined;
  excluirIds: string[];
  alConfirmar: (activos: ActivoParaSubOrden[]) => void;
  alCerrar: () => void;
  /** Cierra el modal y pide el nombre del activo momentáneo afuera, en la
   * propia tabla de sub-órdenes (§ pedido: no adentro de este diálogo). */
  alContinuarSinActivo: () => void;
}): JSX.Element {
  const [tipo, setTipo] = useState<'ACTIVO' | 'PERSONA'>('ACTIVO');
  const [buscar, setBuscar] = useState('');
  const [ubicacionId, setUbicacionId] = useState('');
  const [tipoActivoIds, setTipoActivoIds] = useState<string[]>([]);
  const [marcados, setMarcados] = useState<Record<string, ActivoParaSubOrden>>({});

  const { data: activos, isLoading: cargandoActivos } = useQuery({
    queryKey: ['activos', 'modal-sub-orden', buscar, ubicacionId, tipoActivoIds],
    queryFn: () =>
      api.get<Pagina<ActivoParaSubOrden>>(
        `/activos${consulta({ buscar, ubicacionId, tipoActivoId: tipoActivoIds, tamano: 100 })}`,
      ),
    enabled: tipo === 'ACTIVO',
  });

  const { data: personas, isLoading: cargandoPersonas } = useQuery({
    queryKey: ['personas', 'modal-sub-orden', buscar, ubicacionId],
    queryFn: () =>
      api.get<Pagina<SujetoEvaluable>>(
        `/sujetos-evaluables${consulta({ buscar, ubicacionId, activo: 'true', tamano: 100 })}`,
      ),
    enabled: tipo === 'PERSONA',
  });

  const isLoading = tipo === 'ACTIVO' ? cargandoActivos : cargandoPersonas;
  const disponibles: ActivoParaSubOrden[] =
    tipo === 'ACTIVO'
      ? (activos?.datos ?? []).filter((a) => !excluirIds.includes(a.id))
      : (personas?.datos ?? []).filter((p) => !excluirIds.includes(p.id)).map(personaComoSubOrden);
  const nombreSobre = tipo === 'ACTIVO' ? 'activos' : 'personas';
  const cantidadMarcados = Object.keys(marcados).length;
  const hayFiltro = buscar.trim() !== '' || ubicacionId !== '' || tipoActivoIds.length > 0;
  const todosMarcados = disponibles.length > 0 && disponibles.every((a) => a.id in marcados);

  function alternar(a: ActivoParaSubOrden): void {
    setMarcados((m) => {
      const copia = { ...m };
      if (a.id in copia) delete copia[a.id];
      else copia[a.id] = a;
      return copia;
    });
  }

  function alternarTodos(): void {
    setMarcados((m) => {
      if (todosMarcados) {
        const copia = { ...m };
        for (const a of disponibles) delete copia[a.id];
        return copia;
      }
      const copia = { ...m };
      for (const a of disponibles) copia[a.id] = a;
      return copia;
    });
  }

  return (
    <Dialogo titulo={`Seleccione ${nombreSobre} para sub-órdenes de trabajo`} ancho={720} alCerrar={alCerrar}>
      <Aviso tono="info">Se crearán sub-órdenes de trabajo para cada {tipo === 'ACTIVO' ? 'activo' : 'persona'} elegido.</Aviso>

      <div className="acciones-cabecera" role="group" aria-label="Sobre" style={{ marginBottom: 12, gap: 8 }}>
        <span className="campo-nota">Sobre:</span>
        <button
          type="button"
          className={`boton pequeno${tipo === 'ACTIVO' ? '' : ' secundario'}`}
          aria-pressed={tipo === 'ACTIVO'}
          onClick={() => setTipo('ACTIVO')}
        >
          Un activo
        </button>
        <button
          type="button"
          className={`boton pequeno${tipo === 'PERSONA' ? '' : ' secundario'}`}
          aria-pressed={tipo === 'PERSONA'}
          onClick={() => setTipo('PERSONA')}
        >
          Una persona
        </button>
      </div>

      <div className="campo">
        <input
          value={buscar}
          onChange={(e) => setBuscar(e.target.value)}
          placeholder={tipo === 'ACTIVO' ? 'Buscar activo…' : 'Buscar persona por nombre…'}
        />
      </div>

      <div className="rejilla-campos" style={{ marginBottom: 12 }}>
        <SelectorUbicacion
          id="modal-suborden-ubicacion"
          etiqueta="Ubicación"
          valor={ubicacionId}
          alCambiar={setUbicacionId}
          ubicaciones={ubicaciones}
        />
        {tipo === 'ACTIVO' ? (
          <SelectorListaMultiple
            id="modal-suborden-tipo"
            etiqueta="Tipos de activo"
            seleccion={tipoActivoIds}
            alCambiar={setTipoActivoIds}
            opciones={(tiposActivo ?? []).map((t) => ({ id: t.id, nombre: t.nombre }))}
            textoVacio="Todos"
          />
        ) : null}
      </div>

      {ubicacionId ? (
        <p style={{ marginTop: 0, marginBottom: 10, fontSize: 12.5, color: 'var(--texto-medio)' }}>
          Se muestran las {nombreSobre} de <strong>{ubicaciones?.find((u) => u.id === ubicacionId)?.nombre}</strong> y de
          sus sub-ubicaciones.
        </p>
      ) : null}

      <div className="tabla-contenedor" style={{ maxHeight: 380, overflowY: 'auto', marginBottom: 14 }}>
        {isLoading ? (
          <Cargando />
        ) : disponibles.length === 0 ? (
          <Vacio titulo={`Sin ${nombreSobre} que coincidan`} />
        ) : (
          <table className="tabla">
            <thead>
              <tr>
                <th style={{ width: 32 }}>
                  {hayFiltro ? (
                    <input
                      type="checkbox"
                      checked={todosMarcados}
                      onChange={alternarTodos}
                      aria-label="Seleccionar todos los activos filtrados"
                      title="Seleccionar todos"
                    />
                  ) : null}
                </th>
                <th>{tipo === 'ACTIVO' ? 'Activo' : 'Persona'}</th>
                <th>Ubicación</th>
              </tr>
            </thead>
            <tbody>
              {disponibles.map((a) => (
                <tr
                  key={a.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => alternar(a)}
                >
                  <td>
                    <input
                      type="checkbox"
                      checked={a.id in marcados}
                      onChange={() => alternar(a)}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Seleccionar ${a.nombre}`}
                    />
                  </td>
                  <td>
                    {a.nombre}
                    {a.activoPadreNombre ? (
                      <span style={{ color: 'var(--texto-tenue)', fontSize: 12 }}> · parte de {a.activoPadreNombre}</span>
                    ) : null}
                  </td>
                  <td>
                    {a.ubicacionRaiz && a.ubicacionRaiz !== a.ubicacionNombre ? (
                      <>
                        <span style={{ color: 'var(--texto-tenue)' }}>{a.ubicacionRaiz} / </span>
                        {a.ubicacionNombre}
                      </>
                    ) : (
                      a.ubicacionNombre ?? '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
          <button type="button" className="boton-enlace" onClick={alContinuarSinActivo}>
            Continuar sin activo
          </button>
          {cantidadMarcados > 0 ? (
            <span style={{ fontSize: 12, color: 'var(--texto-medio)' }}>
              {cantidadMarcados} seleccionado{cantidadMarcados === 1 ? '' : 's'}{' '}
              <button type="button" className="boton-enlace" onClick={() => setMarcados({})}>
                Limpiar
              </button>
            </span>
          ) : null}
        </div>
        <button
          type="button"
          className="boton"
          disabled={cantidadMarcados === 0}
          onClick={() => alConfirmar(Object.values(marcados))}
        >
          Crear sub-órdenes de trabajo
        </button>
      </div>
    </Dialogo>
  );
}

/**
 * Edición local de una sub-orden en el formulario de creación (§ pedido): no
 * llama al servidor — guarda en el estado del formulario y recién se envía
 * junto con el resto de la sub-orden al pulsar "Crear orden" más abajo.
 */
function DialogoEditarSubOrden({
  activo,
  detalle,
  ubicaciones,
  usuarios,
  grupos,
  fallas,
  alGuardar,
  alCerrar,
}: {
  activo: ActivoParaSubOrden;
  detalle: SubOrdenDetalle;
  ubicaciones: NodoUbicacion[] | undefined;
  usuarios: { id: string; nombre: string }[] | undefined;
  grupos: { id: string; nombre: string }[] | undefined;
  fallas: { id: string; nombre: string; activo: boolean; familia: string | null }[] | undefined;
  alGuardar: (detalle: SubOrdenDetalle) => void;
  alCerrar: () => void;
}): JSX.Element {
  const [descripcion, setDescripcion] = useState(detalle.descripcion);
  const [ubicacionId, setUbicacionId] = useState(detalle.ubicacionId);
  const [prioridad, setPrioridad] = useState<Prioridad>(detalle.prioridad);
  const [tipoTrabajo, setTipoTrabajo] = useState<TipoTrabajo>(detalle.tipoTrabajo);
  const [tiposFalla, setTiposFalla] = useState(detalle.tiposFalla);
  const [asignadosUsuarios, setAsignadosUsuarios] = useState(detalle.asignadosUsuarios);
  const [asignadosGrupos, setAsignadosGrupos] = useState(detalle.asignadosGrupos);
  const [encargadoCategoriaId, setEncargadoCategoriaId] = useState(detalle.encargadoCategoriaId);
  const [tiempoEstimado, setTiempoEstimado] = useState(detalle.tiempoEstimado);
  const [fechaInicio, setFechaInicio] = useState(detalle.fechaInicio);
  const [fechaVencimiento, setFechaVencimiento] = useState(detalle.fechaVencimiento);
  const [fotos, setFotos] = useState(detalle.fotos);

  return (
    <Dialogo titulo={`Editar sub-orden de trabajo — ${activo.nombre}`} ancho={620} alCerrar={alCerrar}>
      <SubidaImagenes etiqueta="Imágenes" valor={fotos} alCambiar={setFotos} />

      <div className="campo">
        <label htmlFor="suborden-descripcion">Descripción</label>
        <textarea
          id="suborden-descripcion"
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          maxLength={8000}
          placeholder="Agregue una descripción"
        />
      </div>

      <SelectorUbicacion
        id="suborden-ubicacion"
        etiqueta="Ubicación"
        valor={ubicacionId}
        alCambiar={setUbicacionId}
        ubicaciones={ubicaciones}
      />

      <SelectorSegmentado
        id="suborden-prioridad"
        etiqueta="Prioridad"
        valor={prioridad}
        alCambiar={(v) => setPrioridad(v as Prioridad)}
        opciones={PRIORIDADES.map((p) => ({ id: p, nombre: ETIQUETA_PRIORIDAD[p] }))}
      />

      <div className="rejilla-campos" style={{ marginTop: 14 }}>
        <SelectorLista
          id="suborden-tipo-trabajo"
          etiqueta="Tipo de trabajo"
          valor={tipoTrabajo}
          alCambiar={(v) => setTipoTrabajo(v as TipoTrabajo)}
          opciones={TIPOS_TRABAJO.map((t) => ({ id: t, nombre: ETIQUETA_TIPO_TRABAJO[t] }))}
        />
        <SelectorCatalogo
          id="suborden-encargado"
          codigo="CATEGORIA_ENCARGADO"
          etiqueta="Encargado"
          valor={encargadoCategoriaId}
          alCambiar={setEncargadoCategoriaId}
        />
      </div>

      <div className="rejilla-campos" style={{ marginTop: 14 }}>
        <SelectorFecha id="suborden-inicio" etiqueta="Fecha de inicio" valor={fechaInicio} alCambiar={setFechaInicio} />
        <SelectorFecha
          id="suborden-vencimiento"
          etiqueta="Fecha de vencimiento"
          valor={fechaVencimiento}
          alCambiar={setFechaVencimiento}
        />
        <div className="campo">
          <label htmlFor="suborden-tiempo-estimado">Tiempo estimado</label>
          <input
            id="suborden-tiempo-estimado"
            type="time"
            value={tiempoEstimado}
            onChange={(e) => setTiempoEstimado(e.target.value)}
          />
        </div>
      </div>

      <div className="rejilla-campos" style={{ marginTop: 14 }}>
        <SeleccionMultiple
          etiqueta="Asignar a grupos"
          opciones={(grupos ?? []).map((g) => ({ id: g.id, nombre: g.nombre }))}
          seleccion={asignadosGrupos}
          alCambiar={setAsignadosGrupos}
          vacio="Aún no hay grupos."
        />
        <SeleccionMultiple
          etiqueta="Asignar a usuarios"
          opciones={(usuarios ?? []).map((u) => ({ id: u.id, nombre: u.nombre }))}
          seleccion={asignadosUsuarios}
          alCambiar={setAsignadosUsuarios}
          vacio="No hay usuarios activos."
        />
      </div>

      <div style={{ marginTop: 14 }}>
        <SeleccionMultiple
          etiqueta="Tipos de falla"
          opciones={(fallas ?? [])
            .filter((f) => f.activo || tiposFalla.includes(f.id))
            .map((f) => ({ id: f.id, nombre: f.nombre, nota: f.familia ?? undefined }))}
          seleccion={tiposFalla}
          alCambiar={setTiposFalla}
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <button type="button" className="boton secundario" onClick={alCerrar}>
          Cancelar
        </button>
        <button
          type="button"
          className="boton"
          onClick={() =>
            alGuardar({
              descripcion,
              ubicacionId,
              prioridad,
              tipoTrabajo,
              tiposFalla,
              asignadosUsuarios,
              asignadosGrupos,
              encargadoCategoriaId,
              tiempoEstimado,
              fechaInicio,
              fechaVencimiento,
              fotos,
            })
          }
        >
          Guardar sub-orden de trabajo
        </button>
      </div>
    </Dialogo>
  );
}

/**
 * Formulario de la orden de trabajo.
 *
 * Es pantalla completa y no diálogo: la orden tiene ubicación, activo, tipos de
 * falla y asignaciones, y encajar todo eso en una ventana flotante obliga a
 * desplazar dentro de un modal, que en el móvil es el peor de los dos mundos.
 */
function FormularioOrden({
  orden,
  prellenado,
}: {
  orden: OrdenTrabajo | null;
  prellenado?: {
    activoId?: string;
    titulo?: string;
    descripcion?: string;
    ubicacionId?: string;
    prioridad?: Prioridad;
    /** §9.2: presente cuando el alta viene de aprobar una solicitud. */
    solicitudId?: string;
    /** Alta para una persona (ficha de personal, §12.7). */
    sujetoEvaluableId?: string;
    /** §16.3: presente cuando el alta viene de "Crear OT" en una observación de activo o persona. */
    observacionId?: string;
  };
}): JSX.Element {
  const navegar = useNavigate();
  const cliente = useQueryClient();
  const { puede } = useSesion();

  const [titulo, setTitulo] = useState(orden?.titulo ?? prellenado?.titulo ?? '');
  const [descripcion, setDescripcion] = useState(orden?.descripcion ?? prellenado?.descripcion ?? '');
  const [prioridad, setPrioridad] = useState<Prioridad>(orden?.prioridad ?? prellenado?.prioridad ?? 'NINGUNA');
  // Al crear, arranca sin elegir (§ pedido): recién al elegir activo y tipo de
  // trabajo se sabe qué lista de materiales previsualizar, así que no hay un
  // valor por defecto razonable (antes "Reactiva" se daba por sentado sin que
  // nadie lo mirara, y la lista de Preventivo quedaba sin usarse nunca).
  const [tipoTrabajo, setTipoTrabajo] = useState<TipoTrabajo | ''>(orden?.tipoTrabajo ?? '');
  const [ubicacionId, setUbicacionId] = useState(orden?.ubicacionId ?? prellenado?.ubicacionId ?? '');
  const [activoId, setActivoId] = useState(orden?.activoId ?? prellenado?.activoId ?? '');
  // Materiales/herramientas desmarcados del checklist previsualizado al crear
  // (§ pedido): por defecto todo lo de la lista se lleva, y acá se guarda solo
  // lo que el creador decidió que no hace falta.
  const [materialesExcluidos, setMaterialesExcluidos] = useState<Set<string>>(new Set());
  // Cambiar de activo invalida la lista previsualizada (es por tipo de
  // activo): se vuelve a elegir el tipo de trabajo para que se cargue la que
  // corresponde, en vez de arrastrar la selección de un activo distinto.
  function cambiarActivo(nuevoId: string): void {
    setActivoId(nuevoId);
    if (esCreacion) {
      setTipoTrabajo('');
      setMaterialesExcluidos(new Set());
    }
  }
  // La OT es sobre un equipo (activo) o sobre una persona (sujeto evaluable), nunca ambos.
  const [sobre, setSobre] = useState<'ACTIVO' | 'PERSONA'>(
    orden?.sujetoEvaluableId || prellenado?.sujetoEvaluableId ? 'PERSONA' : 'ACTIVO',
  );
  const [sujetoId, setSujetoId] = useState(orden?.sujetoEvaluableId ?? prellenado?.sujetoEvaluableId ?? '');
  // Al crear una OT sobre un activo, el tipo de trabajo es obligatorio y se
  // ofrecen los tipos que tienen lista propia para ese equipo. Con solo ubicación
  // (o sobre una persona) es opcional: el servidor le pone el valor por defecto.
  const tipoObligatorio = sobre === 'ACTIVO' && activoId !== '';
  const restringeTipoTrabajo = orden === null && tipoObligatorio;
  const { data: trabajosDelActivo, isLoading: cargandoTrabajos } = useQuery({
    queryKey: ['trabajos-de-activo', activoId],
    queryFn: () => api.get<TipoTrabajo[]>(`/listas-materiales/trabajos-de-activo/${activoId}`),
    enabled: restringeTipoTrabajo && activoId !== '',
  });
  const opcionesTipoTrabajo =
    restringeTipoTrabajo && trabajosDelActivo && trabajosDelActivo.length > 0 ? trabajosDelActivo : TIPOS_TRABAJO;
  const { puede: puedeSesion } = useSesion();
  const [ubicacionPersonaId, setUbicacionPersonaId] = useState('');
  const { data: personaElegida } = useQuery({
    queryKey: ['persona-ot', sujetoId],
    queryFn: () => api.get<SujetoEvaluable>(`/sujetos-evaluables/${sujetoId}`),
    enabled: puedeSesion('sujeto_evaluable:leer') && sujetoId !== '',
  });

  // Una OT que viene de la observación de un activo trae solo el activo: la
  // ubicación se toma del propio activo, para que el selector de sede y el de
  // activo muestren lo mismo que el activo tiene registrado.
  const { data: activoPrellenado } = useQuery({
    queryKey: ['activo', prellenado?.activoId],
    queryFn: () => api.get<{ ubicacionId: string | null }>(`/activos/${prellenado?.activoId}`),
    enabled: orden == null && Boolean(prellenado?.activoId) && !prellenado?.ubicacionId,
  });
  // Solo al llegar el activo: si después el usuario borra la sede, no se rellena de nuevo.
  useEffect(() => {
    const ubicacionDelActivo = activoPrellenado?.ubicacionId;
    if (orden == null && ubicacionDelActivo) {
      setUbicacionId((actual) => actual || ubicacionDelActivo);
    }
  }, [activoPrellenado, orden]);

  const [tiposFalla, setTiposFalla] = useState<string[]>(orden?.tiposFalla.map((t) => t.id) ?? []);
  const [asignadosUsuarios, setAsignadosUsuarios] = useState<string[]>(
    orden?.asignados.filter((a) => a.tipo === 'USUARIO').map((a) => a.id) ?? [],
  );
  const [asignadosGrupos, setAsignadosGrupos] = useState<string[]>(
    orden?.asignados.filter((a) => a.tipo === 'GRUPO').map((a) => a.id) ?? [],
  );
  const [encargadoCategoriaId, setEncargadoCategoriaId] = useState(orden?.encargadoCategoriaId ?? '');
  // Un solo campo "HH:MM" (§ pedido): se combina a minutos recién al guardar.
  const [tiempoEstimado, setTiempoEstimado] = useState(comoTextoDeDuracion(orden?.tiempoEstimadoMinutos ?? null));
  const [fechaInicio, setFechaInicio] = useState(comoFechaDeCampo(orden?.fechaInicio ?? null));
  const [fechaVencimiento, setFechaVencimiento] = useState(comoFechaDeCampo(orden?.fechaVencimiento ?? null));
  const [recurrencia, setRecurrencia] = useState<Recurrencia>(orden?.recurrencia ?? 'NO_SE_REPITE');
  const [esPlantillaRecurrente, setEsPlantillaRecurrente] = useState(orden?.esPlantillaRecurrente ?? false);
  const [recurrenciaActiva, setRecurrenciaActiva] = useState(orden?.recurrenciaActiva ?? true);
  const [fotos, setFotos] = useState<AdjuntoImagen[]>([]);
  const [archivos, setArchivos] = useState<AdjuntoImagen[]>([]);
  // Las fotos/archivos existentes llegan async (adjuntos de la OT o de la
  // solicitud que se aprueba) y pisan `fotos`/`archivos` recién montado el
  // formulario: sin esto, ese relleno inicial se leía como si el usuario
  // hubiera tocado algo y la barra de acciones avisaba «cambios sin guardar»
  // sin que existiera ninguno todavía.
  const omitirProximoCambioRef = useRef(false);
  // Nombre del activo fijado al escanear un QR y un contador para remontar el
  // `SelectorActivo` con ese nombre (su `nombreActual` solo se lee al montar).
  const [activoEscaneado, setActivoEscaneado] = useState<string | null>(null);
  const [escaneoSeq, setEscaneoSeq] = useState(0);

  // Procedimiento, sub-órdenes y partes: solo se eligen al crear (§ pedido del
  // usuario), en una sola pasada junto con el resto del formulario — no
  // dependen de que la OT ya exista, porque todavía no existe.
  const esCreacion = orden === null;
  const [procedimientoPlantillaId, setProcedimientoPlantillaId] = useState('');
  // Sub-órdenes: el modal solo decide QUÉ activos entran; el procedimiento de
  // cada uno (en masa o por fila) se ajusta después, ya en la tabla.
  const [subOrdenModalAbierto, setSubOrdenModalAbierto] = useState(false);
  const [subOrdenActivos, setSubOrdenActivos] = useState<ActivoParaSubOrden[]>([]);
  const [subOrdenProcedimientos, setSubOrdenProcedimientos] = useState<Record<string, string>>({});
  const [subOrdenFilasMarcadas, setSubOrdenFilasMarcadas] = useState<string[]>([]);
  const [subOrdenPlantillaMasiva, setSubOrdenPlantillaMasiva] = useState('');
  // El resto de los campos de cada sub-orden (descripción, asignados,
  // encargado, tiempo estimado, vencimiento, fotos) se editan en un modal
  // local que lee/escribe este mapa — ver `SubOrdenDetalle` arriba.
  const [subOrdenDetalles, setSubOrdenDetalles] = useState<Record<string, SubOrdenDetalle>>({});
  const [subOrdenEditandoId, setSubOrdenEditandoId] = useState<string | null>(null);
  // Activo momentáneo de sub-orden (§ pedido): "Continuar sin activo" cierra
  // el modal de selección y pide el título acá, en la propia tabla — no
  // adentro del diálogo.
  const [nombreMomentaneoSubOrden, setNombreMomentaneoSubOrden] = useState<string | null>(null);
  const crearMomentaneoSubOrden = useMutation({
    mutationFn: (nombre: string) =>
      api.post<ActivoParaSubOrden>('/activos/momentaneo', {
        nombre: nombre.trim(),
        ubicacionId: ubicacionId || null,
      }),
    onSuccess: (activo) => {
      setSubOrdenActivos((s) => [...s, activo]);
      // Se deja abierto, listo para el siguiente título (§ pedido: mismo
      // flujo que la referencia — "Pulse intro para guardar" uno tras otro,
      // sin reabrir el modal cada vez). Solo "Cancelar" lo cierra.
      setNombreMomentaneoSubOrden('');
    },
  });
  const [partesSeleccionadas, setPartesSeleccionadas] = useState<
    {
      parteId: string;
      nombre: string;
      ubicacionId: string;
      ubicacionNombre: string;
      cantidad: number;
      costoUnitarioPen: string;
    }[]
  >([]);
  const haySubOrdenes = subOrdenActivos.length > 0;

  // Repartida en sub-órdenes, la OT padre no tiene ubicación/activo,
  // procedimiento, tiempo estimado ni partes propios — cada hija tiene los
  // suyos — así que se limpia lo que se hubiera elegido para el padre en
  // cuanto aparece la primera sub-orden.
  useEffect(() => {
    if (haySubOrdenes) {
      setUbicacionId('');
      setActivoId('');
      setActivoEscaneado(null);
      setProcedimientoPlantillaId('');
      setTiempoEstimado('');
      setPartesSeleccionadas([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [haySubOrdenes]);

  const { data: ubicaciones } = useUbicaciones();
  const { data: fallas } = useTiposFalla();
  const { data: usuarios } = useUsuariosOpciones();
  const { data: grupos } = useGruposOpciones();
  const { data: tiposActivo } = useTiposActivo(esCreacion);

  // Alta al vuelo de un tipo de falla (§ pedido): sin salir del formulario de
  // OT. Si el nombre es parecido a uno ya existente, el servidor lo rechaza
  // con un aviso para confirmar — a diferencia del alta completa (Catálogos),
  // acá SÍ hay que pedirle al usuario que confirme explícitamente en vez de
  // reintentar solo, o la detección de duplicados del servidor no sirve de
  // nada en el punto de entrada más usado (§ bug reportado).
  const [tipoFallaSimilar, setTipoFallaSimilar] = useState<{ nombre: string; mensaje: string } | null>(null);
  const crearTipoFalla = useMutation({
    mutationFn: async (nombre: string) => {
      try {
        return await api.post<{ id: string; nombre: string }>('/tipos-falla', { nombre });
      } catch (error) {
        if (error instanceof ErrorApi && error.codigo === CODIGOS_ERROR.TIPO_FALLA_SIMILAR) {
          setTipoFallaSimilar({ nombre, mensaje: error.message });
          return null;
        }
        throw error;
      }
    },
    onSuccess: async (nuevo) => {
      if (!nuevo) return;
      await cliente.invalidateQueries({ queryKey: ['tipos-falla'] });
      setTiposFalla((previos) => [...previos, nuevo.id]);
    },
  });
  const confirmarTipoFallaSimilar = useMutation({
    mutationFn: () =>
      api.post<{ id: string; nombre: string }>('/tipos-falla', {
        nombre: tipoFallaSimilar!.nombre,
        confirmarSimilar: true,
      }),
    onSuccess: async (nuevo) => {
      setTipoFallaSimilar(null);
      await cliente.invalidateQueries({ queryKey: ['tipos-falla'] });
      setTiposFalla((previos) => [...previos, nuevo.id]);
    },
  });

  const { data: plantillasPublicadas } = useQuery({
    queryKey: ['procedimientos', 'PUBLICADA', ''],
    queryFn: () => api.get<PlantillaOpcion[]>('/procedimientos?estado=PUBLICADA'),
    enabled: esCreacion,
  });

  // Plantillas relacionadas a los tipos de los activos de la tabla: se listan
  // primero en los selectores de procedimiento de las sub-órdenes.
  const idsActivosSub = subOrdenActivos.filter((a) => !a.esPersona).map((a) => a.id).join(',');
  const { data: sugeridasSub } = useQuery({
    queryKey: ['procedimientos', 'sugeridas', idsActivosSub],
    queryFn: () => api.get<{ id: string; nombre: string }[]>(`/procedimientos/sugeridas?activoIds=${idsActivosSub}`),
    enabled: esCreacion && idsActivosSub.length > 0,
  });

  /** Opciones de un selector de procedimiento de sub-orden: sugeridas primero, luego el resto. */
  function opcionesProcedimientoSub(conCampos: boolean): {
    opciones?: { id: string; nombre: string }[];
    grupos?: { etiqueta: string; opciones: { id: string; nombre: string }[] }[];
  } {
    const etiqueta = (p: PlantillaOpcion | { id: string; nombre: string }): string =>
      conCampos && 'cantidadCampos' in p ? `${p.nombre} (${p.cantidadCampos} campos)` : p.nombre;
    const sugeridas = sugeridasSub ?? [];
    if (sugeridas.length === 0) {
      return { opciones: (plantillasPublicadas ?? []).map((p) => ({ id: p.id, nombre: etiqueta(p) })) };
    }
    const idsSugeridas = new Set(sugeridas.map((s) => s.id));
    return {
      grupos: [
        { etiqueta: 'Sugeridos para estos activos', opciones: sugeridas.map((s) => ({ id: s.id, nombre: s.nombre })) },
        {
          etiqueta: 'Todos los procedimientos',
          opciones: (plantillasPublicadas ?? [])
            .filter((p) => !idsSugeridas.has(p.id))
            .map((p) => ({ id: p.id, nombre: etiqueta(p) })),
        },
      ],
    };
  }

  const [parteEnCursoId, setParteEnCursoId] = useState('');
  const [parteEnCurso, setParteEnCurso] = useState<ParteBuscable | null>(null);
  const [ubicacionParteEnCurso, setUbicacionParteEnCurso] = useState('');
  const [cantidadParteEnCurso, setCantidadParteEnCurso] = useState('1');
  const stockParteEnCurso =
    parteEnCurso?.stock.find((s) => s.ubicacionId === ubicacionParteEnCurso)?.unidadesEnExistencia ?? 0;
  const cantidadExcedeStockParte = Number(cantidadParteEnCurso) > stockParteEnCurso;

  // Al editar, las fotos ya vinculadas a la OT se cargan una sola vez: si el
  // usuario las quita durante la edición, no deben reaparecer al refrescar.
  const { data: adjuntosExistentes } = useQuery({
    queryKey: ['adjuntos', 'ORDEN_TRABAJO', orden?.id],
    queryFn: () => api.get<AdjuntoImagen[]>(`/adjuntos${consulta({ entidad: 'ORDEN_TRABAJO', entidadId: orden?.id })}`),
    enabled: orden != null,
  });
  // §9.2: aprobar una solicitud carga sus fotos como punto de partida, igual
  // que al editar una OT ya creada.
  const { data: adjuntosSolicitud } = useQuery({
    queryKey: ['adjuntos', 'SOLICITUD', prellenado?.solicitudId],
    queryFn: () =>
      api.get<AdjuntoImagen[]>(`/adjuntos${consulta({ entidad: 'SOLICITUD', entidadId: prellenado?.solicitudId })}`),
    enabled: orden == null && Boolean(prellenado?.solicitudId),
  });
  useEffect(() => {
    if (adjuntosSolicitud) {
      omitirProximoCambioRef.current = true;
      setFotos(adjuntosSolicitud.filter((a) => a.tipoMime.startsWith('image/')));
      setArchivos(adjuntosSolicitud.filter((a) => !a.tipoMime.startsWith('image/')));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adjuntosSolicitud]);
  useEffect(() => {
    if (adjuntosExistentes) {
      omitirProximoCambioRef.current = true;
      setFotos(adjuntosExistentes.filter((a) => a.tipoMime.startsWith('image/')));
      setArchivos(adjuntosExistentes.filter((a) => !a.tipoMime.startsWith('image/')));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adjuntosExistentes]);

  const guardar = useMutation({
    mutationFn: async (): Promise<{ id: string }> => {
      // Los adjuntos ya vinculados a la solicitud de origen los clona el
      // propio `aprobar()` en el servidor; aquí solo se envían los que se
      // agregaron de nuevo en este formulario (huérfanos, sin entidad aún).
      const adjuntosNuevos = [...fotos, ...archivos].filter((a) => a.entidadId === null).map((a) => a.id);
      const tiempoEstimadoMinutos = haySubOrdenes ? null : comoMinutosDeDuracion(tiempoEstimado);
      const activoIdEfectivo = haySubOrdenes || activoId === '' || sobre === 'PERSONA' ? null : activoId;
      const camposComunes = {
        titulo: titulo.trim(),
        descripcion: descripcion.trim(),
        prioridad,
        // El botón de "Crear orden"/"Guardar cambios" está deshabilitado
        // mientras esté vacío (§ validación), así que acá ya es un valor válido.
        // Vacío (OT solo con ubicación) se omite para que el servidor use su valor por defecto.
        tipoTrabajo: tipoTrabajo === '' ? undefined : tipoTrabajo,
        // Repartida en sub-órdenes, el padre no lleva ubicación/activo propios
        // aunque el estado local no se haya limpiado a tiempo.
        // Una OT de persona toma la ubicación de la persona (si la tiene).
        ubicacionId:
          sobre === 'PERSONA'
            ? personaElegida?.ubicacionId ?? null
            : haySubOrdenes || ubicacionId === ''
              ? null
              : ubicacionId,
        activoId: activoIdEfectivo,
        sujetoEvaluableId: sobre === 'PERSONA' && sujetoId !== '' ? sujetoId : null,
        tiposFalla,
        asignadosGrupos,
        encargadoCategoriaId: encargadoCategoriaId === '' ? null : encargadoCategoriaId,
        tiempoEstimadoMinutos,
        fechaInicio: fechaInicio === '' ? null : fechaInicio,
        fechaVencimiento: fechaVencimiento === '' ? null : fechaVencimiento,
        recurrencia,
        esPlantillaRecurrente: recurrencia !== 'NO_SE_REPITE' && esPlantillaRecurrente,
        recurrenciaActiva,
        // Solo al crear, y solo si el checklist llegó a mostrarse (si no, el
        // servidor deja la generación diferida de siempre, toda sin marcar).
        // Si la OT viene de una solicitud, su plan de materiales es el que cuenta
        // (lo copia el servidor al aprobar), así que aquí no se manda nada.
        materialesExcluidos:
          esCreacion && !prellenado?.solicitudId && activoIdEfectivo && tipoTrabajo !== '' && puede('lista_materiales:editar')
            ? Array.from(materialesExcluidos)
            : undefined,
      };

      let otId: string;
      if (prellenado?.solicitudId) {
        const respuesta = await api.post<{ ordenTrabajoId: string }>(
          `/solicitudes/${prellenado.solicitudId}/aprobar`,
          { ...camposComunes, asignados: asignadosUsuarios, adjuntos: adjuntosNuevos },
        );
        otId = respuesta.ordenTrabajoId;
      } else if (orden) {
        const cuerpo = {
          ...camposComunes,
          asignadosUsuarios,
          // La configuración fina de la recurrencia no se edita aquí todavía; se
          // reenvía tal cual para que guardar la ficha no la descarte.
          recurrenciaConfig: orden.recurrenciaConfig ?? null,
          ordenTrabajoPadreId: orden.ordenTrabajoPadreId ?? null,
          adjuntos: adjuntosNuevos,
        };
        const guardada = await api.put<OrdenTrabajo>(`/ordenes-trabajo/${orden.id}`, cuerpo);
        otId = guardada.id;
      } else {
        const cuerpo = {
          ...camposComunes,
          asignadosUsuarios,
          recurrenciaConfig: null,
          ordenTrabajoPadreId: null,
          adjuntos: adjuntosNuevos,
        };
        const creada = await api.post<OrdenTrabajo>('/ordenes-trabajo', cuerpo);
        otId = creada.id;
      }

      // Procedimiento, sub-órdenes y partes: solo tienen sentido la primera
      // vez, cuando la OT recién obtiene un id — se aplican en la misma
      // acción de "Crear orden" para que todo lo llenado en el formulario
      // quede hecho de una vez, sin tener que volver a entrar a agregarlo.
      if (esCreacion) {
        if (procedimientoPlantillaId) {
          await api.post(`/ordenes-trabajo/${otId}/procedimientos`, { plantillaId: procedimientoPlantillaId });
        }
        if (subOrdenActivos.length > 0) {
          await api.post(`/ordenes-trabajo/${otId}/sub-ordenes`, {
            items: subOrdenActivos.map((a) => {
              const detalle = subOrdenDetalles[a.id] ?? subOrdenDetalleVacio;
              const tiempoEstimadoMinutos = comoMinutosDeDuracion(detalle.tiempoEstimado);
              return {
                ...(a.esPersona ? { sujetoEvaluableId: a.id } : { activoId: a.id }),
                procedimientoPlantillaId: subOrdenProcedimientos[a.id] || null,
                descripcion: detalle.descripcion || undefined,
                ubicacionId: detalle.ubicacionId || null,
                prioridad: detalle.prioridad,
                tipoTrabajo: detalle.tipoTrabajo,
                tiposFalla: detalle.tiposFalla,
                asignadosUsuarios: detalle.asignadosUsuarios.length > 0 ? detalle.asignadosUsuarios : undefined,
                asignadosGrupos: detalle.asignadosGrupos.length > 0 ? detalle.asignadosGrupos : undefined,
                encargadoCategoriaId: detalle.encargadoCategoriaId || null,
                tiempoEstimadoMinutos,
                fechaInicio: detalle.fechaInicio || null,
                fechaVencimiento: detalle.fechaVencimiento || null,
                adjuntos: detalle.fotos.map((f) => f.id),
              };
            }),
          });
        }
        if (partesSeleccionadas.length > 0) {
          await api.post(`/ordenes/${otId}/partes`, {
            lineas: partesSeleccionadas.map((p) => ({
              parteId: p.parteId,
              ubicacionId: p.ubicacionId,
              cantidad: p.cantidad,
            })),
          });
        }
        if (prellenado?.observacionId && prellenado.activoId) {
          await api.post(`/activos/${prellenado.activoId}/observaciones/${prellenado.observacionId}/vincular-ot`, {
            ordenTrabajoId: otId,
          });
        } else if (prellenado?.observacionId && sobre === 'PERSONA' && sujetoId !== '') {
          await api.post(`/sujetos-evaluables/${sujetoId}/observaciones/${prellenado.observacionId}/vincular-ot`, {
            ordenTrabajoId: otId,
          });
        }
      }

      // Fotos/archivos que ya estaban guardados en la OT y el usuario quitó
      // durante esta edición: hasta este punto solo se desvincularon de la
      // vista (§ SubidaImagenes.quitar), para que un "Cancelar" no perdiera
      // evidencia ya guardada. Recién al confirmar el guardado se borran de
      // verdad — los que vienen de una solicitud aprobada nunca llegan aquí,
      // el propio formulario no los deja quitar de la vista.
      if (orden && adjuntosExistentes) {
        const idsActuales = new Set([...fotos, ...archivos].map((a) => a.id));
        const quitados = adjuntosExistentes.filter((a) => !idsActuales.has(a.id));
        await Promise.all(quitados.map((a) => api.delete(`/adjuntos/${a.id}`).catch(() => undefined)));
      }

      return { id: otId };
    },
    onSuccess: async ({ id: otId }) => {
      await cliente.invalidateQueries({ queryKey: ['ordenes'] });
      await cliente.invalidateQueries({ queryKey: ['orden', otId] });
      await cliente.invalidateQueries({ queryKey: ['orden-hilo', otId] });
      // Sin esto, fotos/archivos agregados al editar quedaban vinculados en
      // el servidor pero el detalle seguía mostrando la caché de adjuntos
      // de antes de editar (30s de `staleTime`) — la OT «sin foto», aunque
      // ya la tuviera (§ bug reportado).
      await cliente.invalidateQueries({ queryKey: ['adjuntos', 'ORDEN_TRABAJO', otId] });
      await cliente.invalidateQueries({ queryKey: ['dashboard'] });
      if (prellenado?.solicitudId) {
        await cliente.invalidateQueries({ queryKey: ['solicitud', prellenado.solicitudId] });
        await cliente.invalidateQueries({ queryKey: ['bandeja-resumen'] });
      }
      if (prellenado?.observacionId && prellenado.activoId) {
        await cliente.invalidateQueries({ queryKey: ['observaciones-activo', prellenado.activoId] });
        await cliente.invalidateQueries({ queryKey: ['observaciones-activo-pendientes'] });
      } else if (prellenado?.observacionId && sujetoId !== '') {
        await cliente.invalidateQueries({ queryKey: ['observaciones-sujeto', sujetoId] });
        await cliente.invalidateQueries({ queryKey: ['observaciones-sujeto-pendientes'] });
      }
      // El mensaje llega solo desde el backend (crear/actualizar la OT ya
      // tienen `@MensajeExito`); aprobar una solicitud todavía no lo declara.
      // `replace`, no push: guardar reemplaza al formulario en el
      // historial — con push, el «atrás» del detalle recién creado volvía
      // al formulario en blanco en vez de a la lista (§ bug reportado).
      navegar(`/ordenes/${otId}`, { replace: true });
    },
  });

  // §10.3: una fecha de inicio posterior al vencimiento describe un trabajo
  // imposible; se detiene aquí y no en un error del servidor.
  const rangoInvalido = fechaInicio !== '' && fechaVencimiento !== '' && fechaInicio > fechaVencimiento;

  // Cambios sin guardar: se marca en cuanto cualquier campo se mueve del
  // valor con el que se montó el formulario, para poder avisar antes de
  // perderlos al salir sin guardar (cierre de pestaña o botones de la ficha).
  const [tieneCambiosSinGuardar, setTieneCambiosSinGuardar] = useState(false);
  // React StrictMode (solo en dev) invoca cada efecto dos veces al montar
  // —efecto, limpieza simulada, efecto de nuevo— para detectar efectos
  // impuros. Con un simple `if (primerRender) { primerRender = false;
  // return }` esa segunda pasada ya encontraba la bandera en `false` y
  // marcaba «cambios sin guardar» en un formulario recién abierto, intacto
  // (§ bug reportado). `listoRef` se arma en su PROPIO efecto (deps `[]`,
  // declarado después de este), cuya función de limpieza lo revierte —
  // así la pasada repetida de StrictMode también lo encuentra en `false`
  // la segunda vez, y el mecanismo queda inmune al doble montaje.
  const listoRef = useRef(false);
  useEffect(() => {
    if (!listoRef.current) return;
    if (omitirProximoCambioRef.current) {
      omitirProximoCambioRef.current = false;
      return;
    }
    setTieneCambiosSinGuardar(true);
  }, [
    titulo,
    descripcion,
    prioridad,
    tipoTrabajo,
    ubicacionId,
    activoId,
    tiposFalla,
    asignadosUsuarios,
    asignadosGrupos,
    encargadoCategoriaId,
    tiempoEstimado,
    fechaInicio,
    fechaVencimiento,
    recurrencia,
    esPlantillaRecurrente,
    recurrenciaActiva,
    fotos,
    archivos,
    procedimientoPlantillaId,
    subOrdenActivos,
    subOrdenProcedimientos,
    subOrdenDetalles,
    partesSeleccionadas,
  ]);
  useEffect(() => {
    listoRef.current = true;
    return () => {
      listoRef.current = false;
    };
  }, []);

  useEffect(() => {
    function alIntentarCerrar(e: BeforeUnloadEvent): void {
      if (!tieneCambiosSinGuardar) return;
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', alIntentarCerrar);
    return () => window.removeEventListener('beforeunload', alIntentarCerrar);
  }, [tieneCambiosSinGuardar]);

  // Navegación pendiente de confirmar: se guarda la función que de verdad
  // navega, y solo se dispara si el usuario elige "Descartar cambios".
  const [salidaPendiente, setSalidaPendiente] = useState<(() => void) | null>(null);

  function intentarSalir(irDeVerdad: () => void): void {
    if (tieneCambiosSinGuardar) setSalidaPendiente(() => irDeVerdad);
    else irDeVerdad();
  }

  // Cambiarse a otra sección (menú lateral, un enlace de "actividad
  // reciente", etc.) navega con un <Link> normal, fuera del control de este
  // formulario. Sin un router de datos (`useBlocker` exige uno) la única
  // forma de interceptarlo es escuchando el click del enlace antes de que
  // React Router lo procese, y decidir ahí si se deja pasar o no.
  useEffect(() => {
    if (!tieneCambiosSinGuardar) return;

    function alClicEnDocumento(e: MouseEvent): void {
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const enlace = (e.target as HTMLElement).closest('a');
      if (!enlace || enlace.target === '_blank' || enlace.hasAttribute('download')) return;

      const destino = enlace.getAttribute('href');
      if (!destino || destino.startsWith('#') || /^[a-z]+:/i.test(destino)) return;

      e.preventDefault();
      e.stopPropagation();
      intentarSalir(() => navegar(destino));
    }

    document.addEventListener('click', alClicEnDocumento, true);
    return () => document.removeEventListener('click', alClicEnDocumento, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tieneCambiosSinGuardar]);

  return (
    <>
      <BotonVolver
        a={orden ? `/ordenes/${orden.id}` : '/ordenes'}
        etiqueta={orden ? 'Volver a la orden' : 'Volver a Órdenes de Trabajo'}
        interceptar={intentarSalir}
      />

      <div className="formulario-angosto">
      <div className="cabecera-pagina">
        <div>
          {orden ? <div className="numero-registro">ORDEN DE TRABAJO #{orden.numero}</div> : null}
          <h1>{orden ? 'Editar orden de trabajo' : 'Nueva orden de trabajo'}</h1>
        </div>
        <BotonEscanearQr
          etiqueta="Escanear QR de activo o ubicación"
          entidades={['ACTIVO', 'UBICACION']}
          alResolver={(r) => {
            // Repartida en sub-órdenes, el padre no tiene ubicación/activo
            // propios: el QR solo aporta el título/descripción de cortesía.
            if (!haySubOrdenes) {
              if (r.entidad === 'UBICACION') {
                setUbicacionId(r.entidadId);
                cambiarActivo('');
                setActivoEscaneado(null);
              } else {
                cambiarActivo(r.entidadId);
                setActivoEscaneado(r.nombre);
                if (r.ubicacionId) {
                  setUbicacionId(r.ubicacionId);
                } else {
                  // La resolución del QR puede no traer la ubicación (API
                  // previa al cambio): se pide a la ficha del activo.
                  void api
                    .get<{ ubicacionId: string | null }>(`/activos/${r.entidadId}`)
                    .then((a) => a.ubicacionId && setUbicacionId(a.ubicacionId))
                    .catch(() => undefined);
                }
              }
              setEscaneoSeq((n) => n + 1);
            }
            // Prellenado de cortesía: solo si el técnico aún no escribió nada,
            // para no pisar lo que haya tecleado.
            const donde = r.entidad === 'UBICACION' ? 'en' : 'del activo';
            if (titulo.trim() === '') setTitulo(`Mantenimiento ${donde} ${r.nombre}`);
            if (descripcion.trim() === '') {
              setDescripcion(
                r.entidad === 'UBICACION'
                  ? `Trabajo en la ubicación ${r.nombre}.`
                  : `Trabajo sobre ${r.nombre}${r.detalle ? ` (${r.detalle})` : ''}.`,
              );
            }
          }}
        />
      </div>

      {/* Formulario largo (activo, procedimiento, tipos de falla…): sin esto,
          guardar exige bajar hasta el final aunque el cambio se hizo arriba
          del todo (§ pedido). Dispara la misma mutación que el botón de
          abajo — no hay dos formularios, solo dos lugares desde donde
          enviarlo. */}
      <div className="barra-acciones-fija">
        <span className="barra-acciones-fija-estado">
          {tieneCambiosSinGuardar ? (
            <>
              <span className="barra-acciones-fija-punto" aria-hidden="true" />
              Cambios sin guardar
            </>
          ) : orden ? (
            'Sin cambios pendientes'
          ) : (
            ''
          )}
        </span>
        <div className="barra-acciones-fija-botones">
          <button
            type="button"
            className="boton secundario pequeno"
            onClick={() => intentarSalir(() => navegar(orden ? `/ordenes/${orden.id}` : '/ordenes'))}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="boton pequeno"
            disabled={guardar.isPending || titulo.trim() === '' || (tipoObligatorio && tipoTrabajo === '') || rangoInvalido}
            onClick={() => !rangoInvalido && guardar.mutate()}
          >
            {guardar.isPending ? 'Guardando…' : orden ? 'Guardar cambios' : 'Crear orden'}
          </button>
        </div>
      </div>

      {guardar.isError ? <Aviso tono="error">{mensajeError(guardar.error)}</Aviso> : null}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!rangoInvalido) guardar.mutate();
        }}
      >
        <div className="panel">
          <div className="panel-cabecera">
            <h2>Detalle</h2>
          </div>
          <div className="panel-cuerpo">
            <div className="campo">
              <label htmlFor="ot-titulo">
                Título
                <span className="campo-obligatorio">*</span>
              </label>
              <input
                id="ot-titulo"
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                maxLength={250}
                required
                placeholder="Ej.: Mantenimiento preventivo trimestral del láser"
              />
            </div>

            <div className="campo">
              <label htmlFor="ot-descripcion">Descripción</label>
              <textarea
                id="ot-descripcion"
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                maxLength={8000}
                placeholder="Alcance del trabajo, condiciones y precauciones."
              />
            </div>

            <SubidaImagenes etiqueta="Fotos" valor={fotos} alCambiar={setFotos} />
          </div>
        </div>

        {esCreacion ? (
          <div className="panel" style={{ marginTop: 16 }}>
            <div className="panel-cabecera">
              <h2>Sub-órdenes de trabajo ({subOrdenActivos.length})</h2>
              {subOrdenActivos.length > 0 ? (
                <button
                  type="button"
                  className="boton-enlace"
                  onClick={() => setSubOrdenModalAbierto(true)}
                >
                  + Añadir sub-órdenes de trabajo
                </button>
              ) : null}
            </div>
            <div className="panel-cuerpo">
              {nombreMomentaneoSubOrden !== null ? (
                <div className="campo" style={{ marginBottom: 14 }}>
                  <label htmlFor="suborden-activo-momentaneo">Título de la sub-orden de trabajo</label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                      id="suborden-activo-momentaneo"
                      autoFocus
                      value={nombreMomentaneoSubOrden}
                      onChange={(e) => setNombreMomentaneoSubOrden(e.target.value)}
                      placeholder="Ej.: Limpieza de ventanas"
                      maxLength={250}
                      disabled={crearMomentaneoSubOrden.isPending}
                      style={{ flex: 1, minWidth: 220 }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && nombreMomentaneoSubOrden.trim() !== '') {
                          e.preventDefault();
                          crearMomentaneoSubOrden.mutate(nombreMomentaneoSubOrden);
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="boton"
                      disabled={nombreMomentaneoSubOrden.trim() === '' || crearMomentaneoSubOrden.isPending}
                      onClick={() => crearMomentaneoSubOrden.mutate(nombreMomentaneoSubOrden)}
                    >
                      {crearMomentaneoSubOrden.isPending ? 'Creando…' : 'Crear'}
                    </button>
                    <button
                      type="button"
                      className="boton secundario"
                      disabled={crearMomentaneoSubOrden.isPending}
                      onClick={() => setNombreMomentaneoSubOrden(null)}
                    >
                      Cancelar
                    </button>
                  </div>
                  {crearMomentaneoSubOrden.isError ? (
                    <Aviso tono="error">No se pudo crear el activo momentáneo.</Aviso>
                  ) : null}
                </div>
              ) : null}

              {subOrdenActivos.length === 0 && nombreMomentaneoSubOrden === null ? (
                <>
                  <p style={{ marginTop: 0, color: 'var(--texto-medio)', fontSize: 13 }}>
                    Divida las tareas grandes en sub-órdenes de trabajo para realizar un seguimiento del trabajo por
                    separado.
                  </p>
                  <button type="button" className="boton secundario" onClick={() => setSubOrdenModalAbierto(true)}>
                    + Añadir sub-órdenes de trabajo
                  </button>
                </>
              ) : subOrdenActivos.length > 0 ? (
                <>
                  {subOrdenFilasMarcadas.length > 0 ? (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
                      <div style={{ flex: 1 }}>
                        <SelectorLista
                          id="sub-orden-plantilla-masiva"
                          valor={subOrdenPlantillaMasiva}
                          alCambiar={setSubOrdenPlantillaMasiva}
                          {...opcionesProcedimientoSub(true)}
                          textoVacio="Sin procedimiento"
                        />
                      </div>
                      <button
                        type="button"
                        className="boton secundario pequeno"
                        onClick={() =>
                          setSubOrdenProcedimientos((s) => ({
                            ...s,
                            ...Object.fromEntries(subOrdenFilasMarcadas.map((id) => [id, subOrdenPlantillaMasiva])),
                          }))
                        }
                      >
                        Aplicar a los {subOrdenFilasMarcadas.length} seleccionados
                      </button>
                    </div>
                  ) : null}

                  <div className="tabla-contenedor">
                    <table className="tabla">
                      <thead>
                        <tr>
                          <th style={{ width: 32 }}>
                            <CasillaVerificacion
                              marcada={
                                subOrdenActivos.length > 0 &&
                                subOrdenFilasMarcadas.length === subOrdenActivos.length
                              }
                              alCambiar={() =>
                                setSubOrdenFilasMarcadas(
                                  subOrdenFilasMarcadas.length === subOrdenActivos.length
                                    ? []
                                    : subOrdenActivos.map((a) => a.id),
                                )
                              }
                              etiqueta="Seleccionar todas"
                            />
                          </th>
                          <th>Título</th>
                          <th>Ubicación</th>
                          <th>Procedimiento</th>
                          <th style={{ width: 32 }} />
                        </tr>
                      </thead>
                      <tbody>
                        {subOrdenActivos.map((a) => (
                          <tr key={a.id}>
                            <td>
                              <CasillaVerificacion
                                marcada={subOrdenFilasMarcadas.includes(a.id)}
                                alCambiar={() =>
                                  setSubOrdenFilasMarcadas((f) =>
                                    f.includes(a.id) ? f.filter((id) => id !== a.id) : [...f, a.id],
                                  )
                                }
                                etiqueta={`Seleccionar ${a.nombre}`}
                              />
                            </td>
                            <td>
                              <button
                                type="button"
                                className="boton-enlace"
                                style={{ fontWeight: 600, textAlign: 'left', whiteSpace: 'normal' }}
                                onClick={() => setSubOrdenEditandoId(a.id)}
                              >
                                {a.nombre}
                              </button>
                              {a.activoPadreNombre ? (
                                <span style={{ color: 'var(--texto-tenue)', fontSize: 12 }}>
                                  {' '}
                                  · parte de {a.activoPadreNombre}
                                </span>
                              ) : null}
                            </td>
                            <td>
                              {(() => {
                                const ubicacionIdElegida = subOrdenDetalles[a.id]?.ubicacionId;
                                const { nombre, raiz } = ubicacionIdElegida
                                  ? resolverUbicacion(ubicaciones, ubicacionIdElegida)
                                  : { nombre: a.ubicacionNombre, raiz: a.ubicacionRaiz };
                                return raiz && raiz !== nombre ? (
                                  <>
                                    <span style={{ color: 'var(--texto-tenue)' }}>{raiz} / </span>
                                    {nombre}
                                  </>
                                ) : (
                                  nombre ?? '—'
                                );
                              })()}
                            </td>
                            <td style={{ maxWidth: 260 }}>
                              <div style={{ maxWidth: 260 }}>
                                <SelectorLista
                                  id={`sub-orden-plantilla-${a.id}`}
                                  valor={subOrdenProcedimientos[a.id] ?? ''}
                                  alCambiar={(v) => setSubOrdenProcedimientos((s) => ({ ...s, [a.id]: v }))}
                                  {...opcionesProcedimientoSub(false)}
                                  textoVacio="Agregue procedimiento"
                                />
                              </div>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <button
                                type="button"
                                className="fila-archivo-quitar"
                                aria-label={`Quitar ${a.nombre}`}
                                onClick={() => {
                                  setSubOrdenActivos((s) => s.filter((x) => x.id !== a.id));
                                  setSubOrdenProcedimientos((s) => {
                                    const copia = { ...s };
                                    delete copia[a.id];
                                    return copia;
                                  });
                                  setSubOrdenDetalles((s) => {
                                    const copia = { ...s };
                                    delete copia[a.id];
                                    return copia;
                                  });
                                  setSubOrdenFilasMarcadas((f) => f.filter((id) => id !== a.id));
                                }}
                              >
                                ×
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        ) : null}

        {subOrdenModalAbierto ? (
          <ModalSeleccionarActivosSubOrden
            ubicaciones={ubicaciones}
            tiposActivo={tiposActivo}
            excluirIds={subOrdenActivos.map((a) => a.id)}
            alCerrar={() => setSubOrdenModalAbierto(false)}
            alConfirmar={(activos) => {
              setSubOrdenActivos((s) => [...s, ...activos]);
              setSubOrdenModalAbierto(false);
            }}
            alContinuarSinActivo={() => {
              setSubOrdenModalAbierto(false);
              setNombreMomentaneoSubOrden('');
            }}
          />
        ) : null}

        {subOrdenEditandoId !== null ? (
          <DialogoEditarSubOrden
            activo={subOrdenActivos.find((a) => a.id === subOrdenEditandoId)!}
            detalle={
              subOrdenDetalles[subOrdenEditandoId] ?? {
                ...subOrdenDetalleVacio,
                // Primera vez que se edita la fila: parte de la ubicación que
                // ya trae el activo (o el momentáneo), no de un campo vacío.
                ubicacionId: subOrdenActivos.find((a) => a.id === subOrdenEditandoId)?.ubicacionId ?? '',
              }
            }
            ubicaciones={ubicaciones}
            usuarios={usuarios}
            grupos={grupos}
            fallas={fallas}
            alGuardar={(detalle) => {
              setSubOrdenDetalles((s) => ({ ...s, [subOrdenEditandoId]: detalle }));
              setSubOrdenEditandoId(null);
            }}
            alCerrar={() => setSubOrdenEditandoId(null)}
          />
        ) : null}

        {!haySubOrdenes ? (
          <div className="panel" style={{ marginTop: 16 }}>
            <div className="panel-cabecera">
              <h2>Ubicación, activo y procedimiento</h2>
            </div>
            <div className="panel-cuerpo">
              {puedeSesion('sujeto_evaluable:leer') ? (
              <div className="acciones-cabecera" role="group" aria-label="Sobre" style={{ marginBottom: 12, gap: 8 }}>
                <span className="campo-nota">Sobre:</span>
                <button
                  type="button"
                  className={`boton pequeno${sobre === 'ACTIVO' ? '' : ' secundario'}`}
                  aria-pressed={sobre === 'ACTIVO'}
                  onClick={() => setSobre('ACTIVO')}
                >
                  Un activo
                </button>
                <button
                  type="button"
                  className={`boton pequeno${sobre === 'PERSONA' ? '' : ' secundario'}`}
                  aria-pressed={sobre === 'PERSONA'}
                  onClick={() => setSobre('PERSONA')}
                >
                  Una persona
                </button>
              </div>
              ) : null}
              <div className="rejilla-campos">
                {sobre === 'ACTIVO' ? (
                  <>
                    <SelectorUbicacion
                      id="ot-ubicacion"
                      valor={ubicacionId}
                      alCambiar={(v) => {
                        setUbicacionId(v);
                        cambiarActivo('');
                        setActivoEscaneado(null);
                      }}
                      ubicaciones={ubicaciones}
                    />
                    <SelectorActivo
                      key={`ot-activo-${escaneoSeq}`}
                      id="ot-activo"
                      valor={activoId}
                      alCambiar={(v) => {
                        cambiarActivo(v);
                        setActivoEscaneado(null);
                      }}
                      ubicacionId={ubicacionId || undefined}
                      nombreActual={activoEscaneado ?? orden?.activo}
                    />
                  </>
                ) : (
                  <>
                    <SelectorUbicacion
                      id="ot-persona-ubicacion"
                      etiqueta="Filtrar por ubicación"
                      valor={ubicacionPersonaId}
                      alCambiar={setUbicacionPersonaId}
                      ubicaciones={ubicaciones}
                    />
                    <SelectorPersona
                      id="ot-persona"
                      valor={sujetoId}
                      alCambiar={setSujetoId}
                      ubicacionId={ubicacionPersonaId || undefined}
                      nombreActual={orden?.sujetoEvaluable}
                    />
                  </>
                )}
                {esCreacion ? (
                  <SelectorLista
                    id="procedimiento-plantilla"
                    etiqueta="Procedimiento"
                    valor={procedimientoPlantillaId}
                    alCambiar={setProcedimientoPlantillaId}
                    opciones={(plantillasPublicadas ?? []).map((p) => ({
                      id: p.id,
                      nombre: `${p.nombre} (${p.cantidadCampos} campos)`,
                    }))}
                    textoVacio="Sin procedimiento"
                  />
                ) : null}
              </div>

              {sobre === 'ACTIVO' ? <ObservacionesDelActivoSeleccionado activoId={activoId} /> : null}
              {sobre === 'PERSONA' ? <ObservacionesDeSujetoSeleccionado sujetoId={sujetoId} /> : null}
            </div>
          </div>
        ) : null}

        {/* Editando una OT ya creada, el procedimiento se administra con el
            componente completo (adjunta de una, no queda "guardado" para más
            tarde) — por eso vive en su propio panel en vez de sumarse al de
            arriba. */}
        {!haySubOrdenes && !esCreacion && puede('procedimiento:ejecutar') ? (
          <div style={{ marginTop: 16 }}>
            <ProcedimientosDeOrden ordenTrabajoId={orden!.id} />
          </div>
        ) : null}

        <div className="panel" style={{ marginTop: 16 }}>
          <div className="panel-cabecera">
            <h2>Asignación</h2>
          </div>
          <div className="panel-cuerpo">
            <div className="rejilla-campos" style={{ marginBottom: 0 }}>
              <SeleccionMultiple
                etiqueta="Asignar a grupos"
                opciones={(grupos ?? []).map((g) => ({ id: g.id, nombre: g.nombre }))}
                seleccion={asignadosGrupos}
                alCambiar={(ids) => {
                  const anteriores = asignadosGrupos;
                  setAsignadosGrupos(ids);

                  // Al elegir un grupo se precargan sus integrantes como sugerencia
                  // de a quién asignar puntualmente; el usuario sigue pudiendo
                  // ajustar la selección a mano en el campo de abajo. Al quitar un
                  // grupo, sus integrantes heredados se quitan con él —si no,
                  // quedan "pegados" en la selección aunque el grupo que los trajo
                  // ya no esté—, salvo que también pertenezcan a otro grupo que
                  // sigue elegido.
                  const miembrosDe = (idsGrupo: string[]): Set<string> =>
                    new Set(
                      (grupos ?? [])
                        .filter((g) => idsGrupo.includes(g.id))
                        .flatMap((g) => g.miembrosDetalle.map((m) => m.id)),
                    );

                  const agregados = ids.filter((id) => !anteriores.includes(id));
                  const quitados = anteriores.filter((id) => !ids.includes(id));
                  const nuevosMiembros = miembrosDe(agregados);
                  const miembrosQuitados = miembrosDe(quitados);
                  const miembrosAunVigentes = miembrosDe(ids);

                  setAsignadosUsuarios((previos) => {
                    const conjunto = new Set([...previos, ...nuevosMiembros]);
                    for (const m of miembrosQuitados) {
                      if (!miembrosAunVigentes.has(m)) conjunto.delete(m);
                    }
                    return Array.from(conjunto);
                  });
                }}
                vacio="Aún no hay grupos."
              />
              <SeleccionMultiple
                etiqueta="Asignar a usuarios"
                opciones={
                  asignadosGrupos.length > 0
                    ? (grupos ?? [])
                        .filter((g) => asignadosGrupos.includes(g.id))
                        .flatMap((g) => g.miembrosDetalle)
                        .filter((m, i, arr) => arr.findIndex((x) => x.id === m.id) === i)
                    : (usuarios ?? []).map((u) => ({ id: u.id, nombre: u.nombre }))
                }
                seleccion={asignadosUsuarios}
                alCambiar={setAsignadosUsuarios}
                vacio={
                  asignadosGrupos.length > 0
                    ? 'Los grupos elegidos no tienen integrantes.'
                    : 'No hay usuarios activos.'
                }
              />
            </div>
          </div>
        </div>

        {/* Antes eran cuatro paneles sueltos (Tiempo estimado, Encargado,
            Programación, Recurrencia y tipo de trabajo, Prioridad): todos
            son selects de una sola decisión, sin texto libre ni tablas — la
            razón real para separarlos en cajas propias no existía, y solo
            alargaban el scroll. Prioridad va primero por ser la que más se
            usa para triar la bandeja de OT. */}
        <div className="panel" style={{ marginTop: 16 }}>
          <div className="panel-cabecera">
            <h2>Clasificación y programación</h2>
          </div>
          <div className="panel-cuerpo">
            <SelectorSegmentado
              id="ot-prioridad"
              etiqueta="Prioridad"
              valor={prioridad}
              alCambiar={(v) => setPrioridad(v as Prioridad)}
              opciones={PRIORIDADES.map((p) => ({ id: p, nombre: ETIQUETA_PRIORIDAD[p] }))}
            />

            <div className="rejilla-campos" style={{ marginTop: 14 }}>
              <SelectorLista
                id="ot-tipo"
                etiqueta="Tipo de trabajo"
                valor={tipoTrabajo}
                alCambiar={(v) => {
                  setTipoTrabajo(v as TipoTrabajo);
                  setMaterialesExcluidos(new Set());
                }}
                opciones={opcionesTipoTrabajo.map((t) => ({ id: t, nombre: ETIQUETA_TIPO_TRABAJO[t] }))}
                textoVacio="Elija el tipo de trabajo…"
                deshabilitado={restringeTipoTrabajo && cargandoTrabajos}
                requerido={!esCreacion || tipoObligatorio}
              />
              <SelectorLista
                id="ot-recurrencia"
                etiqueta="Recurrencia"
                valor={recurrencia}
                alCambiar={(v) => setRecurrencia(v as Recurrencia)}
                opciones={RECURRENCIAS.map((r) => ({ id: r, nombre: ETIQUETA_RECURRENCIA[r] }))}
                requerido
              />
              <SelectorCatalogo
                id="ot-encargado"
                codigo="CATEGORIA_ENCARGADO"
                etiqueta="Categoría de encargado"
                valor={encargadoCategoriaId}
                alCambiar={setEncargadoCategoriaId}
              />
            </div>

            <div className="rejilla-campos" style={{ marginTop: 14, marginBottom: 0 }}>
              <SelectorFecha
                id="ot-inicio"
                etiqueta="Fecha de inicio"
                valor={fechaInicio}
                alCambiar={setFechaInicio}
                maximo={fechaInicioMaxima()}
              />
              <SelectorFecha
                id="ot-vencimiento"
                etiqueta="Fecha de vencimiento"
                valor={fechaVencimiento}
                alCambiar={setFechaVencimiento}
              />
              {!haySubOrdenes ? (
                <div className="campo" style={{ marginBottom: 0 }}>
                  <label htmlFor="ot-estimado">Tiempo estimado</label>
                  <input
                    id="ot-estimado"
                    type="time"
                    value={tiempoEstimado}
                    onChange={(e) => setTiempoEstimado(e.target.value)}
                  />
                </div>
              ) : null}
            </div>

            {rangoInvalido ? (
              <Aviso tono="error">La fecha de inicio no puede ser posterior a la de vencimiento.</Aviso>
            ) : null}

            {recurrencia !== 'NO_SE_REPITE' && !haySubOrdenes ? (
              <div style={{ marginTop: 14 }}>
                <label className="campo campo-checkbox" style={{ marginBottom: 0 }}>
                  <input
                    type="checkbox"
                    checked={esPlantillaRecurrente}
                    onChange={(e) => setEsPlantillaRecurrente(e.target.checked)}
                  />
                  Es una plantilla recurrente
                </label>
                <span className="campo-nota">
                  {esPlantillaRecurrente
                    ? 'Esta OT no se trabaja: sirve de plantilla. Cada vez que llegue la fecha de vencimiento, se crea sola una OT nueva con lo que esta plantilla tenga guardado en ese momento (asignados, prioridad, etc.), sin esperar a que nadie la complete.'
                    : 'Sin marcar, la siguiente ocurrencia recién se crea al completar esta OT (comportamiento de siempre).'}
                </span>
                {esPlantillaRecurrente && orden ? (
                  <label className="campo campo-checkbox" style={{ marginTop: 10, marginBottom: 0 }}>
                    <input
                      type="checkbox"
                      checked={recurrenciaActiva}
                      onChange={(e) => setRecurrenciaActiva(e.target.checked)}
                    />
                    Activa (desmarcar la pausa sin perder la configuración)
                  </label>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        {esCreacion && !haySubOrdenes ? (
          <div className="panel" style={{ marginTop: 16 }}>
            <div className="panel-cabecera">
              <h2>Partes</h2>
            </div>
            <div className="panel-cuerpo">
              {partesSeleccionadas.length > 0 ? (
                <div className="lista-archivos" style={{ marginBottom: 12 }}>
                  {partesSeleccionadas.map((p, i) => (
                    <div key={`${p.parteId}-${p.ubicacionId}`} className="fila-archivo">
                      <span className="fila-archivo-nombre">
                        {p.nombre} · {p.cantidad} un. desde {p.ubicacionNombre} · {formatearMoneda(p.costoUnitarioPen)} c/u
                      </span>
                      <span className="campo-nota" style={{ fontWeight: 700, marginRight: 8 }}>
                        {formatearMoneda(Number(p.costoUnitarioPen) * p.cantidad)}
                      </span>
                      <button
                        type="button"
                        className="fila-archivo-quitar"
                        aria-label={`Quitar ${p.nombre}`}
                        onClick={() => setPartesSeleccionadas((s) => s.filter((_, j) => j !== i))}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <div className="fila-archivo" style={{ justifyContent: 'flex-end', gap: 8 }}>
                    <span style={{ fontWeight: 700 }}>Total en partes</span>
                    <span style={{ fontWeight: 800 }}>
                      {formatearMoneda(
                        partesSeleccionadas.reduce((n, p) => n + Number(p.costoUnitarioPen) * p.cantidad, 0),
                      )}
                    </span>
                  </div>
                </div>
              ) : null}

              <SelectorParte
                id="ot-parte-buscar"
                etiqueta="Buscar parte"
                valor={parteEnCursoId}
                alElegir={(p) => {
                  setParteEnCursoId(p?.id ?? '');
                  setParteEnCurso(p);
                  setUbicacionParteEnCurso('');
                }}
              />

              {parteEnCurso ? (
                <div style={{ marginTop: 10 }}>
                  <div className="rejilla-campos">
                    <SelectorLista
                      id="ot-parte-ubicacion"
                      etiqueta="Ubicación de origen"
                      valor={ubicacionParteEnCurso}
                      alCambiar={setUbicacionParteEnCurso}
                      opciones={parteEnCurso.stock.map((s) => ({
                        id: s.ubicacionId,
                        nombre: `${s.ubicacionNombre} (${s.unidadesEnExistencia} disp.)`,
                      }))}
                      textoVacio="Elija de dónde sale…"
                      requerido
                    />
                    <div className="campo">
                      <label htmlFor="ot-parte-cantidad">Cantidad</label>
                      <input
                        id="ot-parte-cantidad"
                        type="number"
                        min={1}
                        max={stockParteEnCurso}
                        value={cantidadParteEnCurso}
                        onChange={(e) => setCantidadParteEnCurso(e.target.value.replace(/\D/g, ''))}
                      />
                      {ubicacionParteEnCurso && cantidadExcedeStockParte ? (
                        <span className="campo-nota">Solo hay {stockParteEnCurso} disponible(s) en esa ubicación.</span>
                      ) : null}
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                    <button
                      type="button"
                      className="boton secundario"
                      disabled={!ubicacionParteEnCurso || Number(cantidadParteEnCurso) < 1 || cantidadExcedeStockParte}
                      onClick={() => {
                        const ubic = parteEnCurso.stock.find((s) => s.ubicacionId === ubicacionParteEnCurso);
                        if (!ubic) return;
                        setPartesSeleccionadas((s) => [
                          ...s,
                          {
                            parteId: parteEnCurso.id,
                            nombre: parteEnCurso.nombre,
                            ubicacionId: ubic.ubicacionId,
                            ubicacionNombre: ubic.ubicacionNombre,
                            cantidad: Number(cantidadParteEnCurso),
                            costoUnitarioPen: parteEnCurso.costoUnitarioPen,
                          },
                        ]);
                        setParteEnCursoId('');
                        setParteEnCurso(null);
                        setCantidadParteEnCurso('1');
                      }}
                    >
                      Agregar
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {esCreacion && prellenado?.solicitudId && puede('ot:crear') ? (
          <PlanMaterialesSolicitud solicitudId={prellenado.solicitudId} activoId={null} editable={false} />
        ) : null}

        {esCreacion && !prellenado?.solicitudId && !haySubOrdenes && sobre === 'ACTIVO' && activoId !== '' && tipoTrabajo !== '' &&
        puede('lista_materiales:editar') ? (
          <SeccionMaterialesPrevisualizados
            activoId={activoId}
            tipoTrabajo={tipoTrabajo}
            excluidos={materialesExcluidos}
            alCambiarExcluidos={setMaterialesExcluidos}
          />
        ) : null}

        <div className="panel" style={{ marginTop: 16 }}>
          <div className="panel-cabecera">
            <h2>Archivos y tipos de falla</h2>
          </div>
          <div className="panel-cuerpo">
            <SubidaArchivos etiqueta="Archivos adjuntos" valor={archivos} alCambiar={setArchivos} />

            <div style={{ borderTop: '1px solid var(--borde-suave)', margin: '18px 0' }} />

            <SeleccionMultiple
              etiqueta="Tipos de falla"
              opciones={(fallas ?? [])
                .filter((f) => f.activo || tiposFalla.includes(f.id))
                .map((f) => ({ id: f.id, nombre: f.nombre, nota: f.familia ?? undefined }))}
              seleccion={tiposFalla}
              alCambiar={setTiposFalla}
              vacio="Aún no hay tipos de falla. Se crean en Tipos de Fallas."
              alCrear={
                puede('tipo_falla:escribir')
                  ? {
                      etiqueta: 'Nuevo tipo de falla…',
                      creando: crearTipoFalla.isPending,
                      crear: (nombre) => crearTipoFalla.mutateAsync(nombre).then(() => undefined),
                    }
                  : undefined
              }
            />
            {tipoFallaSimilar ? (
              <Aviso tono="atencion">
                <p style={{ margin: '0 0 8px' }}>{tipoFallaSimilar.mensaje}</p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    className="boton secundario pequeno"
                    disabled={confirmarTipoFallaSimilar.isPending}
                    onClick={() => confirmarTipoFallaSimilar.mutate()}
                  >
                    Crear de todos modos
                  </button>
                  <button
                    type="button"
                    className="boton secundario pequeno"
                    disabled={confirmarTipoFallaSimilar.isPending}
                    onClick={() => setTipoFallaSimilar(null)}
                  >
                    Cancelar
                  </button>
                </div>
              </Aviso>
            ) : null}
          </div>
        </div>

        {/* Sin bar de acciones acá abajo: la de arriba (`.barra-acciones-fija`)
            queda pegada mientras se scrollea, así que un segundo juego de
            botones al final solo duplicaría lo mismo. El submit oculto
            mantiene Enter funcionando en los campos de texto. */}
        <button type="submit" style={{ display: 'none' }} aria-hidden="true" tabIndex={-1} />
      </form>
      </div>

      {salidaPendiente ? (
        <Dialogo titulo="¿Descartar los cambios no guardados?" alCerrar={() => setSalidaPendiente(null)}>
          <p style={{ fontSize: 13.5, color: 'var(--texto-medio)', marginBottom: 14 }}>
            Tienes cambios sin guardar en esta Orden de Trabajo. ¿Estás seguro de que quieres salir?
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="boton secundario" onClick={() => setSalidaPendiente(null)}>
              Cancelar
            </button>
            <button
              type="button"
              className="boton"
              onClick={() => {
                const ir = salidaPendiente;
                setSalidaPendiente(null);
                ir();
              }}
            >
              Descartar cambios
            </button>
          </div>
        </Dialogo>
      ) : null}
    </>
  );
}
